// Ο Monaco editor + όλη η λογική της εφαρμογής (γλώσσα/UI/διάγραμμα ροής)
// φορτώνονται ΔΥΝΑΜΙΚΑ, μέσα στο `boot()` παρακάτω (βλ. σχόλιο εκεί) — όχι
// εδώ, στην κορυφή, στατικά. Έτσι η αρχική σελίδα «ζωγραφίζεται» αμέσως
// (στατικό HTML/CSS ήδη υπάρχει), χωρίς να περιμένει να κατέβει/εκτελεστεί
// ολόκληρο το ~3MB bundle πριν φανεί οτιδήποτε — βλ. plan
// transient-zooming-sky (PageSpeed 59/100 → lazy-load fix).
//
// Το `import type` παρακάτω ΔΕΝ φορτώνει τίποτα (διαγράφεται εντελώς στο
// build, καθαρά τύποι) — υπάρχει ΜΟΝΟ ώστε το `monaco.editor.ITextModel`
// κ.λπ. να παραμένουν έγκυρες ΤΥΠΟΙ-αναφορές σε type annotations παντού στο
// αρχείο. Η πραγματική, εκτελέσιμη ΤΙΜΗ `monaco` δηλώνεται ξεχωριστά, τοπικά
// μέσα στο `boot()`, από το δυναμικό `import(...)` — οι δύο συνυπάρχουν
// χωρίς σύγκρουση, αφού ένα type-only import namespace δεν εισάγει ΤΙΜΗ.
import type * as monaco from 'monaco-editor/editor/editor.api.js';
import {
  Dialect,
  tokenize,
  parse,
  parseFragment,
  interpret,
  interpretStatements,
  RuntimeArray,
  formatRuntimeValue,
  detectSelectionStructures,
  detectRepetitionStructures,
  replaceChain,
  computeNodeDepths,
  printStatements,
  printProgram,
  printSubprogram,
  printDeclarationsSection,
  printProgramAsNaturalLanguageSteps,
  printStatementsAsNaturalLanguageSteps,
  parseNaturalLanguageStepsToProgram,
  parseNaturalLanguageStepsToStatements,
  convertAllSubprograms,
  TokenType,
  normalizeKeywordText,
  BUILTIN_MATH_FUNCTION_NAMES,
  findUndeclaredGlossaReference,
  findReusedLoopCounter,
  findConstantReassignment,
  findParameterKindMismatch,
  findWholeArrayAssignment,
  findDuplicateDeclaration,
  findLockedForCounterReassignment,
  findArrayParameterSizeMismatch,
  findArrayDimensionMismatch,
  dimensionWord,
  indexWord,
  findDuplicateParameterName,
  findBuiltinFunctionNameCollision,
  findReservedFunctionNameAsIdentifier,
  findSwitchArrayDiscriminant,
  findReadBooleanTarget,
  findArrayFillSuggestions,
  findArrayFillSuggestionsInStatements,
  findMultiReadFillSuggestions,
  findMultiReadFillSuggestionsInStatements,
  generateArrayFillInputText,
} from '../../src/index.js';
import type {
  LexError,
  ParseError,
  RuntimeError,
  RuntimeValue,
  SelectionShape,
  RepetitionShape,
  Statement,
  VariableScope,
  GlossaProgram,
  Program,
  Token,
  UndeclaredReference,
  ReusedLoopCounter,
  ConstantReassignment,
  ParameterKindMismatch,
  WholeArrayAssignment,
  DuplicateDeclaration,
  LockedCounterReassignment,
  ArrayParameterSizeMismatch,
  ArrayDimensionMismatch,
  DuplicateParameterName,
  BuiltinFunctionNameCollision,
  ReservedFunctionNameUse,
  SwitchArrayDiscriminant,
  ReadBooleanTarget,
  ArrayFillSuggestion,
  ArrayFillOverride,
  CharacterAlphabet,
  MultiReadFillSuggestion,
} from '../../src/index.js';
// (Σκόπιμα ΧΩΡΙΣ `import './style.css'` εδώ — το index.html έχει ήδη δικό
// του `<link rel="stylesheet" href="./src/style.css">`, που το build πλέον
// κάνει inline μέσα στο HTML (βλ. `inlineStylesheetsPlugin` στο
// vite.config.ts). Ένα ΕΠΙΠΛΕΟΝ import εδώ έκανε τον Vite να εισάγει ΚΑΙ
// ένα δεύτερο, δυναμικό αίτημα δικτύου για το ΙΔΙΟ CSS — αχρείαστο, το
// αναίρεσε το inline fix.)

// --- DOM στοιχεία ----------------------------------------------------------

const editorContainer = document.getElementById('editor-container')!;
const outputEl = document.getElementById('console-output')!;
const dialectSelect = document.getElementById('dialect-select') as HTMLSelectElement;
const saveCodeBtn = document.getElementById('save-code-btn') as HTMLButtonElement;
const openCodeBtn = document.getElementById('open-code-btn') as HTMLButtonElement;
const openCodeInput = document.getElementById('open-code-input') as HTMLInputElement;
const runBtn = document.getElementById('run-btn') as HTMLButtonElement;
const stopBtn = document.getElementById('stop-btn') as HTMLButtonElement;
const newDocumentBtn = document.getElementById('new-document-btn') as HTMLButtonElement;
const statusText = document.getElementById('status-text')!;
const cursorPositionEl = document.getElementById('cursor-position')!;
const keywordPanel = document.getElementById('keyword-panel')!;
const keywordPanelBody = document.getElementById('keyword-panel-body')!;
const keywordPanelResizeHandle = document.getElementById('keyword-panel-resize-handle')!;
const keywordPanelToggleBtn = document.getElementById('keyword-panel-toggle') as HTMLButtonElement;
const variablesPanelBody = document.getElementById('variables-panel-body')!;
const variablesPanelLogo = document.getElementById('variables-panel-logo') as HTMLDivElement;
const variablesPanel = document.getElementById('variables-panel')!;
const variablesResizeHandle = document.getElementById('variables-resize-handle')!;
const themeToggleBtn = document.getElementById('theme-toggle-btn') as HTMLButtonElement;
const flowchartContainer = document.getElementById('flowchart-container')!;
const flowchartZoomControls = document.getElementById('flowchart-zoom-controls')!;
const subprogramConversionContainer = document.getElementById('subprogram-conversion-container')!;
const subprogramConversionNote = document.getElementById('subprogram-conversion-note')!;
const slowExecutionToggleBtn = document.getElementById('slow-execution-toggle') as HTMLButtonElement;
const editorPane = document.getElementById('editor-pane')!;
const outputPane = document.getElementById('output-pane')!;
const editorOutputResizeHandle = document.getElementById('editor-output-resize-handle')!;
const consoleOutputHalf = document.getElementById('console-output-half')!;
const outputSplitResizeHandle = document.getElementById('output-split-resize-handle')!;
/**
 * Τα 4 ιστορικά ονόματα (`flowchartPaneHalf`/`variablesPanel`/κ.λπ.) είναι
 * πλέον απλά ΘΕΣΕΙΣ (positional slots) — ό,τι περιεχόμενο δείχνουν σήμερα
 * το αποφασίζει το `slotContent`/`setSlotContent` πιο πάνω (βλ. "Εναλλάξιμα
 * πλαίσια"), όχι το όνομά τους εδώ. Οι λαβές αλλαγής μεγέθους/collapse-
 * κουμπιά παρακάτω μένουν καλωδιωμένα πάνω σε αυτά τα ΙΔΙΑ ονόματα/ids,
 * ανεξάρτητα από περιεχόμενο.
 */
const flowchartPaneHalf = document.getElementById('flowchart-pane-half')!;
const flowchartPanelToggleBtn = document.getElementById('flowchart-panel-toggle') as HTMLButtonElement;
const variablesPanelToggleBtn = document.getElementById('variables-panel-toggle') as HTMLButtonElement;
const outputPaneToggleBtn = document.getElementById('output-pane-toggle') as HTMLButtonElement;
const fontDecreaseBtn = document.getElementById('font-decrease-btn') as HTMLButtonElement;
const fontIncreaseBtn = document.getElementById('font-increase-btn') as HTMLButtonElement;
const flowchartZoomOutBtn = document.getElementById('flowchart-zoom-out-btn') as HTMLButtonElement;
const flowchartZoomInBtn = document.getElementById('flowchart-zoom-in-btn') as HTMLButtonElement;
const flowchartDownloadBtn = document.getElementById('flowchart-download-btn') as HTMLButtonElement;
const segmentToggleBtn = document.getElementById('flowchart-segment-toggle') as HTMLButtonElement;
const appToast = document.getElementById('app-toast')!;
const appToastTitle = document.getElementById('app-toast-title')!;
const appToastBody = document.getElementById('app-toast-body')!;
const appConfirmToast = document.getElementById('app-confirm-toast')!;
const appConfirmToastTitle = document.getElementById('app-confirm-toast-title')!;
const appConfirmToastBody = document.getElementById('app-confirm-toast-body')!;
const appConfirmToastYesBtn = document.getElementById('app-confirm-toast-yes') as HTMLButtonElement;
const appConfirmToastNoBtn = document.getElementById('app-confirm-toast-no') as HTMLButtonElement;

/**
 * Σύντομη ειδοποίηση στο κέντρο της οθόνης (fade in/out, βλ. style.css
 * `.app-toast`) — χρησιμοποιείται από ΟΠΟΙΟΔΗΠΟΤΕ toggle κουμπί της
 * εργαλειοθήκης θέλει να εξηγήσει ΤΙ άλλαξε, χωρίς να μπλοκάρει τίποτα
 * (δεν χρειάζεται κλείσιμο, χάνεται μόνη της). Κάθε νέα κλήση ακυρώνει
 * τυχόν προηγούμενο χρονόμετρο — αν ο μαθητής πατήσει ένα toggle (ή δύο
 * διαφορετικά, στη σειρά) πολύ γρήγορα, βλέπει πάντα το ΤΕΛΕΥΤΑΙΟ μήνυμα,
 * με φρέσκο χρόνο.
 */
const TOAST_DURATION_MS = 4100;
let appToastTimer: ReturnType<typeof setTimeout> | undefined;

function showToast(title: string, body: string): void {
  appToastTitle.textContent = title;
  appToastBody.textContent = body;
  if (appToastTimer !== undefined) clearTimeout(appToastTimer);
  appToast.classList.add('is-visible');
  appToastTimer = setTimeout(() => {
    appToast.classList.remove('is-visible');
    appToastTimer = undefined;
  }, TOAST_DURATION_MS);
}

function showSegmentModeToast(active: boolean): void {
  showToast(
    active ? 'Ενεργοποίηση Τμήματος Αλγορίθμου' : 'Απενεργοποίηση Τμήματος Αλγορίθμου',
    active
      ? 'Τώρα μπορείτε να γράψετε και να εκτελέσετε ένα κομμάτι του αλγορίθμου και όχι ολόκληρο τον κώδικα.'
      : 'Τώρα ξαναγράφετε ολόκληρο τον κώδικα, με πλήρες περίβλημα (Πρόγραμμα/Αλγόριθμος...Τέλος).',
  );
}

function showSlowExecutionToast(active: boolean): void {
  showToast(
    active ? 'Ενεργοποίηση Αργής Εκτέλεσης' : 'Απενεργοποίηση Αργής Εκτέλεσης',
    active
      ? 'Τώρα η εκτέλεση θα σταματά για λίγο μετά από κάθε εντολή, ώστε να παρακολουθείτε βήμα-βήμα την τρέχουσα γραμμή και τις μεταβλητές.'
      : 'Τώρα ο κώδικας θα εκτελείται κανονικά, χωρίς καθυστέρηση ανάμεσα στις εντολές.',
  );
}

/**
 * Ίδιο glass στυλ με το `showToast` παραπάνω, αλλά ΓΙΑ ΕΡΩΤΗΣΗ ΝΑΙ/ΟΧΙ —
 * αντικαθιστά το native `window.confirm` (πιο μοντέρνο, ταιριάζει με την
 * υπόλοιπη αισθητική). Σε αντίθεση με το `showToast`, ΔΕΝ χάνεται μόνο του —
 * ΠΕΡΙΜΕΝΕΙ οριστικά απάντηση από τον μαθητή (κλικ σε "Ναι" ή "Όχι"), όσο
 * χρόνο κι αν χρειαστεί, ίδια λογική με το native `window.confirm` που
 * αντικαθιστά (κι αυτό δεν είχε χρονικό όριο).
 */
function showConfirmToast(title: string, body: string): Promise<boolean> {
  appConfirmToastTitle.textContent = title;
  appConfirmToastBody.textContent = body;
  appConfirmToast.classList.add('is-visible');
  return new Promise<boolean>((resolve) => {
    const finish = (result: boolean): void => {
      appConfirmToast.classList.remove('is-visible');
      appConfirmToastYesBtn.removeEventListener('click', onYes);
      appConfirmToastNoBtn.removeEventListener('click', onNo);
      resolve(result);
    };
    const onYes = (): void => finish(true);
    const onNo = (): void => finish(false);
    appConfirmToastYesBtn.addEventListener('click', onYes);
    appConfirmToastNoBtn.addEventListener('click', onNo);
  });
}
const centerColumn = document.getElementById('center-column')!;
const transformBtn = document.getElementById('transform-btn') as HTMLButtonElement;
const conversionPanel = document.getElementById('conversion-panel')!;
const conversionInputContainer = document.getElementById('conversion-input-container')!;
const conversionInputPlaceholder = document.getElementById('conversion-input-placeholder')!;
const conversionOptions = document.getElementById('conversion-options')!;
const conversionOutputContainer = document.getElementById('conversion-output-container')!;
const conversionOutputPlaceholder = document.getElementById('conversion-output-placeholder')!;
const conversionPanelCloseBtn = document.getElementById('conversion-panel-close') as HTMLButtonElement;
const appMain = document.getElementById('app-main')!;
const mobileTabs = document.getElementById('mobile-tabs')!;
const mobileQuickStrip = document.getElementById('mobile-quick-strip')!;
const conversionMobileQuickStrip = document.getElementById('conversion-mobile-quick-strip')!;
const codeEditorLabel = document.getElementById('code-editor-label')!;
const editorSplitControls = document.getElementById('editor-split-controls')!;
const naturalEditorBox = document.getElementById('natural-editor-box')!;
const naturalEditorContainer = document.getElementById('natural-editor-container')!;
const toNaturalBtn = document.getElementById('to-natural-btn') as HTMLButtonElement;
const toCodeBtn = document.getElementById('to-code-btn') as HTMLButtonElement;

// --- Θέμα (φωτεινό/σκοτεινό) --------------------------------------------------

const THEME_STORAGE_KEY = 'glossa-theme';

/** Το data-theme το έχει ήδη ορίσει το inline script στο <head> (πριν από κάθε render, βλ. index.html). */
function currentTheme(): 'light' | 'dark' {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

/**
 * Μόνο το "χρώμα" της σελίδας (data-theme/localStorage/εικονίδιο) — ΧΩΡΙΣ
 * αναφορά στον Monaco, ώστε να δουλεύει αμέσως, ΠΡΙΝ καν φορτώσει το
 * `boot()` (βλ. πιο κάτω) — το κουμπί θέματος είναι πάντα προσβάσιμο (όχι
 * μόνο σε κινητό), άρα ο χρήστης μπορεί να το πατήσει ενώ φορτώνει ακόμα ο
 * editor. Το πραγματικό θέμα ΤΟΥ editor ενημερώνεται ξεχωριστά, μέσω του
 * `onThemeChange` hook που καλωδιώνει το `boot()` μόλις είναι έτοιμο.
 */
function applyThemeChrome(theme: 'light' | 'dark'): void {
  document.documentElement.dataset.theme = theme;
  localStorage.setItem(THEME_STORAGE_KEY, theme);
  themeToggleBtn.textContent = theme === 'dark' ? '☀️' : '🌙';
}

/** Ανατίθεται μέσα στο `boot()`, μόλις φορτώσει ο Monaco — μέχρι τότε `null`, οπότε το κλικ στο κουμπί θέματος αλλάζει σωστά το «χρώμα» της σελίδας αλλά δεν έχει ακόμα να ενημερώσει το θέμα ΤΟΥ editor (θα το πάρει ούτως ή άλλως από το `currentTheme()` μόλις έτοιμος, βλ. `boot()`). */
let onThemeChange: ((theme: 'light' | 'dark') => void) | null = null;

themeToggleBtn.addEventListener('click', () => {
  const next = currentTheme() === 'dark' ? 'light' : 'dark';
  applyThemeChrome(next);
  onThemeChange?.(next);
});

// --- Εναλλάξιμα πλαίσια (Δεσμευμένες Λέξεις / Διάγραμμα ροής / Οθόνη εκτέλεσης / Μεταβλητές) ---

/**
 * Οι 4 ΘΕΣΕΙΣ (slots) στην οθόνη είναι σταθερές — ίδιο id, ίδια λαβή αλλαγής
 * μεγέθους/collapse-toggle, ίδιο tab κινητού, ό,τι περιεχόμενο κι αν δείχνουν
 * κάθε φορά (βλ. index.html). Το `<select>` σε κάθε header επιλέγει ΤΙ
 * φαίνεται εκεί — η επιλογή ΑΝΤΑΛΛΑΣΣΕΙ με τη θέση που ήδη έδειχνε αυτό το
 * περιεχόμενο (καμία θέση δεν μένει ποτέ κενή, κανένα περιεχόμενο δεν
 * εμφανίζεται ποτέ σε δύο θέσεις ταυτόχρονα — bijection πάντα). Καθαρά
 * χειρισμός DOM (μετακίνηση ήδη υπαρχόντων στοιχείων, όχι re-render) —
 * καμία εξάρτηση από τον Monaco, γι' αυτό ζει εδώ, στην fast zone, ΠΡΙΝ το
 * `boot()` — η θέση του χρήστη εφαρμόζεται όσο το δυνατόν νωρίτερα, πριν
 * καν προλάβει να φανεί η προεπιλεγμένη διάταξη.
 */
type PanelContentKind = 'keywords' | 'flowchart' | 'output' | 'variables';
type PanelSlotId = 'keyword-panel' | 'console-output-half' | 'flowchart-pane-half' | 'variables-panel';

const PANEL_SLOT_IDS: readonly PanelSlotId[] = [
  'keyword-panel',
  'console-output-half',
  'flowchart-pane-half',
  'variables-panel',
];

const PANEL_CONTENT_LABEL: Record<PanelContentKind, string> = {
  keywords: 'Δεσμευμένες Λέξεις',
  flowchart: 'Διάγραμμα ροής',
  output: 'Οθόνη εκτέλεσης',
  variables: 'Μεταβλητές',
};

/** Ταιριάζει ΑΚΡΙΒΩΣ τη σημερινή, ήδη εγκατεστημένη εμφάνιση (το index.html ήδη σερβίρει τα bundles σε αυτή τη διάταξη) — μηδενική οπτική αλλαγή για όποιον δεν αγγίξει ποτέ ένα `<select>`. */
const DEFAULT_SLOT_CONTENT: Record<PanelSlotId, PanelContentKind> = {
  'keyword-panel': 'keywords',
  'console-output-half': 'output',
  'flowchart-pane-half': 'variables',
  'variables-panel': 'flowchart',
};

/**
 * ΣΚΟΠΙΜΑ όχι localStorage εδώ (σε αντίθεση με θέμα/μέγεθος γραμματοσειράς
 * πιο πάνω/κάτω) — ρητό αίτημα: κάθε ανανέωση σελίδας πρέπει ΠΑΝΤΑ να
 * ξεκινάει από την προεπιλεγμένη διάταξη, ό,τι κι αν είχε επιλέξει ο
 * χρήστης πριν (μια ανταλλαγή θέσεων είναι κάτι πρόχειρο/της στιγμής, όχι
 * μόνιμη προτίμηση). Bug report πριν από αυτή τη διόρθωση: "αλλάζω τις
 * θέσεις και με refresh παραμένουν εκεί που είναι — δεν επανέρχονται στις
 * αρχικές".
 */
const slotContent: Record<PanelSlotId, PanelContentKind> = { ...DEFAULT_SLOT_CONTENT };

function findSlotFor(kind: PanelContentKind): PanelSlotId {
  for (const slot of PANEL_SLOT_IDS) {
    if (slotContent[slot] === kind) return slot;
  }
  // Αδύνατο όσο το slotContent παραμένει έγκυρη μετάθεση (το εγγυώνται
  // loadPanelLayout/setSlotContent) — μόνο για να ικανοποιηθεί ο TypeScript.
  throw new Error(`Κανένα slot δεν δείχνει "${kind}"`);
}

const panelSlotMounts: Record<PanelSlotId, HTMLElement> = {
  'keyword-panel': document.querySelector('[data-slot-mount="keyword-panel"]')!,
  'console-output-half': document.querySelector('[data-slot-mount="console-output-half"]')!,
  'flowchart-pane-half': document.querySelector('[data-slot-mount="flowchart-pane-half"]')!,
  'variables-panel': document.querySelector('[data-slot-mount="variables-panel"]')!,
};
const panelSlotExtrasMounts: Record<PanelSlotId, HTMLElement> = {
  'keyword-panel': document.querySelector('[data-slot-extras="keyword-panel"]')!,
  'console-output-half': document.querySelector('[data-slot-extras="console-output-half"]')!,
  'flowchart-pane-half': document.querySelector('[data-slot-extras="flowchart-pane-half"]')!,
  'variables-panel': document.querySelector('[data-slot-extras="variables-panel"]')!,
};
const panelSlotHeadings: Record<PanelSlotId, HTMLElement> = {
  'keyword-panel': document.querySelector('[data-slot-heading="keyword-panel"]')!,
  'console-output-half': document.querySelector('[data-slot-heading="console-output-half"]')!,
  'flowchart-pane-half': document.querySelector('[data-slot-heading="flowchart-pane-half"]')!,
  'variables-panel': document.querySelector('[data-slot-heading="variables-panel"]')!,
};
const panelSlotSelects: Record<PanelSlotId, HTMLSelectElement> = {
  'keyword-panel': document.querySelector('select.panel-content-select[data-slot="keyword-panel"]')!,
  'console-output-half': document.querySelector('select.panel-content-select[data-slot="console-output-half"]')!,
  'flowchart-pane-half': document.querySelector('select.panel-content-select[data-slot="flowchart-pane-half"]')!,
  'variables-panel': document.querySelector('select.panel-content-select[data-slot="variables-panel"]')!,
};
const panelContentBundles: Record<PanelContentKind, HTMLElement> = {
  keywords: document.getElementById('content-keywords')!,
  output: document.getElementById('content-output')!,
  flowchart: document.getElementById('content-flowchart')!,
  variables: document.getElementById('content-variables')!,
};
/** Μόνο το "Διάγραμμα ροής" έχει έξτρα κουμπιά στο header του σήμερα (zoom/λήψη διαγράμματος) — τα άλλα 3 απλά δεν έχουν αντίστοιχο bundle εδώ (το "Ιστορικό" των Μεταβλητών αφαιρέθηκε — πλέον δείχνεται πάντα το πλήρες ιστορικό, χωρίς toggle). */
const panelContentExtrasBundles: Partial<Record<PanelContentKind, HTMLElement>> = {
  flowchart: document.getElementById('content-flowchart-extras')!,
};

function mobileTabBtnForSlot(slot: PanelSlotId): HTMLButtonElement | null {
  return document.querySelector<HTMLButtonElement>(`.mobile-tab-btn[data-tab="${slot}"]`);
}

/**
 * Μετακινεί το bundle περιεχομένου `kind` (+ τυχόν έξτρα κουμπιά του) στη
 * θέση `slot`, ενημερώνει το `<select>`/sr-only heading της — ΚΑΘΑΡΑ
 * χειρισμός DOM, ΔΕΝ αγγίζει το state `slotContent` (αυτό το κάνει ο
 * καλών, βλ. `setSlotContent`/`applyInitialPanelLayout`).
 */
function applySlotContent(slot: PanelSlotId, kind: PanelContentKind): void {
  panelSlotMounts[slot].appendChild(panelContentBundles[kind]);
  const extras = panelContentExtrasBundles[kind];
  if (extras !== undefined) panelSlotExtrasMounts[slot].appendChild(extras);
  panelSlotSelects[slot].value = kind;
  panelSlotHeadings[slot].textContent = PANEL_CONTENT_LABEL[kind];
}

/**
 * Ξαναγράφει τα aria-label/title strings στις λαβές αλλαγής μεγέθους/
 * collapse-κουμπιά των 4 θέσεων — αυτά ζουν ΕΞΩ από τα ίδια τα content
 * bundles (ανήκουν στη ΘΕΣΗ, όχι στο περιεχόμενο, βλ. index.html), οπότε
 * δεν ταξιδεύουν αυτόματα μαζί του σε μια ανταλλαγή· χωρίς αυτό, ένας
 * χρήστης screen reader θα άκουγε π.χ. "απόκρυψη πλαισίου Μεταβλητές" πάνω
 * σε ένα κουμπί που πλέον κρύβει κάτι εντελώς διαφορετικό. Ξαναϋπολογίζει
 * ΟΛΑ μαζί κάθε φορά (φτηνό, μετράει 8 ιδιότητες) αντί να προσπαθεί να
 * καταλάβει "ποιο ακριβώς άλλαξε" — απλούστερο, καμία πιθανότητα να
 * ξεχαστεί κάποιο.
 */
function refreshSlotChromeLabels(): void {
  const kw = PANEL_CONTENT_LABEL[slotContent['keyword-panel']];
  const left = PANEL_CONTENT_LABEL[slotContent['console-output-half']];
  const right = PANEL_CONTENT_LABEL[slotContent['flowchart-pane-half']];
  const fc = PANEL_CONTENT_LABEL[slotContent['variables-panel']];

  keywordPanelResizeHandle.setAttribute('aria-label', `Αλλαγή πλάτους πλαισίου ${kw}`);
  keywordPanelToggleBtn.setAttribute('aria-label', `Εμφάνιση/απόκρυψη πλαισίου ${kw}`);
  keywordPanelToggleBtn.title = `Εμφάνιση/απόκρυψη πλαισίου ${kw}`;

  editorOutputResizeHandle.setAttribute('aria-label', `Αλλαγή ύψους πλαισίου κώδικα / ${left} - ${right}`);
  outputPaneToggleBtn.setAttribute('aria-label', `Εμφάνιση/απόκρυψη πλαισίου ${left} / ${right}`);
  outputPaneToggleBtn.title = `Εμφάνιση/απόκρυψη πλαισίου ${left} / ${right}`;

  outputSplitResizeHandle.setAttribute('aria-label', `Αλλαγή πλάτους πλαισίου ${left} / πλαισίου ${right}`);
  variablesPanelToggleBtn.setAttribute('aria-label', `Εμφάνιση/απόκρυψη πλαισίου ${right}`);
  variablesPanelToggleBtn.title = `Εμφάνιση/απόκρυψη πλαισίου ${right}`;

  variablesResizeHandle.setAttribute('aria-label', `Αλλαγή πλάτους πλαισίου ${fc}`);
  flowchartPanelToggleBtn.setAttribute('aria-label', `Εμφάνιση/απόκρυψη πλαισίου ${fc}`);
  flowchartPanelToggleBtn.title = `Εμφάνιση/απόκρυψη πλαισίου ${fc}`;
}

/**
 * Το κείμενο των 3 (από τις 4) καρτελών κινητού που αντιστοιχούν σε
 * εναλλάξιμη θέση — η "Κώδικας" μένει πάντα σταθερή, και το
 * `console-output-half` δεν έχει δική του καρτέλα (ταξιδεύει μαζί με τον
 * Κώδικα, βλ. index.html/style.css). Η θέση που δείχνει Διάγραμμα ροής, όσο
 * είναι σε λειτουργία "Μετατροπή υποπρογράμματος", ξαναγράφεται αμέσως μετά
 * από τις showFlowchartDiagramMode/showSubprogramConversionMode (μέσα στο
 * boot() πιο κάτω) — αν τύχει να ανταλλαγεί πλαίσιο ενώ είναι ενεργή αυτή η
 * ειδική λειτουργία, η ετικέτα θα δείξει στιγμιαία "Διάγραμμα ροής" αντί
 * για "Μετατροπή" μέχρι την επόμενη ανανέωση του διαγράμματος (π.χ. την
 * επόμενη αλλαγή κώδικα) — αμελητέο, σπάνιο σενάριο.
 */
function refreshMobileTabLabels(): void {
  for (const slot of PANEL_SLOT_IDS) {
    if (slot === 'console-output-half') continue;
    const btn = mobileTabBtnForSlot(slot);
    if (btn === null) continue;
    btn.textContent = PANEL_CONTENT_LABEL[slotContent[slot]];
  }
}

/** Πάντα εφαρμόζει την ΠΡΟΕΠΙΛΟΓΗ (βλ. σχόλιο στο `slotContent` — καμία αποθήκευση) — στην πράξη απλά επιβεβαιώνει ό,τι ήδη δείχνει το στατικό HTML, αλλά μένει ρητό εδώ ώστε τα `<select>`/labels να ξεκινούν πάντα σωστά συγχρονισμένα με το `slotContent`. */
function applyInitialPanelLayout(): void {
  for (const slot of PANEL_SLOT_IDS) {
    applySlotContent(slot, slotContent[slot]);
  }
  refreshSlotChromeLabels();
  refreshMobileTabLabels();
}
applyInitialPanelLayout();

function setSlotContent(slot: PanelSlotId, newKind: PanelContentKind): void {
  const oldKind = slotContent[slot];
  if (oldKind === newKind) return;
  const otherSlot = findSlotFor(newKind);
  applySlotContent(slot, newKind);
  applySlotContent(otherSlot, oldKind);
  slotContent[slot] = newKind;
  slotContent[otherSlot] = oldKind;
  refreshSlotChromeLabels();
  refreshMobileTabLabels();
}

for (const slot of PANEL_SLOT_IDS) {
  panelSlotSelects[slot].addEventListener('change', () => {
    setSlotContent(slot, panelSlotSelects[slot].value as PanelContentKind);
  });
}

/**
 * "Περισσότερα"/"Λιγότερα" — ορατό ΜΟΝΟ σε κινητό (βλ. style.css), ανοίγει/
 * κλείνει το #toolbar-more (Οδηγός Χρήσης/Επικοινωνία/Αποθήκευση/Άνοιγμα/
 * A-/A+/🌙), που εκεί μένει κρυμμένο από προεπιλογή ώστε η εργαλειοθήκη να
 * χωράει σε μία συμπαγή γραμμή (🐢▶🧹⇄🧩) και να μένει περισσότερο ύψος για
 * τον editor. Το ίδιο το κείμενο του κουμπιού εναλλάσσεται ανάλογα με την
 * κατάσταση, ώστε να είναι σαφές τι θα κάνει το επόμενο κλικ.
 */
const toolbarMoreToggleBtn = document.getElementById('toolbar-more-toggle') as HTMLButtonElement;
const toolbarMore = document.getElementById('toolbar-more')!;
toolbarMoreToggleBtn.addEventListener('click', () => {
  const expanded = toolbarMore.classList.toggle('is-expanded');
  toolbarMoreToggleBtn.setAttribute('aria-expanded', String(expanded));
  toolbarMoreToggleBtn.textContent = expanded ? 'Λιγότερα' : 'Περισσότερα';
});

/**
 * "Σχετικά" (footer, δίπλα στο "Όροι Χρήσης") — ανοίγει το κρυμμένο
 * `#about-tool` (βλ. σχόλιο στο index.html: το native <summary> του είναι
 * πλέον `.sr-only`, δεν πιάνει ύψος στη σελίδα όσο είναι κλειστό) και κάνει
 * scroll εκεί. Ρητό `preventDefault` + JS (όχι μόνο `href="#about-tool"`) —
 * η native συμπεριφορά ΘΑ έκανε ήδη scroll/auto-open σε σύγχρονους
 * browsers, αλλά ΔΕΝ είναι καθολικά αξιόπιστη· το ρητό `.open = true` +
 * `scrollIntoView` εγγυάται το ίδιο αποτέλεσμα παντού. Fast zone (καμία
 * εξάρτηση από Monaco) — δουλεύει αμέσως, πριν καν φορτώσει το `boot()`.
 */
const aboutToolLink = document.getElementById('about-tool-link') as HTMLAnchorElement;
const aboutToolDetails = document.getElementById('about-tool') as HTMLDetailsElement;
aboutToolLink.addEventListener('click', (e) => {
  e.preventDefault();
  // Toggle — ξανά κλικ ενώ είναι ήδη ανοιχτό το κλείνει (χωρίς αυτό, το
  // "Σχετικά" έμοιαζε "κολλημένο" ανοιχτό, δεν έκλεινε ποτέ).
  aboutToolDetails.open = !aboutToolDetails.open;
  if (aboutToolDetails.open) aboutToolDetails.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

// --- Αργή εκτέλεση --------------------------------------------------------------

/** Καθυστέρηση (ms) ανάμεσα σε διαδοχικές εντολές όταν είναι ενεργή η "αργή εκτέλεση" — απλή επιβράδυνση, όχι βήμα-βήμα παύση που περιμένει κλικ. */
const SLOW_EXECUTION_DELAY_MS = 600;
let slowExecutionEnabled = true;

slowExecutionToggleBtn.addEventListener('click', () => {
  slowExecutionEnabled = !slowExecutionEnabled;
  slowExecutionToggleBtn.setAttribute('aria-pressed', String(slowExecutionEnabled));
  showSlowExecutionToast(slowExecutionEnabled);
});

// --- Αλλαγή μεγέθους πλαισίου μεταβλητών (drag) --------------------------------

/**
 * Χειροκίνητη υλοποίηση αντί για native CSS `resize` — σε flex layout το
 * native `resize` δεν εφάρμοζε αξιόπιστα το νέο πλάτος (το flex-basis του
 * γονέα «το ξανάπαιρνε πίσω»). Ορίζουμε ρητά `flex-basis` σε κάθε
 * pointermove, που νικά το CSS `flex: 0 0 180px` λόγω inline style.
 *
 * Pointer Events (ΟΧΙ mouse events) — μία ενιαία υλοποίηση που δουλεύει ΚΑΙ
 * με ποντίκι ΚΑΙ με αφή/γραφίδα, χωρίς ξεχωριστό κώδικα για το καθένα (bug
 * report: "στο tablet δεν μπορώ να αλλάξω το μέγεθος των πλαισίων" — τα
 * mousedown/mousemove/mouseup που είχε πριν δεν πυροδοτούνται ΚΑΘΟΛΟΥ από
 * αφή). Το `setPointerCapture` εξασφαλίζει ότι η ΙΔΙΑ λαβή συνεχίζει να
 * λαμβάνει pointermove ΑΚΟΜΑ ΚΙ ΑΝ το δάχτυλο/δρομέας βγει έξω από τα όριά
 * της κατά το σύρσιμο (πολύ πιθανό, αφού η λαβή είναι λεπτή) — χωρίς αυτό,
 * σε αφή το σύρσιμο σταματάει μόλις βγει το δάχτυλο απ' αυτήν. Το
 * `touch-action: none` (βλ. `.panel-resize-handle` στο style.css) εμποδίζει
 * τον browser να ερμηνεύσει το σύρσιμο σαν scroll της σελίδας αντί για
 * αλλαγή μεγέθους.
 */
const VARIABLES_PANEL_MIN_WIDTH = 160;
const VARIABLES_PANEL_MAX_WIDTH = 640;

variablesResizeHandle.addEventListener('pointerdown', (e) => {
  e.preventDefault();
  variablesResizeHandle.setPointerCapture(e.pointerId);
  const startX = e.clientX;
  const startWidth = variablesPanel.getBoundingClientRect().width;
  variablesResizeHandle.classList.add('is-dragging');

  const onPointerMove = (moveEvent: PointerEvent): void => {
    // Το πλαίσιο είναι στη δεξιά άκρη — σύρσιμο προς τα αριστερά (dx < 0) το μεγαλώνει.
    const dx = moveEvent.clientX - startX;
    const newWidth = Math.min(VARIABLES_PANEL_MAX_WIDTH, Math.max(VARIABLES_PANEL_MIN_WIDTH, startWidth - dx));
    variablesPanel.style.flex = `0 0 ${newWidth}px`;
  };

  const onPointerUp = (): void => {
    variablesResizeHandle.classList.remove('is-dragging');
    variablesResizeHandle.removeEventListener('pointermove', onPointerMove);
    variablesResizeHandle.removeEventListener('pointerup', onPointerUp);
    variablesResizeHandle.removeEventListener('pointercancel', onPointerUp);
  };

  variablesResizeHandle.addEventListener('pointermove', onPointerMove);
  variablesResizeHandle.addEventListener('pointerup', onPointerUp);
  variablesResizeHandle.addEventListener('pointercancel', onPointerUp);
});

/** Ίδια τεχνική με το πλαίσιο μεταβλητών, αλλά κάθετη αλλαγή μεγέθους (ύψος) ανάμεσα σε κώδικα και οθόνη εκτέλεσης/διάγραμμα. */
const EDITOR_PANE_MIN_HEIGHT = 120;

/**
 * ΜΟΝΟ σε tablet landscape (ΙΔΙΟ media query με το style.css, βλ. σχόλιο εκεί
 * για ΓΙΑΤΙ το Λέξεις-κλειδιά/Διάγραμμα ροής χρειάζονται ρητό JS ύψος αντί
 * για CSS stretch): το ύψος τους πρέπει να ΞΑΝΑΤΑΙΡΙΑΞΕΙ με το πραγματικό
 * ύψος του `.center-column` (Κώδικας + Οθόνη εκτέλεσης) ΚΑΘΕ φορά που αυτό
 * αλλάζει — αλλιώς, μόλις ο μαθητής σύρει τη λαβή `editorOutputResizeHandle`
 * παρακάτω, το Κώδικας/Οθόνη εκτέλεσης αλλάζουν μέγεθος αλλά το Λέξεις-
 * κλειδιά/Διάγραμμα ροής μένουν "κολλημένα" στο ΠΑΛΙΟ ύψος — δίνοντας την
 * εντύπωση ότι τα πλαίσια "μετακινούνται" αντί να αλλάζουν μέγεθος (bug
 * report). Καλείται στο load, σε αλλαγή μεγέθους/προσανατολισμού παραθύρου,
 * ΚΑΙ σε κάθε pointermove όσο σέρνεται η λαβή Κώδικα/Οθόνης εκτέλεσης.
 */
const TABLET_LANDSCAPE_QUERY =
  '(orientation: landscape) and (min-width: 641px) and (max-width: 1200px) and (min-height: 501px)';

function syncKeywordVariablesPanelHeight(): void {
  if (!window.matchMedia(TABLET_LANDSCAPE_QUERY).matches) {
    // Εκτός tablet landscape δεν πρέπει να έχουν inline ύψος — αφήνει το
    // κανονικό CSS (stretch σε desktop, mobile-tabs σε κινητό/tablet portrait)
    // να αναλάβει, χωρίς να μείνει "κολλημένη" μια παλιά τιμή από προηγούμενο
    // πλάτος/προσανατολισμό.
    keywordPanel.style.height = '';
    variablesPanel.style.height = '';
    return;
  }
  const height = centerColumn.getBoundingClientRect().height;
  if (height > 0) {
    keywordPanel.style.height = `${height}px`;
    variablesPanel.style.height = `${height}px`;
  }
}

syncKeywordVariablesPanelHeight();
window.addEventListener('resize', syncKeywordVariablesPanelHeight);

editorOutputResizeHandle.addEventListener('pointerdown', (e) => {
  // Τίποτα να αλλάξει μέγεθος όσο η Οθόνη εκτέλεσης είναι κρυμμένη (βλ.
  // `setOutputPaneVisible`) — το κουμπί εμφάνισης/απόκρυψης πάνω στην ΙΔΙΑ
  // λαβή παραμένει πατήσιμο (δικό του stopPropagation, δεν φτάνει ποτέ εδώ).
  if (outputPane.hidden) return;
  e.preventDefault();
  editorOutputResizeHandle.setPointerCapture(e.pointerId);
  const startY = e.clientY;
  const startHeight = editorPane.getBoundingClientRect().height;
  // Σε ορισμένα breakpoints (π.χ. tablet landscape) το CSS επιβάλλει δικό
  // του, αυστηρότερο min-height απ' ό,τι το γενικό EDITOR_PANE_MIN_HEIGHT
  // εδώ — χωρίς να το σεβαστούμε ρητά, το σύρσιμο θα προσπαθούσε να
  // μικρύνει κάτω απ' αυτό, αλλά το πλαίσιο θα έμενε "κολλημένο" στο CSS
  // όριο ενώ ο δρομέας συνεχίζει να κινείται — αίσθηση ότι κάτι δεν
  // αλλάζει μέγεθος σωστά.
  const cssMinHeight = parseFloat(getComputedStyle(editorPane).minHeight) || 0;
  const effectiveMinHeight = Math.max(EDITOR_PANE_MIN_HEIGHT, cssMinHeight);
  // Σε tablet landscape το `.center-column` έχει ΠΛΕΟΝ σταθερό, ρητό ύψος
  // (βλ. style.css) — το Κώδικας/Οθόνη εκτέλεσης πρέπει να ΑΝΑΔΙΑΝΕΜΟΥΝ
  // ΑΥΤΟ το σταθερό ύψος μεταξύ τους (ρητό αίτημα: "το μέγεθος της σελίδας
  // ... όταν μετακινώ αυτό να μην αλλάζει"), ΟΧΙ να μεγαλώνει το Κώδικας
  // ελεύθερα (όπως πριν, `EDITOR_PANE_MAX_HEIGHT`), κάτι που άφηνε ολόκληρη
  // τη στήλη να μεγαλώνει ασύστολα. Το `outputHeight` (μαζί με το δικό του
  // min-height) δίνει το ΠΑΝΩ όριο εδώ — έτσι η λαβή μπορεί να πάει ΚΑΙ
  // προς τα πάνω ΚΑΙ προς τα κάτω, μέσα στον σταθερό συνολικό χώρο.
  const outputHeight = outputPane.getBoundingClientRect().height;
  const outputMinHeight = parseFloat(getComputedStyle(outputPane).minHeight) || EDITOR_PANE_MIN_HEIGHT;
  const totalHeight = startHeight + outputHeight;
  // Χωρίς επιπλέον σταθερό cap (υπήρχε παλιά ένα EDITOR_PANE_MAX_HEIGHT=900
  // εδώ, αφαιρέθηκε) — το `totalHeight - outputMinHeight` είναι ήδη το
  // σωστό, δυναμικό όριο (όσο επιτρέπει η πραγματική οθόνη), το κουμπί
  // πρέπει να μπορεί να πάει "μέχρι κάτω" (μέχρι να φτάσει η Οθόνη
  // εκτέλεσης στο δικό της ελάχιστο), όχι να κολλάει νωρίτερα σε τυχαίο
  // σταθερό νούμερο.
  const effectiveMaxHeight = totalHeight - outputMinHeight;
  editorOutputResizeHandle.classList.add('is-dragging');

  const onPointerMove = (moveEvent: PointerEvent): void => {
    const dy = moveEvent.clientY - startY;
    const newHeight = Math.min(effectiveMaxHeight, Math.max(effectiveMinHeight, startHeight + dy));
    // Μόνο το Κώδικας παίρνει ρητό (0 0 Npx) flex-basis — η Οθόνη εκτέλεσης
    // κρατάει το κανονικό της `flex: 1 1 0`, οπότε γεμίζει αυτόματα ό,τι
    // απομένει μέσα στο σταθερό ύψος του `.center-column` (ΙΔΙΑ τεχνική με
    // το desktop — δεν χρειάζεται να οριστεί κι αυτή ρητά).
    editorPane.style.flex = `0 0 ${newHeight}px`;
    syncKeywordVariablesPanelHeight();
  };

  const onPointerUp = (): void => {
    editorOutputResizeHandle.classList.remove('is-dragging');
    editorOutputResizeHandle.removeEventListener('pointermove', onPointerMove);
    editorOutputResizeHandle.removeEventListener('pointerup', onPointerUp);
    editorOutputResizeHandle.removeEventListener('pointercancel', onPointerUp);
  };

  editorOutputResizeHandle.addEventListener('pointermove', onPointerMove);
  editorOutputResizeHandle.addEventListener('pointerup', onPointerUp);
  editorOutputResizeHandle.addEventListener('pointercancel', onPointerUp);
});

/**
 * Ίδια τεχνική, οριζόντια αλλαγή μεγέθους (πλάτος) ανάμεσα στην οθόνη
 * εκτέλεσης και το διάγραμμα ροής. Το `OUTPUT_HALF_MAX_WIDTH` παρακάτω ήταν
 * ένα ΣΤΑΘΕΡΟ 1400px — στην πράξη χωρίς κανένα πραγματικό όριο (bug
 * report: "δεν έχει όριο", η Οθόνη εκτέλεσης μπορούσε να "καταπιεί" σχεδόν
 * όλη τη γραμμή, αφήνοντας τις Μεταβλητές/Διάγραμμα ροής σε ένα χάλκινο
 * σημείο πλάτους — το `#flowchart-pane-half` δεν έχει δικό του min-width).
 * Αντί γι' αυτό, υπολογίζουμε ΤΩΡΑ ένα δυναμικό όριο ανά σύρσιμο: το
 * συνολικό πλάτος της γραμμής (και τα δύο μισά μαζί, ΣΤΑΘΕΡΟ όσο διαρκεί
 * το σύρσιμο) μείον το ελάχιστο που πρέπει να κρατήσει το ΑΛΛΟ μισό — έτσι
 * η λαβή μπορεί να πάει "μέχρι τέρμα" προς όποια πλευρά, αλλά ΠΟΤΕ πέρα
 * από το σημείο που θα εξαφάνιζε το απέναντι πλαίσιο.
 */
const OUTPUT_HALF_MIN_WIDTH = 200;

outputSplitResizeHandle.addEventListener('pointerdown', (e) => {
  e.preventDefault();
  outputSplitResizeHandle.setPointerCapture(e.pointerId);
  const startX = e.clientX;
  const startWidth = consoleOutputHalf.getBoundingClientRect().width;
  const totalWidth = startWidth + flowchartPaneHalf.getBoundingClientRect().width;
  const effectiveMaxWidth = totalWidth - OUTPUT_HALF_MIN_WIDTH;
  outputSplitResizeHandle.classList.add('is-dragging');

  const onPointerMove = (moveEvent: PointerEvent): void => {
    const dx = moveEvent.clientX - startX;
    const newWidth = Math.min(effectiveMaxWidth, Math.max(OUTPUT_HALF_MIN_WIDTH, startWidth + dx));
    consoleOutputHalf.style.flex = `0 0 ${newWidth}px`;
  };

  const onPointerUp = (): void => {
    outputSplitResizeHandle.classList.remove('is-dragging');
    outputSplitResizeHandle.removeEventListener('pointermove', onPointerMove);
    outputSplitResizeHandle.removeEventListener('pointerup', onPointerUp);
    outputSplitResizeHandle.removeEventListener('pointercancel', onPointerUp);
  };

  outputSplitResizeHandle.addEventListener('pointermove', onPointerMove);
  outputSplitResizeHandle.addEventListener('pointerup', onPointerUp);
  outputSplitResizeHandle.addEventListener('pointercancel', onPointerUp);
});

/** Ίδια τεχνική, οριζόντια αλλαγή μεγέθους (πλάτος) του πλαισίου Λέξεις-κλειδιά — αυτό βρίσκεται ΑΡΙΣΤΕΡΑ από τη λαβή του (ίδια γεωμετρία με το `consoleOutputHalf` παραπάνω, όχι σαν το `variablesPanel` που είναι δεξιά), οπότε το σύρσιμο προς τα δεξιά (dx > 0) το μεγαλώνει. */
const KEYWORD_PANEL_MIN_WIDTH = 140;
const KEYWORD_PANEL_MAX_WIDTH = 480;

keywordPanelResizeHandle.addEventListener('pointerdown', (e) => {
  e.preventDefault();
  keywordPanelResizeHandle.setPointerCapture(e.pointerId);
  const startX = e.clientX;
  const startWidth = keywordPanel.getBoundingClientRect().width;
  keywordPanelResizeHandle.classList.add('is-dragging');

  const onPointerMove = (moveEvent: PointerEvent): void => {
    const dx = moveEvent.clientX - startX;
    const newWidth = Math.min(KEYWORD_PANEL_MAX_WIDTH, Math.max(KEYWORD_PANEL_MIN_WIDTH, startWidth + dx));
    keywordPanel.style.flex = `0 0 ${newWidth}px`;
  };

  const onPointerUp = (): void => {
    keywordPanelResizeHandle.classList.remove('is-dragging');
    keywordPanelResizeHandle.removeEventListener('pointermove', onPointerMove);
    keywordPanelResizeHandle.removeEventListener('pointerup', onPointerUp);
    keywordPanelResizeHandle.removeEventListener('pointercancel', onPointerUp);
  };

  keywordPanelResizeHandle.addEventListener('pointermove', onPointerMove);
  keywordPanelResizeHandle.addEventListener('pointerup', onPointerUp);
  keywordPanelResizeHandle.addEventListener('pointercancel', onPointerUp);
});

// --- Εμφάνιση/απόκρυψη πλαισίων Διάγραμμα ροής / Μεταβλητές -------------------

/**
 * Κουμπιά ΠΑΝΩ στις λαβές αλλαγής μεγέθους (βλ. `.panel-collapse-btn` στο
 * style.css) — ο χρήστης ζήτησε το κουμπί να είναι στην "άκρη" του πλαισίου
 * (όχι στη γραμμή εργαλείων) και το ΙΔΙΟ κουμπί να ανοίγει/κλείνει (toggle),
 * όχι δύο ξεχωριστά στοιχεία. Το βελάκι αλλάζει κατεύθυνση ανάλογα με την
 * κατάσταση. Θυμάται τι επέλεξε ρητά ο χρήστης (`flowchartPanelUserHidden`/
 * `variablesPanelUserHidden`) ώστε το άνοιγμα/κλείσιμο του πλαισίου
 * Μετατροπή (βλ. `openConversionPanel`/`closeConversionPanel`) να ΜΗΝ
 * ξαναφέρνει σιωπηλά ένα πλαίσιο που ο χρήστης είχε ήδη κλείσει ο ίδιος.
 */
let flowchartPanelUserHidden = false;
let variablesPanelUserHidden = false;

function setFlowchartPanelVisible(visible: boolean): void {
  flowchartPanelUserHidden = !visible;
  variablesPanel.hidden = !visible;
  flowchartPanelToggleBtn.setAttribute('aria-pressed', String(visible));
  flowchartPanelToggleBtn.textContent = visible ? '›' : '‹';
}

function setVariablesPanelVisible(visible: boolean): void {
  variablesPanelUserHidden = !visible;
  flowchartPaneHalf.hidden = !visible;
  if (!visible) {
    // Καθαρίζει τυχόν ρητό πλάτος από σύρσιμο (βλ. outputSplitResizeHandle
    // παραπάνω) — αλλιώς το `consoleOutputHalf` θα έμενε "κολλημένο" στο
    // παλιό (πιθανόν στενό) πλάτος αντί να γεμίσει όλη τη γραμμή μόλις
    // κρυφτεί το απέναντι πλαίσιο (bug report: "χάνεται το πλαίσιο και δεν
    // καλύπτεται με το άλλο" — ίδιο ζήτημα με το setOutputPaneVisible).
    consoleOutputHalf.style.flex = '';
  }
  variablesPanelToggleBtn.setAttribute('aria-pressed', String(visible));
  variablesPanelToggleBtn.textContent = visible ? '›' : '‹';
}

/**
 * Ίδιο κουμπί-toggle πάνω στη λαβή αλλαγής μεγέθους, αυτή τη φορά για το
 * πλαίσιο Λέξεις-κλειδιά — που είναι ΑΡΙΣΤΕΡΑ της λαβής του (αντίθετα από τα
 * δύο παραπάνω, που είναι δεξιά), γι' αυτό το βελάκι είναι «κατοπτρικό»: '‹'
 * όσο είναι ορατό (δείχνει προς το πλαίσιο, σαν να λέει "κλείσε το προς τα
 * εκεί"), '›' όσο είναι κρυμμένο (δείχνει "άνοιξέ το ξανά").
 */
function setKeywordPanelVisible(visible: boolean): void {
  keywordPanel.hidden = !visible;
  keywordPanelToggleBtn.setAttribute('aria-pressed', String(visible));
  keywordPanelToggleBtn.textContent = visible ? '‹' : '›';
}

/**
 * Ίδιο κουμπί-toggle, αυτή τη φορά πάνω στην ΟΡΙΖΟΝΤΙΑ λαβή ανάμεσα σε
 * Κώδικας/Οθόνη εκτέλεσης — κάθετο βελάκι (⌄/⌃) αντί για οριζόντιο (‹/›),
 * ίδια λογική "δείχνει προς το πλαίσιο για να κλείσει": ⌄ (προς τα κάτω,
 * προς την Οθόνη εκτέλεσης που είναι από κάτω) όσο είναι ορατό, ⌃ όσο είναι
 * κρυμμένο.
 */
function setOutputPaneVisible(visible: boolean): void {
  outputPane.hidden = !visible;
  if (!visible) {
    // Καθαρίζει τυχόν ρητό ύψος από σύρσιμο (βλ. editorOutputResizeHandle
    // παρακάτω) — αλλιώς το Κώδικας θα έμενε "κολλημένο" στο παλιό ύψος
    // αντί να γεμίσει όλο τον ελεύθερο χώρο μόλις κρυφτεί η Οθόνη εκτέλεσης.
    editorPane.style.flex = '';
  }
  outputPaneToggleBtn.setAttribute('aria-pressed', String(visible));
  outputPaneToggleBtn.textContent = visible ? '⌄' : '⌃';
}

// `mousedown` σταματάει να διαδοθεί στη λαβή-γονέα (που θα ξεκινούσε
// σύρσιμο αλλαγής μεγέθους) — το ίδιο το κουμπί χειρίζεται μόνο του το κλικ.
flowchartPanelToggleBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
flowchartPanelToggleBtn.addEventListener('click', () => setFlowchartPanelVisible(variablesPanel.hidden));
variablesPanelToggleBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
variablesPanelToggleBtn.addEventListener('click', () => setVariablesPanelVisible(flowchartPaneHalf.hidden));
keywordPanelToggleBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
keywordPanelToggleBtn.addEventListener('click', () => setKeywordPanelVisible(keywordPanel.hidden));
outputPaneToggleBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
outputPaneToggleBtn.addEventListener('click', () => setOutputPaneVisible(outputPane.hidden));

// --- Μέγεθος γραμματοσειράς κώδικα ---------------------------------------------

const FONT_SIZE_STORAGE_KEY = 'glossa-editor-font-size';
const FONT_SIZE_MIN = 10;
const FONT_SIZE_MAX = 28;
const FONT_SIZE_STEP = 2;
const FONT_SIZE_DEFAULT = 14;

function loadFontSize(): number {
  const saved = Number(localStorage.getItem(FONT_SIZE_STORAGE_KEY));
  return Number.isInteger(saved) && saved >= FONT_SIZE_MIN && saved <= FONT_SIZE_MAX ? saved : FONT_SIZE_DEFAULT;
}

// --- Αυτόματη αποθήκευση κώδικα (μη χαμένος κώδικας σε refresh) ---------------

/**
 * ΣΕ ΑΝΤΙΘΕΣΗ με τη διάταξη πλαισίων (βλ. σχόλιο `slotContent` παρακάτω, ΕΚΕΙ
 * σκόπιμα ΧΩΡΙΣ localStorage) — εδώ ο μαθητής θέλει ΡΗΤΑ να ΜΗΝ χάνεται ό,τι
 * έχει γράψει σε ένα τυχαίο refresh/κλείσιμο tab: "μετά από refresh θέλω ο
 * κώδικας να μην χάνεται". Αφορά ΜΟΝΟ τον κύριο editor (μαζί με διάλεκτο και
 * "Τμήμα αλγορίθμου" — χρειάζονται και τα δύο για να παρθεί σωστά ο
 * αποθηκευμένος κώδικας κατά την επαναφορά, βλ. `saveCodeAutosave`) — ΟΧΙ τη
 * Φυσική Γλώσσα Κατά Βήματα ούτε τον Μετατροπέα, εκεί δεν αναφέρθηκε πρόβλημα.
 */
const CODE_AUTOSAVE_STORAGE_KEY = 'glossa-code-autosave';

interface CodeAutosavePayload {
  code: string;
  dialect: 'glossa' | 'pseudoglossa';
  segment: boolean;
}

function loadCodeAutosave(): CodeAutosavePayload | null {
  const raw = localStorage.getItem(CODE_AUTOSAVE_STORAGE_KEY);
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CodeAutosavePayload>;
    if (typeof parsed.code !== 'string' || parsed.code.trim() === '') return null;
    return {
      code: parsed.code,
      dialect: parsed.dialect === 'glossa' ? 'glossa' : 'pseudoglossa',
      segment: parsed.segment === true,
    };
  } catch {
    return null; // παλιά/κατεστραμμένη τιμή — αγνόησέ την, μη σπάσεις την εκκίνηση
  }
}

/**
 * Αν το `boot()` παρακάτω αποτύχει (π.χ. offline, ή ένα μπαγιάτικο cached
 * index.html δείχνει σε hashed αρχεία που δεν υπάρχουν πια μετά από νέο
 * deploy) — δείχνουμε ένα ρητό μήνυμα με κουμπί "Δοκιμάστε ξανά" ΜΕΣΑ στο
 * ίδιο σκελετό-placeholder (βλ. index.html #editor-loading-skeleton) αντί
 * να μείνει ο editor σιωπηλά άδειος. Καθαρά fast-zone, καμία εξάρτηση από
 * τον Monaco.
 */
function showBootErrorState(err: unknown): void {
  document.documentElement.classList.remove('app-booting');
  const skeleton = document.getElementById('editor-loading-skeleton');
  if (skeleton === null) return;
  skeleton.textContent = '';
  skeleton.append('Η φόρτωση του επεξεργαστή απέτυχε. ');
  const retryBtn = document.createElement('button');
  retryBtn.type = 'button';
  retryBtn.textContent = 'Δοκιμάστε ξανά';
  retryBtn.addEventListener('click', () => window.location.reload());
  skeleton.append(retryBtn);
}

/**
 * Φόρτωση σε ΔΥΟ φάσεις: η "γρήγορη ζώνη" παραπάνω (DOM lookups, θέμα,
 * toast/βοηθητικά, μερικά toggle-κουμπιά) τρέχει αμέσως, στατικά — δεν
 * χρειάζεται τίποτα από εδώ. Το `boot()` φορτώνει ΔΥΝΑΜΙΚΑ (`import()`) τον
 * Monaco editor ΚΑΙ όλη την υπόλοιπη λογική της εφαρμογής (γλώσσα/UI/
 * διάγραμμα ροής/Μετατροπέας/φυσική γλώσσα κ.λπ. — ό,τι ήταν από εδώ και
 * κάτω) — έτσι η αρχική σελίδα ζωγραφίζεται αμέσως, χωρίς να περιμένει να
 * κατέβει/εκτελεστεί το ~3MB bundle του Monaco πριν φανεί οτιδήποτε (βλ.
 * plan transient-zooming-sky — PageSpeed mobile score 59/100 → lazy-load
 * fix). Το `void 0` τύπου annotation του `monaco` (destructured εδώ) είναι
 * ολόκληρο το ίδιο API surface που πριν εισαγόταν στατικά — καμία αλλαγή
 * τύπων/λειτουργίας παρακάτω, μόνο ΠΟΤΕ φορτώνεται.
 */
async function boot(): Promise<void> {
  // Το 'monaco-editor/editor/editor.api.js' είναι μόνο το API surface — καμία
  // λειτουργία (suggest/find/hover κ.λπ.) δεν είναι «καλωδιωμένη» από μόνη
  // της. Το πλήρες 'editor.main.js' θα έφερνε πίσω και τα ~90 έτοιμα language
  // bundles που αποφύγαμε σκόπιμα — γι' αυτό εισάγουμε μόνο τα συγκεκριμένα
  // contributions που χρειαζόμαστε: το suggestController για την αυτόματη
  // συμπλήρωση, και το FormatOnType (μέσα στο formatActions.js) για τη
  // διόρθωση λέξης-κλειδιού με space/Enter — το registerOnTypeFormattingEditProvider
  // από μόνο του δεν αρκεί, χρειάζεται αυτό το contribution να «ακούει» τα
  // πληκτρολογήματα και να το καλεί.
  const [monaco] = await Promise.all([
    import('monaco-editor/editor/editor.api.js'),
    import('./monaco-worker-setup.js'),
    import('monaco-editor/editor/contrib/suggest/browser/suggestController.js'),
    import('monaco-editor/editor/contrib/format/browser/formatActions.js'),
  ]);
  const {
    registerGlossaLanguage,
    registerGlossaNaturalLanguage,
    registerNaturalLanguageStepAutoIncrement,
    registerAutoIndent,
    reindentGlossaCode,
    normalizeOperatorSpacing,
    createGlossaKeywordCorrector,
    updateGlossaLanguageDialect,
    GLOSSA_LANGUAGE_ID,
    GLOSSA_NATURAL_LANGUAGE_ID,
    GLOSSA_THEME_ID,
    GLOSSA_THEME_DARK_ID,
  } = await import('./glossa-language.js');
  const { GLOSSA_PALETTE, PSEUDOGLOSSA_PALETTE, NATURAL_LANGUAGE_PALETTE_GROUP } = await import('./command-palette.js');
  const { renderFlowchart, renderFlowchartFragment, clearFlowchart, setActiveFlowchartLine, setFlowchartZoom, downloadFlowchartAsPng } = await import('./flowchart.js');

  // --- Monaco editor -----------------------------------------------------------

  /**
   * Η "Φυσική γλώσσα κατά βήματα" είναι μια 3η επιλογή στο ΙΔΙΟ dropdown, αλλά
   * ΔΕΝ είναι πραγματική Dialect — δεν έχει αυστηρή γραμματική, δεν μπορεί να
   * γίνει parse με τον ίδιο ντετερμινιστικό τρόπο. Επιλέγοντάς την ανοίγει το
   * split (βλ. enterNaturalSplit) στο πλαίσιο "Κώδικας" αντί να αλλάζει τι
   * αναγνωρίζει ο lexer/parser. Το `codeDialect` θυμάται τη ΤΕΛΕΥΤΑΙΑ
   * πραγματική επιλογή ΓΛΩΣΣΑ/Ψευδογλώσσα — η αριστερή μεριά του split
   * συνεχίζει να λειτουργεί σε αυτήν ανεξάρτητα από το αν είναι ενεργό το split.
   * ΠΡΕΠΕΙ να δηλωθεί ΠΡΙΝ το registerGlossaLanguage/describeDialect παρακάτω
   * (που καλείται αμέσως, όχι απλά αναφέρεται) — αλλιώς `let` temporal dead zone.
   */
  let codeDialect: Dialect = Dialect.PSEUDOGLOSSA;

  registerGlossaLanguage(describeDialect);
  registerGlossaNaturalLanguage();
  const correctGlossaKeyword = createGlossaKeywordCorrector(describeDialect);

  // Καθαρίζουμε ρητά τον σκελετό-placeholder (βλ. index.html
  // #editor-loading-skeleton) πριν ο Monaco αναλάβει το ίδιο container — δεν
  // βασιζόμαστε σε τυχόν σιωπηλή συμπεριφορά του Monaco να το αφαιρέσει μόνος του.
  editorContainer.replaceChildren();
  const editor = monaco.editor.create(editorContainer, {
    value: '',
    language: GLOSSA_LANGUAGE_ID,
    theme: currentTheme() === 'dark' ? GLOSSA_THEME_DARK_ID : GLOSSA_THEME_ID,
    automaticLayout: true,
    minimap: { enabled: false },
    fontSize: loadFontSize(),
    fontFamily: "'SF Mono', 'Cascadia Code', 'Consolas', 'Courier New', monospace",
    quickSuggestions: { other: true, comments: false, strings: false },
    // Το Monaco έχει ΔΙΚΟ ΤΟΥ, ξεχωριστό μηχανισμό προτάσεων που σαρώνει το
    // ήδη γραμμένο κείμενο του editor και προτείνει λέξεις που βρίσκει εκεί
    // (π.χ. αν έχεις ήδη γράψει "ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ" νωρίτερα, θα την προτείνει
    // ΞΑΝΑ σε κάθε "ΤΕΛΟ" που πληκτρολογείς, ΑΚΟΜΑ ΚΙ ΑΝ εννοείς
    // "ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ"). Αυτό «σκεπάζει» τη δική μας πλήρη, σωστά
    // ταξινομημένη λίστα (βλ. `registerCompletionItemProvider` — ήδη ξέρει
    // ΟΛΕΣ τις πραγματικές λέξεις-κλειδιά, βλ. `sortTierFor`) κατά την
    // αυτόματη εμφάνιση προτάσεων ενώ πληκτρολογείς (όχι με ρητό Ctrl+Space,
    // εκεί συγχωνεύονται σωστά και τα δύο) — την απενεργοποιούμε εντελώς.
    wordBasedSuggestions: 'off',
    formatOnType: true,
    glyphMargin: true,
    lineNumbersMinChars: 2,
  });
  registerAutoIndent(editor, correctGlossaKeyword);

  // --- Breakpoints (τελίτσες στο περιθώριο, αριστερά από τον αριθμό γραμμής) ---

  /**
   * Πηγή αλήθειας για ΠΟΙΕΣ γραμμές έχουν breakpoint είναι οι ίδιες οι
   * decorations του Monaco — ΟΧΙ ένα ξεχωριστό `Set<number>` με "παγωμένους"
   * αριθμούς γραμμής. Ο λόγος: το `IEditorDecorationsCollection` ΞΑΝΑ-
   * ΤΟΠΟΘΕΤΕΙ μόνο του τα ranges του καθώς ο μαθητής προσθέτει/αφαιρεί
   * γραμμές πάνω από ένα breakpoint (`.getRanges()` επιστρέφει πάντα τις
   * ΤΡΕΧΟΥΣΕΣ θέσεις) — ένα απλό Set αριθμών θα "έμενε πίσω" και θα έδειχνε
   * breakpoint σε λάθος γραμμή μετά την πρώτη επεξεργασία κώδικα.
   */
  const breakpointDecorations = editor.createDecorationsCollection([]);

  function breakpointDecorationOptions(): monaco.editor.IModelDecorationOptions {
    return {
      glyphMarginClassName: 'breakpoint-glyph',
      glyphMarginHoverMessage: { value: 'Breakpoint — κλικ ξανά για αφαίρεση' },
      stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
    };
  }

  function currentBreakpointLines(): ReadonlySet<number> {
    return new Set(breakpointDecorations.getRanges().map((r) => r.startLineNumber));
  }

  function toggleBreakpointAtLine(line: number): void {
    const ranges = breakpointDecorations.getRanges();
    const withoutLine = ranges.filter((r) => r.startLineNumber !== line);
    const nextRanges = withoutLine.length === ranges.length ? [...ranges, new monaco.Range(line, 1, line, 1)] : withoutLine;
    breakpointDecorations.set(nextRanges.map((range) => ({ range, options: breakpointDecorationOptions() })));
  }

  /**
   * Ξαναχτίζει τις decorations των breakpoints ΑΠΟ ΤΗΝ ΑΡΧΗ, στις δοσμένες
   * γραμμές — χρειάζεται μετά από ένα edit που αντικαθιστά ΟΛΟΚΛΗΡΟ το
   * περιεχόμενο του editor μονομιάς (βλ. "reindent-on-run" στο `runProgram`):
   * το Monaco ΔΕΝ ξανατοποθετεί αξιόπιστα zero-width decorations μέσα από ένα
   * τόσο μεγάλο, ενιαίο replace-όλου-του-buffer edit (παρατηρήθηκε να τις
   * μετακινεί σε λάθος γραμμή) — αφού όμως η αναδιάταξη εσοχών ΠΟΤΕ δεν αλλάζει
   * ΠΟΙΑ γραμμή έχει ποια εντολή (μόνο το αρχικό whitespace), οι ΙΔΙΟΙ αριθμοί
   * γραμμής παραμένουν σωστοί — απλά τις ξαναφτιάχνουμε εκεί.
   */
  function restoreBreakpointsAtLines(lines: readonly number[]): void {
    breakpointDecorations.set(lines.map((line) => ({ range: new monaco.Range(line, 1, line, 1), options: breakpointDecorationOptions() })));
  }

  function clearAllBreakpoints(): void {
    breakpointDecorations.clear();
  }

  editor.onMouseDown((e) => {
    if (e.target.type !== monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN) return;
    const line = e.target.position?.lineNumber;
    if (line === undefined) return;
    toggleBreakpointAtLine(line);
  });

  /**
   * Δύο ακόμα instances Monaco, ίδια γλώσσα/θέμα με τον κύριο editor, για το
   * πλαίσιο "Μετατροπή δομών" — έτσι ο κώδικας εκεί φαίνεται ΑΚΡΙΒΩΣ όπως στο
   * κύριο πλαίσιο "Κώδικας" (χρωματισμός λέξεων-κλειδιών κ.λπ.), όχι σαν απλό
   * πεδίο κειμένου. Το `monaco.editor.setTheme` παρακάτω είναι καθολικό (όχι
   * ανά instance), οπότε η εναλλαγή θέματος τα ενημερώνει όλα μαζί αυτόματα.
   */
  // Οι 2 editors του Μετατροπέα (`ensureConversionEditors`, πιο κάτω) —
  // ΔΕΝ δημιουργούνται εδώ πια, μόνο δηλώνονται. Δημιουργούνται την ΠΡΩΤΗ
  // φορά που ανοίγει πραγματικά το πλαίσιο (βλ. `openConversionPanel`) —
  // μειώνει τη δουλειά που κάνει ο Monaco αμέσως μόλις φορτώσει (λιγότερες
  // "μεγάλες εργασίες" στο κύριο νήμα, βλ. plan transient-zooming-sky,
  // PageSpeed diagnostic "Αποφύγετε μεγάλες εργασίες στο κύριο νήμα") — οι
  // περισσότεροι επισκέπτες δεν ανοίγουν ποτέ τον Μετατροπέα.
  let conversionInputEditor: monaco.editor.IStandaloneCodeEditor | undefined;
  let conversionOutputEditor: monaco.editor.IStandaloneCodeEditor | undefined;

  /** Δημιουργεί τους 2 editors του Μετατροπέα, μόνο την πρώτη φορά (idempotent) — βλ. σχόλιο στη δήλωσή τους παραπάνω. Καλείται από `openConversionPanel`. */
  function ensureConversionEditors(): void {
    if (conversionInputEditor !== undefined) return;

    conversionInputEditor = monaco.editor.create(conversionInputContainer, {
      value: '',
      language: GLOSSA_LANGUAGE_ID,
      theme: currentTheme() === 'dark' ? GLOSSA_THEME_DARK_ID : GLOSSA_THEME_ID,
      automaticLayout: true,
      minimap: { enabled: false },
      fontSize: loadFontSize(),
      fontFamily: "'SF Mono', 'Cascadia Code', 'Consolas', 'Courier New', monospace",
      quickSuggestions: { other: true, comments: false, strings: false },
      // Το Monaco έχει ΔΙΚΟ ΤΟΥ, ξεχωριστό μηχανισμό προτάσεων που σαρώνει το
      // ήδη γραμμένο κείμενο του editor και προτείνει λέξεις που βρίσκει εκεί
      // (π.χ. αν έχεις ήδη γράψει "ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ" νωρίτερα, θα την προτείνει
      // ΞΑΝΑ σε κάθε "ΤΕΛΟ" που πληκτρολογείς, ΑΚΟΜΑ ΚΙ ΑΝ εννοείς
      // "ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ"). Αυτό «σκεπάζει» τη δική μας πλήρη, σωστά
      // ταξινομημένη λίστα (βλ. `registerCompletionItemProvider` — ήδη ξέρει
      // ΟΛΕΣ τις πραγματικές λέξεις-κλειδιά, βλ. `sortTierFor`) κατά την
      // αυτόματη εμφάνιση προτάσεων ενώ πληκτρολογείς (όχι με ρητό Ctrl+Space,
      // εκεί συγχωνεύονται σωστά και τα δύο) — την απενεργοποιούμε εντελώς.
      wordBasedSuggestions: 'off',
      formatOnType: true,
      lineNumbersMinChars: 2,
    });
    registerAutoIndent(conversionInputEditor, correctGlossaKeyword);

    conversionOutputEditor = monaco.editor.create(conversionOutputContainer, {
      value: '',
      language: GLOSSA_LANGUAGE_ID,
      theme: currentTheme() === 'dark' ? GLOSSA_THEME_DARK_ID : GLOSSA_THEME_ID,
      automaticLayout: true,
      minimap: { enabled: false },
      fontSize: loadFontSize(),
      fontFamily: "'SF Mono', 'Cascadia Code', 'Consolas', 'Courier New', monospace",
      readOnly: true,
      lineNumbersMinChars: 2,
    });

    conversionInputEditor.onDidChangeModelContent(() => {
      conversionInputPlaceholder.classList.toggle('is-hidden', conversionInputEditor!.getValue().length > 0);
      // Ίδια αυτόματη ανίχνευση διαλέκτου όπως στον κύριο editor (βλ.
      // `autoDetectDialectFromText`) — ΠΡΙΝ το `updateConversionOptions`
      // παρακάτω, ώστε αυτό να αναλύσει το κείμενο ήδη με τη ΣΩΣΤΗ διάλεκτο.
      autoDetectDialectFromConversionEditorStart();
      if (conversionInputDebounceTimer !== undefined) clearTimeout(conversionInputDebounceTimer);
      conversionInputDebounceTimer = setTimeout(updateConversionOptions, CONVERSION_INPUT_DEBOUNCE_MS);
    });
  }

  /**
   * Αριστερή μεριά του split στο πλαίσιο "Κώδικας" (βλ. index.html
   * #editor-split) — ενεργή μόνο όταν επιλέγεται "Φυσική γλώσσα κατά βήματα"
   * στο dropdown. Πλήρως επεξεργάσιμη (όχι readOnly) και προς τις δύο
   * κατευθύνσεις: τα κουμπιά Μετάτρεψε →/← Μετάτρεψε κάνουν τη μετατροπή ρητά,
   * με ένα κλικ — δεν υπάρχει live/αυτόματος συγχρονισμός (θα ήταν αδύνατο να
   * μείνει σταθερός καθώς ο χρήστης γράφει μισοτελειωμένες γραμμές). Δικό της
   * Monaco language id (όχι GLOSSA_LANGUAGE_ID) ώστε το λεξιλόγιο-μόνο-της-
   * φυσικής-γλώσσας (Βήμα/Θέσε/Πήγαινε/τότε/αλλιώς) να χρωματίζεται σαν
   * λέξη-κλειδί ΕΔΩ χωρίς να επηρεάζει τον κύριο editor κώδικα.
   */
  // Ίδιο σκεπτικό με `conversionInputEditor`/`conversionOutputEditor`
  // παραπάνω (lazy, βλ. σχόλιο εκεί) — δημιουργείται μόνο την πρώτη φορά
  // που ανοίγει το split "Φυσική γλώσσα κατά βήματα" (βλ. `enterNaturalSplit`).
  let naturalEditor: monaco.editor.IStandaloneCodeEditor | undefined;

  /** Δημιουργεί το `naturalEditor`, μόνο την πρώτη φορά (idempotent) — βλ. σχόλιο στη δήλωσή του παραπάνω. Καλείται από `enterNaturalSplit`. */
  function ensureNaturalEditor(): void {
    if (naturalEditor !== undefined) return;

    /**
     * Αριστερή μεριά του split στο πλαίσιο "Κώδικας" (βλ. index.html
     * #editor-split) — ενεργή μόνο όταν επιλέγεται "Φυσική γλώσσα κατά βήματα"
     * στο dropdown. Πλήρως επεξεργάσιμη (όχι readOnly) και προς τις δύο
     * κατευθύνσεις: τα κουμπιά Μετάτρεψε →/← Μετάτρεψε κάνουν τη μετατροπή ρητά,
     * με ένα κλικ — δεν υπάρχει live/αυτόματος συγχρονισμός (θα ήταν αδύνατο να
     * μείνει σταθερός καθώς ο χρήστης γράφει μισοτελειωμένες γραμμές). Δικό της
     * Monaco language id (όχι GLOSSA_LANGUAGE_ID) ώστε το λεξιλόγιο-μόνο-της-
     * φυσικής-γλώσσας (Βήμα/Θέσε/Πήγαινε/τότε/αλλιώς) να χρωματίζεται σαν
     * λέξη-κλειδί ΕΔΩ χωρίς να επηρεάζει τον κύριο editor κώδικα.
     */
    naturalEditor = monaco.editor.create(naturalEditorContainer, {
      value: '',
      language: GLOSSA_NATURAL_LANGUAGE_ID,
      theme: currentTheme() === 'dark' ? GLOSSA_THEME_DARK_ID : GLOSSA_THEME_ID,
      automaticLayout: true,
      minimap: { enabled: false },
      fontSize: loadFontSize(),
      fontFamily: "'SF Mono', 'Cascadia Code', 'Consolas', 'Courier New', monospace",
      quickSuggestions: { other: true, comments: false, strings: false },
      // Το Monaco έχει ΔΙΚΟ ΤΟΥ, ξεχωριστό μηχανισμό προτάσεων που σαρώνει το
      // ήδη γραμμένο κείμενο του editor και προτείνει λέξεις που βρίσκει εκεί
      // (π.χ. αν έχεις ήδη γράψει "ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ" νωρίτερα, θα την προτείνει
      // ΞΑΝΑ σε κάθε "ΤΕΛΟ" που πληκτρολογείς, ΑΚΟΜΑ ΚΙ ΑΝ εννοείς
      // "ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ"). Αυτό «σκεπάζει» τη δική μας πλήρη, σωστά
      // ταξινομημένη λίστα (βλ. `registerCompletionItemProvider` — ήδη ξέρει
      // ΟΛΕΣ τις πραγματικές λέξεις-κλειδιά, βλ. `sortTierFor`) κατά την
      // αυτόματη εμφάνιση προτάσεων ενώ πληκτρολογείς (όχι με ρητό Ctrl+Space,
      // εκεί συγχωνεύονται σωστά και τα δύο) — την απενεργοποιούμε εντελώς.
      wordBasedSuggestions: 'off',
      formatOnType: true,
      lineNumbersMinChars: 2,
    });
    registerNaturalLanguageStepAutoIncrement(naturalEditor);
    naturalEditor.onDidFocusEditorText(() => {
      lastFocusedEditor = naturalEditor!;
      dismissEditorIntroLine();
    });
  }

  /**
   * Εμφανίζεται ΣΤΗ ΘΕΣΗ του διαγράμματος ροής όταν ο κώδικας (ΓΛΩΣΣΑ) έχει
   * ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ — δείχνει το ΙΔΙΟ πρόγραμμα με κάθε υποπρόγραμμα
   * μετατρεμμένο στο αντίθετο είδος του (βλ. `convertAllSubprograms`,
   * `updateFlowchart`). Μόνο για ανάγνωση — ο μαθητής δεν το επεξεργάζεται.
   */
  const subprogramConversionEditor = monaco.editor.create(subprogramConversionContainer, {
    value: '',
    language: GLOSSA_LANGUAGE_ID,
    theme: currentTheme() === 'dark' ? GLOSSA_THEME_DARK_ID : GLOSSA_THEME_ID,
    automaticLayout: true,
    minimap: { enabled: false },
    fontSize: loadFontSize(),
    fontFamily: "'SF Mono', 'Cascadia Code', 'Consolas', 'Courier New', monospace",
    readOnly: true,
    lineNumbersMinChars: 2,
  });

  onThemeChange = (theme) => {
    monaco.editor.setTheme(theme === 'dark' ? GLOSSA_THEME_DARK_ID : GLOSSA_THEME_ID);
  };
  onThemeChange(currentTheme());

  function applyFontSize(size: number): void {
    const clamped = Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, size));
    editor.updateOptions({ fontSize: clamped });
    // Οι 4 lazy editors (Μετατροπέας ×2 + Φυσική γλώσσα) μπορεί να μην έχουν
    // δημιουργηθεί ακόμα (βλ. `ensureConversionEditors`/`enterNaturalSplit`)
    // — απλά δεν έχουν τίποτα να ενημερώσουν προς το παρόν, θα πάρουν το
    // ΤΡΕΧΟΝ (ήδη αποθηκευμένο, βλ. `loadFontSize`) μέγεθος μόλις φτιαχτούν.
    conversionInputEditor?.updateOptions({ fontSize: clamped });
    conversionOutputEditor?.updateOptions({ fontSize: clamped });
    naturalEditor?.updateOptions({ fontSize: clamped });
    subprogramConversionEditor.updateOptions({ fontSize: clamped });
    // Ίδιο μέγεθος και για τις λέξεις-κλειδιά και την οθόνη εκτέλεσης, μέσω
    // κοινού CSS custom property — βλ. --code-font-size στο style.css.
    document.documentElement.style.setProperty('--code-font-size', `${clamped}px`);
    localStorage.setItem(FONT_SIZE_STORAGE_KEY, String(clamped));
    fontDecreaseBtn.disabled = clamped <= FONT_SIZE_MIN;
    fontIncreaseBtn.disabled = clamped >= FONT_SIZE_MAX;
  }

  fontDecreaseBtn.addEventListener('click', () => {
    applyFontSize(editor.getOption(monaco.editor.EditorOption.fontSize) - FONT_SIZE_STEP);
  });

  fontIncreaseBtn.addEventListener('click', () => {
    applyFontSize(editor.getOption(monaco.editor.EditorOption.fontSize) + FONT_SIZE_STEP);
  });

  applyFontSize(loadFontSize());

  editor.onDidChangeCursorPosition((e) => {
    cursorPositionEl.textContent = `Γραμμή ${e.position.lineNumber}, Στήλη ${e.position.column}`;
  });

  function isNaturalLanguageSelected(): boolean {
    return dialectSelect.value === 'natural';
  }

  /**
   * Η μετατροπή σε/από φυσική γλώσσα δουλεύει ΜΟΝΟ με Ψευδογλώσσα (βλ.
   * natural-language.ts) — άρα όσο το split είναι ανοιχτό, η αριστερή μεριά
   * ΠΑΝΤΑ δουλεύει σε Ψευδογλώσσα (γι' αυτό η ετικέτα εκεί είναι σταθερό
   * κείμενο "Ψευδογλώσσα", όχι επιλογέας — δεν έχει νόημα να διαλέξεις ΓΛΩΣΣΑ
   * εδώ αφού τα κουμπιά Μετάτρεψε θα αρνούνταν αμέσως).
   */
  function enterNaturalSplit(): void {
    ensureNaturalEditor();
    naturalEditorBox.hidden = false;
    editorSplitControls.hidden = false;
    codeEditorLabel.hidden = false;
    codeDialect = Dialect.PSEUDOGLOSSA;
    updateGlossaLanguageDialect(describeDialect());
    naturalEditor!.layout();
    // Βλ. `.editor-pane.is-natural-split` στο style.css (μόνο σε κινητό) — με
    // ΔΥΟ κουτιά στοιβαγμένα (Φυσική γλώσσα/Ψευδογλώσσα) αντί για ένα, το
    // πλαίσιο "Κώδικας" χρειάζεται μεγαλύτερο μερίδιο ύψους ΚΑΙ τα ίδια τα
    // κουτιά χρειάζονται μοιρασμένο (όχι σταθερό) ύψος — αλλιώς το δεύτερο
    // κουτί καταλήγει εκτός οθόνης, ΚΑΙ αδύνατο να το φτάσεις με scroll (το
    // Monaco «καταπίνει» τις χειρονομίες scroll/swipe αντί να τις περνάει στον
    // γονικό scrollable container — bug report: "δεν φαίνεται το πλαίσιο της
    // ψευδογλώσσας").
    editorPane.classList.add('is-natural-split');
  }

  function exitNaturalSplit(): void {
    naturalEditorBox.hidden = true;
    editorSplitControls.hidden = true;
    codeEditorLabel.hidden = true;
    currentNaturalLineMap = null;
    clearNaturalLineHighlight();
    editorPane.classList.remove('is-natural-split');
    editor.layout();
  }

  function applyCodeDialect(value: 'glossa' | 'pseudoglossa'): void {
    codeDialect = value === 'glossa' ? Dialect.GLOSSA : Dialect.PSEUDOGLOSSA;
    dialectSelect.value = value;
    renderKeywordPalette();
    renderMobileQuickStrips();
    updateGlossaLanguageDialect(describeDialect());
    scheduleFlowchartUpdate();
  }

  /**
   * Αυτόματη εναλλαγή διαλέκτου μόλις η ΑΡΧΗ ενός κειμένου αρχίσει να ταιριάζει
   * με "ΠΡΟ" (πάει να γράψει ΠΡΟΓΡΑΜΜΑ -> ΓΛΩΣΣΑ) ή με "ΑΛΓ" (πάει να γράψει
   * Αλγόριθμος -> Ψευδογλώσσα) — είτε ο μαθητής τα πληκτρολογεί γράμμα-γράμμα
   * είτε κάνει copy-paste έτοιμο κώδικα, γλιτώνει το χειροκίνητο swap του
   * dialect-select ΠΡΙΝ γράψει το πρόγραμμα. 3 γράμματα (όχι μόνο το πρώτο)
   * ώστε να ΜΗΝ μπερδεύεται με άλλες λέξεις-κλειδιά που ξεκινούν με το ίδιο
   * πρώτο γράμμα (π.χ. ΑΡΧΗ/ΑΝ/ΑΚΕΡΑΙΕΣ ξεκινούν κι αυτές με Α, αλλά όχι με
   * ΑΛΓ). Δεν πειράζει τίποτα άλλο από τη διάλεκτο — ΔΕΝ ξαναγράφει το
   * κείμενο. Κοινή λογική για ΚΑΘΕ editor που γράφει κώδικα — βλ.
   * `autoDetectDialectFromMainEditorStart` (κύριος editor) και
   * `autoDetectDialectFromConversionEditorStart` (πλαίσιο εισαγωγής του
   * Μετατροπέα) — και οι δύο μοιράζονται το ΙΔΙΟ `dialect-select`/`codeDialect`
   * (βλ. σχόλιο `describeDialect`), οπότε η ίδια εναλλαγή έχει νόημα και στους
   * δύο.
   */
  function autoDetectDialectFromText(text: string): void {
    const normalizedStart = normalizeKeywordText(text).trimStart();
    if (normalizedStart.startsWith('ΠΡΟ')) {
      if (codeDialect !== Dialect.GLOSSA) applyCodeDialect('glossa');
    } else if (normalizedStart.startsWith('ΑΛΓ')) {
      if (codeDialect !== Dialect.PSEUDOGLOSSA) applyCodeDialect('pseudoglossa');
    }
  }

  /**
   * Δεν εφαρμόζεται όσο είναι ανοιχτό το split φυσικής γλώσσας, όπου η
   * διάλεκτος είναι σταθερά κλειδωμένη σε Ψευδογλώσσα (βλ. `enterNaturalSplit`)
   * — δεν υπάρχει καν επιλογέας διαλέκτου εκεί για να αλλάξει.
   */
  function autoDetectDialectFromMainEditorStart(): void {
    if (!naturalEditorBox.hidden) return;
    autoDetectDialectFromText(editor.getValue());
  }

  dialectSelect.addEventListener('change', () => {
    if (isNaturalLanguageSelected()) {
      renderKeywordPalette();
      renderMobileQuickStrips();
      enterNaturalSplit();
      return;
    }
    if (!naturalEditorBox.hidden) exitNaturalSplit();
    applyCodeDialect(dialectSelect.value === 'glossa' ? 'glossa' : 'pseudoglossa');
    saveCodeAutosave();
  });

  // --- Αποθήκευση / Άνοιγμα κώδικα σε αρχείο .txt ------------------------------

  /** Χαρακτήρες μη ασφαλείς για όνομα αρχείου σε Windows/macOS/Linux — δεν αναμένονται σε πραγματικό όνομα προγράμματος (έγκυρο identifier), αλλά προστατεύει και σε ημιτελή/λανθασμένο κώδικα. */
  const UNSAFE_FILENAME_CHARS = /[\\/:*?"<>|]/g;

  /**
   * Ψάχνει το "ΠΡΟΓΡΑΜΜΑ <όνομα>"/"Αλγόριθμος <όνομα>" στην ΑΡΧΗ του κειμένου
   * (καλύπτει ΓΛΩΣΣΑ, Ψευδογλώσσα, ΚΑΙ Φυσική γλώσσα κατά βήματα — και οι
   * τρεις ξεκινάνε έτσι) — ΜΕ ΑΠΛΟ regex πάνω στο κανονικοποιημένο (πεζά/
   * κεφαλαία + τόνοι αδιάφορα) κείμενο, ΟΧΙ πλήρες parsing, ώστε να δουλεύει
   * ΑΚΟΜΑ ΚΙ ΑΝ υπάρχει συντακτικό λάθος παρακάτω στο πρόγραμμα (δεν θέλουμε
   * η αποθήκευση να μπλοκάρεται από αυτό). Το `normalizeKeywordText` διατηρεί
   * 1:1 τις θέσεις χαρακτήρων, οπότε το εύρος που βρίσκουμε στο κανονικοποιημένο
   * κείμενο αντιστοιχεί ΑΚΡΙΒΩΣ στο ίδιο εύρος στο πρωτότυπο — παίρνουμε από
   * ΕΚΕΙ το όνομα, με την ΠΡΑΓΜΑΤΙΚΗ γραφή του χρήστη (όχι κεφαλαιοποιημένο).
   */
  function extractProgramName(sourceText: string): string | null {
    const normalized = normalizeKeywordText(sourceText);
    // ΣΚΟΠΙΜΑ [ \t]+ (ΟΧΙ \s+) ανάμεσα στη λέξη-κλειδί και το όνομα — το \s
    // ταιριάζει ΚΑΙ με αλλαγή γραμμής, οπότε ένα "Αλγόριθμος" ΧΩΡΙΣ όνομα
    // (κενή συνέχεια, μόνο νέα γραμμή μετά) θα προσπερνούσε κατευθείαν στην
    // ΕΠΟΜΕΝΗ γραμμή και θα άρπαζε την ΠΡΩΤΗ της λέξη σαν να ήταν το όνομα
    // (π.χ. "Αλγόριθμος\nΔιάβασε χ" θα έδινε λανθασμένα "Διάβασε"). Με
    // [ \t]+, το όνομα πρέπει να είναι στην ΙΔΙΑ γραμμή — αλλιώς δεν ταιριάζει
    // καθόλου το regex, `match` γίνεται `null`, και ο καλών πέφτει στο
    // fallback "Κώδικας" (βλ. `saveCodeBtn` listener).
    const match = /^\s*(?:ΠΡΟΓΡΑΜΜΑ|ΑΛΓΟΡΙΘΜΟΣ)[ \t]+(\S+)/.exec(normalized);
    if (match === null || match[1] === undefined) return null;
    const nameStart = match.index + match[0].length - match[1].length;
    return sourceText.slice(nameStart, nameStart + match[1].length);
  }

  saveCodeBtn.addEventListener('click', () => {
    const text = editor.getValue();
    const rawName = extractProgramName(text) ?? 'Κώδικας';
    const safeName = rawName.replace(UNSAFE_FILENAME_CHARS, '_');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${safeName}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  });

  openCodeBtn.addEventListener('click', () => openCodeInput.click());

  openCodeInput.addEventListener('change', () => {
    const file = openCodeInput.files?.[0];
    openCodeInput.value = ''; // ώστε η επιλογή του ΙΔΙΟΥ αρχείου ξανά να ξαναπυροδοτεί το 'change'
    if (file === undefined) return;

    void file.text().then((text) => {
      if (!naturalEditorBox.hidden) exitNaturalSplit();
      // Το αποθηκευμένο αρχείο κωδικοποιεί ΜΙΑ συγκεκριμένη διάλεκτο — αν δεν
      // ταιριάξουμε αυτόματα το dropdown, ο editor θα δείχνει λέξεις-κλειδιά
      // "ξένης" διαλέκτου αχρωμάτιστες, και η Εκτέλεση θα σκάει αμέσως.
      const normalizedStart = normalizeKeywordText(text).trimStart();
      applyCodeDialect(normalizedStart.startsWith('ΠΡΟΓΡΑΜΜΑ') ? 'glossa' : 'pseudoglossa');
      clearAllBreakpoints(); // τα breakpoints αφορούσαν τον ΠΡΟΗΓΟΥΜΕΝΟ κώδικα — άσχετα με τις γραμμές του νέου
      editor.setValue(text);
    });
  });

  // --- Πλαϊνό panel λέξεων-κλειδιών --------------------------------------------

  /**
   * Ποιος editor είχε τελευταία τον δρομέα — ΟΧΙ `hasTextFocus()` στη στιγμή
   * του κλικ, γιατί το ίδιο το κλικ σε ένα κουμπί λέξης-κλειδιού έχει ήδη
   * κλέψει την εστίαση από τον editor ΠΡΙΝ φτάσουμε στο insertAtCursor.
   */
  let lastFocusedEditor: monaco.editor.IStandaloneCodeEditor = editor;
  editor.onDidFocusEditorText(() => {
    lastFocusedEditor = editor;
    dismissEditorIntroLine();
  });

  /**
   * Κρύβει τη γραμμή εισαγωγικού κειμένου πάνω από τον editor ΚΑΙ τον τίτλο
   * της κεφαλίδας (#app-title — βλ. σχόλιο στο index.html) με το πρώτο
   * πραγματικό κλικ/focus του χρήστη μέσα σε οποιονδήποτε από τους δύο
   * editors. Και τα δύο ορατά εξαρχής σε όλους (χρήστες και crawlers),
   * εξαφανίζονται μόνο μετά από πραγματική αλληλεπίδραση — όχι κρυμμένα από
   * την αρχή (SEO).
   */
  function dismissEditorIntroLine(): void {
    const introLine = document.getElementById('editor-intro-line');
    if (introLine !== null) introLine.classList.add('is-dismissed');
    const appTitle = document.getElementById('app-title');
    if (appTitle !== null) appTitle.classList.add('is-dismissed');
  }

  /** Εισάγει κείμενο στη θέση του δρομέα (με undo-history, όπως κανονική πληκτρολόγηση). */
  /**
   * Στοχεύει τον editor εισόδου του πλαισίου "Μετατροπή" όσο είναι αυτό
   * ανοιχτό, αλλιώς όποιον από τους δύο editors του split (κώδικας/φυσική
   * γλώσσα) είχε τελευταία τον δρομέα — έτσι οι Λέξεις-κλειδιά γράφουν εκεί
   * που όντως γράφει ο χρήστης, όχι πάντα στον κύριο editor κώδικα.
   */
  function insertAtCursor(text: string): void {
    // Το "!" εδώ είναι ασφαλές: αν το πλαίσιο είναι ήδη ορατό (`!conversionPanel.hidden`
    // / `!naturalEditorBox.hidden`), ο αντίστοιχος lazy editor έχει ήδη
    // δημιουργηθεί — το ίδιο το άνοιγμα του πλαισίου (`openConversionPanel`/
    // `enterNaturalSplit`) τον δημιουργεί ΠΡΙΝ τον ξεκρύψει.
    const target = !conversionPanel.hidden
      ? conversionInputEditor!
      : !naturalEditorBox.hidden && lastFocusedEditor === naturalEditor
        ? naturalEditor!
        : editor;
    target.focus();
    target.trigger('keyboard', 'type', { text: `${text} ` });
  }

  function renderKeywordPalette(): void {
    const baseGroups = dialectSelect.value === 'glossa' ? GLOSSA_PALETTE : PSEUDOGLOSSA_PALETTE;
    // Το λεξιλόγιο της φυσικής γλώσσας κατά βήματα (Βήμα/Είσοδος:/Έξοδος:/...)
    // ΔΕΝ ανήκει στο κανονικό πλαίσιο "Λέξεις-κλειδιά" της ΓΛΩΣΣΑ/Ψευδογλώσσα
    // (κώδικας) — εμφανίζεται εδώ ΜΟΝΟ όσο είναι ενεργό το split "Φυσική
    // γλώσσα κατά βήματα" (dialectSelect.value === 'natural').
    const groups = isNaturalLanguageSelected() ? [...baseGroups, NATURAL_LANGUAGE_PALETTE_GROUP] : baseGroups;
    keywordPanelBody.innerHTML = '';
    for (const group of groups) {
      const section = document.createElement('div');
      section.className = 'keyword-group';

      const heading = document.createElement('h3');
      heading.textContent = group.title;
      section.appendChild(heading);

      const buttons = document.createElement('div');
      buttons.className = 'keyword-buttons';
      for (const item of group.items) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = item;
        btn.addEventListener('click', () => insertAtCursor(item));
        buttons.appendChild(btn);
      }
      section.appendChild(buttons);
      keywordPanelBody.appendChild(section);
    }
  }

  renderKeywordPalette();

  /**
   * Λωρίδα γρήγορης εισαγωγής (ορατή μόνο σε στενές οθόνες, βλ. style.css) —
   * ΜΟΝΟ σύμβολα, ΟΧΙ λέξεις-κλειδιά, γιατί οι λέξεις-κλειδιά προβλέπονται ήδη
   * από την αυτόματη συμπλήρωση του editor (βλ. glossa-language.ts) καθώς
   * πληκτρολογεί ο χρήστης· τα σύμβολα (ειδικά το ←, που δεν υπάρχει σε κανένα
   * πληκτρολόγιο) όχι.
   */
  function buildQuickStripItems(): string[] {
    const groups = dialectSelect.value === 'glossa' ? GLOSSA_PALETTE : PSEUDOGLOSSA_PALETTE;
    const operators = groups.find((g) => g.title === 'Τελεστές');
    // Μόνο τα καθαρά σύμβολα (=,<>,<,<=,>,>=,+,-,*,/,^) — οι λέξεις-τελεστές
    // (ΚΑΙ/Η/ΟΧΙ/DIV/MOD) είναι ήδη καλυμμένες από την αυτόματη συμπλήρωση,
    // άρα παραλείπονται εδώ. Το ← δεν είναι πια σε αυτή την ομάδα (βλ.
    // "Άλλες εντολές" παρακάτω) — δεν είναι τελεστής, είναι ανάθεση.
    const symbols = operators !== undefined ? operators.items.filter((i) => /^[=<>+\-*/^]+$/.test(i)) : [];
    const otherCommands = groups.find((g) => g.title === 'Άλλες εντολές');
    // Το ← δεν υπάρχει σε κανένα πληκτρολόγιο — πάντα πρώτο στη λωρίδα.
    const arrow = otherCommands?.items.find((i) => i === '←');
    const selection = groups.find((g) => g.title === 'Επιλογή');
    const rangeDots = selection !== undefined ? selection.items.filter((i) => i === '..') : [];
    // Το "_" δεν είναι τελεστής (χρησιμοποιείται μέσα σε σύνθετες λέξεις-
    // κλειδιά όπως Τέλος_αν/Με_βήμα) αλλά είναι εξίσου δυσεύρετο σε mobile
    // πληκτρολόγιο — μπαίνει δίπλα στο ← γιατί εκεί θα το ψάξει ο χρήστης.
    return [...(arrow !== undefined ? [arrow] : []), '_', ...symbols, ...rangeDots];
  }

  function renderMobileQuickStrips(): void {
    const items = buildQuickStripItems();
    for (const container of [mobileQuickStrip, conversionMobileQuickStrip]) {
      container.innerHTML = '';
      for (const item of items) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = item;
        btn.addEventListener('click', () => insertAtCursor(item));
        container.appendChild(btn);
      }
    }
  }

  renderMobileQuickStrips();

  // --- Διάγραμμα ροής (αυτόματη ενημέρωση καθώς γράφει ο χρήστης) --------------

  const FLOWCHART_DEBOUNCE_MS = 400;
  let flowchartDebounceTimer: ReturnType<typeof setTimeout> | undefined;

  // Το SVG ξεκινά πάντα από zoom 1 κάθε φορά που ξαναφτιάχνεται (νέος κώδικας) —
  // γι' αυτό ξαναεφαρμόζουμε το τρέχον zoom μετά από κάθε επιτυχημένο render.
  const FLOWCHART_ZOOM_MIN = 0.5;
  const FLOWCHART_ZOOM_MAX = 2.5;
  const FLOWCHART_ZOOM_STEP = 0.15;
  const FLOWCHART_FIT_PADDING = 24; // ~2 x το padding του container (0.75rem)

  let flowchartZoom = 1;
  // Όσο δεν έχει πειράξει ο χρήστης τα +/-, το διάγραμμα προσαρμόζεται πάντα
  // ώστε να χωράει ολόκληρο μέσα στο πλαίσιο (γεμίζοντάς το). Μόλις πατήσει
  // κάποιο από τα δύο κουμπιά, περνάμε μόνιμα σε χειροκίνητο zoom.
  let flowchartAutoFit = true;

  function updateFlowchartZoomButtons(): void {
    flowchartZoomOutBtn.disabled = flowchartZoom <= FLOWCHART_ZOOM_MIN;
    flowchartZoomInBtn.disabled = flowchartZoom >= FLOWCHART_ZOOM_MAX;
  }

  /**
   * "Τμήμα αλγορίθμου": επιτρέπει να γράφεις μια γυμνή λίστα εντολών, ΧΩΡΙΣ
   * το περίβλημα ΑΛΓΟΡΙΘΜΟΣ/ΠΡΟΓΡΑΜΜΑ...ΤΕΛΟΣ, χωρίς συντακτικό σφάλμα — τόσο
   * η Εκτέλεση όσο και το Διάγραμμα ροής δουλεύουν πάνω σε αυτό το τμήμα
   * (βλ. `parseFragment`/`interpretStatements`/`renderFlowchartFragment`). Το
   * διάγραμμα ξεκινά/τελειώνει με απλό βέλος αντί για Αρχή/Τέλος.
   */
  let segmentMode = false;

  segmentToggleBtn.addEventListener('click', () => {
    void (async () => {
      if (!segmentMode && !(await tryConvertFullProgramToSegment())) return; // ο μαθητής αρνήθηκε τη μετατροπή — άσε το toggle σβηστό
      segmentMode = !segmentMode;
      segmentToggleBtn.setAttribute('aria-pressed', String(segmentMode));
      updateFlowchart();
      showSegmentModeToast(segmentMode);
      saveCodeAutosave();
    })();
  });

  /**
   * Αποθηκεύεται σε κάθε αλλαγή κώδικα/διαλέκτου/"Τμήμα αλγορίθμου" — βλ.
   * σχόλιο `CODE_AUTOSAVE_STORAGE_KEY` παραπάνω. Άμεση αποθήκευση (όχι
   * debounce) όταν καλείται από ένα διακριτό click/change (διάλεκτος, Τμήμα
   * αλγορίθμου) — μόνο το πληκτρολόγημα στον editor χρειάζεται debounce (βλ.
   * `scheduleCodeAutosave`, καλείται από `onDidChangeModelContent` παρακάτω).
   */
  function saveCodeAutosave(): void {
    const payload: CodeAutosavePayload = {
      code: editor.getValue(),
      dialect: codeDialect === Dialect.GLOSSA ? 'glossa' : 'pseudoglossa',
      segment: segmentMode,
    };
    localStorage.setItem(CODE_AUTOSAVE_STORAGE_KEY, JSON.stringify(payload));
  }

  let codeAutosaveTimer: ReturnType<typeof setTimeout> | undefined;

  function scheduleCodeAutosave(): void {
    clearTimeout(codeAutosaveTimer);
    codeAutosaveTimer = setTimeout(saveCodeAutosave, 500);
  }

  /** Η γραμμή του ΠΡΩΤΟΥ token με το δοσμένο keyword — βλ. `tryConvertFullProgramToSegment` (εντοπίζει το ΑΡΧΗ της ΓΛΩΣΣΑΣ, που δεν αποθηκεύεται πουθενά στο ίδιο το AST). */
  function findFirstKeywordLine(tokens: readonly Token[], keyword: string): number | null {
    return tokens.find((t) => t.type === TokenType.KEYWORD && t.value === keyword)?.line ?? null;
  }

  /**
   * Η μορφή "Τμήμα αλγορίθμου" της φυσικής γλώσσας δεν έχει καθόλου κεφαλίδα
   * (βλ. `printStatementsAsNaturalLanguageSteps`/`parseNaturalLanguageStepsToStatements`
   * στο natural-language.ts) — μόνο "Βήμα Ν: ...". Η πλήρης μορφή πάντα
   * ξεκινάει με ΑΚΡΙΒΩΣ 3 γραμμές κεφαλίδας: "Αλγόριθμος <όνομα>", "Είσοδος:
   * ...", "Έξοδος: ..." (βλ. `printProgramAsNaturalLanguageSteps`) — αφαιρεί
   * αυτές τις 3 γραμμές, κρατώντας από την πρώτη "Βήμα" και μετά. Αν δεν
   * βρεθεί "Βήμα" (δεν είναι στην αναμενόμενη μορφή), αφήνει το κείμενο όπως
   * είναι, χωρίς να μαντέψει.
   */
  function stripNaturalLanguageHeader(text: string): string {
    const lines = text.split('\n');
    const firstStepIndex = lines.findIndex((l) => l.trimStart().startsWith('Βήμα'));
    if (firstStepIndex <= 0) return text;
    return lines.slice(firstStepIndex).join('\n');
  }

  /**
   * Ενεργοποιείται ΜΟΝΟ όταν ο μαθητής ενεργοποιεί το "Τμήμα αλγορίθμου" πάνω
   * σε ΠΛΗΡΕΣ πρόγραμμα (ξεκινά με 'ΠΡΟΓΡΑΜΜΑ'/'Αλγόριθμος') — αντί να
   * αρνηθεί σιωπηλά (παλιά συμπεριφορά, βλ. `autoDisableSegmentModeIfFullProgram`,
   * που παραμένει σαν δίχτυ ασφαλείας για ΑΛΛΕΣ περιπτώσεις) ρωτάει ρητά, και
   * — αν ο μαθητής συμφωνήσει — κρατάει ΜΟΝΟ ό,τι υπάρχει ανάμεσα σε
   * ΑΡΧΗ/ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ (ΓΛΩΣΣΑ) ή Αλγόριθμος/Τέλος (Ψευδογλώσσα),
   * αντικαθιστώντας το περιεχόμενο του editor με αυτό. Αν είναι ανοιχτό ΚΑΙ
   * το πλαίσιο φυσικής γλώσσας (βλ. `naturalEditorBox`), αφαιρεί ΚΑΙ εκεί την
   * κεφαλίδα (βλ. `stripNaturalLanguageHeader`) — αλλιώς η αριστερή μεριά θα
   * έμενε σε πλήρη μορφή ενώ η δεξιά (ο κώδικας) θα είχε ήδη γίνει τμήμα
   * (bug report: "έκανε τμήμα μόνο το δεξί πλαίσιο").
   *
   * Επιστρέφει `false` όταν ο μαθητής ρωτήθηκε ρητά ΚΑΙ πάτησε "Όχι" (βλ.
   * `showConfirmToast` — περιμένει οριστικά κλικ, δεν λήγει μόνο του), Ή όταν
   * το πρόγραμμα έχει υποπρόγραμμα (ΣΥΝΑΡΤΗΣΗ/ΔΙΑΔΙΚΑΣΙΑ — η μετατροπή θα το
   * πετούσε σιωπηλά, βλ. τον σχετικό έλεγχο παρακάτω, οπότε αρνούμαστε με
   * μήνυμα αντί να ρωτήσουμε) — σε κάθε άλλη περίπτωση (δεν είναι πλήρες
   * πρόγραμμα, έχει ήδη σφάλματα, δεν μπορέσαμε να εντοπίσουμε τα όρια)
   * επιστρέφει `true` και αφήνει το toggle να ενεργοποιηθεί κανονικά — το
   * υπάρχον δίχτυ ασφαλείας (`autoDisableSegmentModeIfFullProgram`) θα το
   * ξανασβήσει αν χρειαστεί.
   */
  async function tryConvertFullProgramToSegment(): Promise<boolean> {
    const source = editor.getValue();
    const dialect = describeDialect();
    const { tokens, errors: lexErrors } = tokenize(source, dialect);
    if (lexErrors.length > 0) return true;
    const firstToken = tokens.find((t) => t.type !== TokenType.NEWLINE && t.type !== TokenType.COMMENT);
    if (firstToken === undefined || firstToken.type !== TokenType.KEYWORD) return true;
    if (firstToken.value !== 'ΠΡΟΓΡΑΜΜΑ' && firstToken.value !== 'ΑΛΓΟΡΙΘΜΟΣ') return true; // δεν είναι πλήρες πρόγραμμα — τίποτα να μετατραπεί, κανονικό toggle

    const { program, error: parseError } = parse(tokens, dialect);
    if (parseError !== null || program === null) return true; // σφάλμα — αφήνει το κανονικό μονοπάτι να το δείξει μόλις ενεργοποιηθεί το toggle

    // Η μετατροπή κρατάει ΜΟΝΟ ό,τι είναι ανάμεσα σε ΑΡΧΗ/ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ
    // (βλ. `startLine`/`endLine` παρακάτω) — αν το πρόγραμμα έχει υποπρόγραμμα
    // (ΣΥΝΑΡΤΗΣΗ/ΔΙΑΔΙΚΑΣΙΑ, πάντα ΕΚΤΟΣ αυτού του εύρους, μετά το
    // ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ) η μετατροπή θα το πετούσε σιωπηλά, αφήνοντας
    // κλήσεις σε ανύπαρκτη πλέον συνάρτηση/διαδικασία — μπερδεμένο σφάλμα
    // χωρίς προφανή αιτία για τον μαθητή. Αρνούμαστε ρητά, με σαφές μήνυμα,
    // αντί να ρωτήσουμε "θέλεις μετατροπή;" σαν να ήταν κανονική περίπτωση.
    if (program.dialect === Dialect.GLOSSA && program.subprograms.length > 0) {
      showToast(
        'Τμήμα Αλγορίθμου',
        'Δεν είναι δυνατή η μετατροπή σε τμήμα αλγορίθμου, γιατί το πρόγραμμα περιέχει υποπρόγραμμα (ΣΥΝΑΡΤΗΣΗ/ΔΙΑΔΙΚΑΣΙΑ).',
      );
      return false;
    }

    const startLine = dialect === Dialect.GLOSSA ? findFirstKeywordLine(tokens, 'ΑΡΧΗ') : program.line;
    const endLine = program.endLine;
    if (startLine === null || endLine === null || endLine <= startLine + 1) return true;

    const confirmed = await showConfirmToast(
      'Τμήμα Αλγορίθμου',
      'Θέλετε ο αλγόριθμός σας να μετατραπεί σε τμήμα αλγορίθμου;',
    );
    if (!confirmed) return false;

    const sourceLines = source.split('\n');
    // Ψευδογλώσσα: το "Δεδομένα //.../" / "Αποτελέσματα //.../" (αν υπάρχουν)
    // βρίσκονται ΜΕΣΑ σε αυτό το εύρος γραμμών, αλλά ΔΕΝ είναι έγκυρη σύνταξη
    // σε "τμήμα αλγορίθμου" (βλ. σχόλιο `Parser.parseFragment` — δεν έχει καν
    // την έννοια ΔΕΔΟΜΕΝΑ/ΑΠΟΤΕΛΕΣΜΑΤΑ) — αφαιρούνται ρητά, ΔΕΝ αρκεί το ίδιο
    // εύρος γραμμών όπως στη ΓΛΩΣΣΑ (bug: το εξαγόμενο τμήμα δεν αναλυόταν).
    const skipLines =
      dialect === Dialect.PSEUDOGLOSSA
        ? new Set(tokens.filter((t) => t.type === TokenType.KEYWORD && (t.value === 'ΔΕΔΟΜΕΝΑ' || t.value === 'ΑΠΟΤΕΛΕΣΜΑΤΑ')).map((t) => t.line))
        : new Set<number>();
    const extracted = sourceLines
      .slice(startLine, endLine - 1)
      .filter((_, i) => !skipLines.has(startLine + 1 + i))
      .join('\n');
    editor.setValue(extracted);
    if (!naturalEditorBox.hidden) {
      naturalEditor!.setValue(stripNaturalLanguageHeader(naturalEditor!.getValue()));
    }
    return true;
  }

  /**
   * Αν το "Τμήμα αλγορίθμου" είναι ενεργό αλλά ο κώδικας ξεκινά με 'ΠΡΟΓΡΑΜΜΑ'/
   * 'Αλγόριθμος' (δηλαδή είναι στην πραγματικότητα πλήρες πρόγραμμα, όχι γυμνό
   * τμήμα εντολών), το toggle γυρνάει αυτόματα σε OFF, χωρίς προειδοποίηση —
   * αλλιώς ο μαθητής βλέπει ένα μπερδεμένο συντακτικό σφάλμα στην ΠΡΩΤΗ λέξη
   * («Αναμενόταν εντολή... — βρέθηκε 'ΠΡΟΓΡΑΜΜΑ'») χωρίς προφανή αιτία, ειδικά
   * αν το toggle έμεινε αναμμένο από παλιότερο πείραμα με ένα κομμάτι κώδικα
   * (δεν αποθηκεύεται πουθενά, αλλά μένει ενεργό μέσα στην ίδια συνεδρία).
   */
  function autoDisableSegmentModeIfFullProgram(tokens: readonly Token[]): void {
    if (!segmentMode) return;
    const firstToken = tokens.find((t) => t.type !== TokenType.NEWLINE && t.type !== TokenType.COMMENT);
    if (firstToken === undefined || firstToken.type !== TokenType.KEYWORD) return;
    if (firstToken.value !== 'ΠΡΟΓΡΑΜΜΑ' && firstToken.value !== 'ΑΛΓΟΡΙΘΜΟΣ') return;
    segmentMode = false;
    segmentToggleBtn.setAttribute('aria-pressed', 'false');
  }

  /** Υπολογίζει το zoom που κάνει το τρέχον SVG να χωράει ακριβώς μέσα στο ορατό πλαίσιο (και στις δύο διαστάσεις). */
  function computeFlowchartFitZoom(): number | null {
    const svg = flowchartContainer.querySelector('svg');
    if (svg === null) return null;
    const naturalWidth = Number(svg.getAttribute('data-natural-width'));
    const naturalHeight = Number(svg.getAttribute('data-natural-height'));
    if (!Number.isFinite(naturalWidth) || !Number.isFinite(naturalHeight) || naturalWidth <= 0 || naturalHeight <= 0) return null;
    const availWidth = flowchartContainer.clientWidth - FLOWCHART_FIT_PADDING;
    const availHeight = flowchartContainer.clientHeight - FLOWCHART_FIT_PADDING;
    if (availWidth <= 0 || availHeight <= 0) return null;
    const fit = Math.min(availWidth / naturalWidth, availHeight / naturalHeight);
    return Math.min(FLOWCHART_ZOOM_MAX, Math.max(0.2, fit));
  }

  function applyFlowchartAutoFit(): void {
    if (!flowchartAutoFit) return;
    const fit = computeFlowchartFitZoom();
    if (fit === null) return;
    flowchartZoom = fit;
    setFlowchartZoom(flowchartContainer, flowchartZoom);
    updateFlowchartZoomButtons();
  }

  /**
   * Το option "flowchart" μέσα στο `<select>` της θέσης που φιλοξενεί ΤΩΡΑ το
   * περιεχόμενο "Διάγραμμα ροής" αλλάζει κείμενο σε `text` — έτσι όσο είναι
   * ενεργή η "Μετατροπή υποπρογράμματος" το ίδιο το dropdown λέει ΜΟΝΟ αυτό
   * (όχι ΚΑΙ "Διάγραμμα ροής" ΚΑΙ ξεχωριστό label δίπλα του — παλιό
   * `#flowchart-pane-title`, αφαιρέθηκε: έδειχνε τα δύο μαζί, ρητό αίτημα να
   * μείνει μόνο ένα, μέσα στο ίδιο select).
   *
   * Ξαναγράφει το option ΣΕ ΟΛΑ τα 4 selects, ΟΧΙ μόνο στο τρέχον, ΚΑΙ με ΤΟ
   * ΙΔΙΟ `text` παντού — ΟΧΙ "τρέχον: text, υπόλοιπα: γενικό 'Διάγραμμα
   * ροής'" (παλιά συμπεριφορά, bug report: ο μαθητής άνοιγε το dropdown ΑΛΛΗΣ
   * θέσης και έβλεπε εκεί "Διάγραμμα ροής" σαν επιλογή, ενώ επιλέγοντάς το θα
   * μετέφερε ΕΚΕΙ το ήδη-ενεργό bundle "Εκφώνηση Άσκησης"/"Μετατροπή
   * υποπρογράμματος" — παραπλανητικό, αφού δεν υπάρχει καν κανονικό Διάγραμμα
   * ροής να επιλεγεί όσο ισχύει μία από αυτές τις ειδικές λειτουργίες). Το
   * ΙΔΙΟ bundle περιεχομένου μετακινείται όπου κι αν επιλεγεί "flowchart" σε
   * ΟΠΟΙΟΔΗΠΟΤΕ dropdown, άρα όλα πρέπει να συμφωνούν για το τι ΠΡΑΓΜΑΤΙΚΑ
   * περιέχει αυτή τη στιγμή.
   */
  function setFlowchartSelectOptionLabel(text: string): void {
    for (const slot of PANEL_SLOT_IDS) {
      const option = panelSlotSelects[slot].querySelector<HTMLOptionElement>('option[value="flowchart"]');
      if (option !== null) option.textContent = text;
    }
  }

  /** Κανονική λειτουργία — SVG διάγραμμα ροής. */
  function showFlowchartDiagramMode(): void {
    setFlowchartSelectOptionLabel(PANEL_CONTENT_LABEL.flowchart);
    // Όχι πια σταθερός const (η θέση/slot που δείχνει το Διάγραμμα ροής
    // μπορεί να έχει αλλάξει, βλ. setSlotContent) — βρίσκουμε ΤΩΡΑ ποιο tab
    // κινητού αντιστοιχεί στο slot που ΤΩΡΑ δείχνει flowchart.
    const mobileFlowchartTabBtn = mobileTabBtnForSlot(findSlotFor('flowchart'));
    if (mobileFlowchartTabBtn !== null) mobileFlowchartTabBtn.textContent = 'Διάγραμμα';
    flowchartZoomControls.hidden = false;
    flowchartContainer.hidden = false;
    subprogramConversionContainer.hidden = true;
    subprogramConversionNote.hidden = true;
  }

  /**
   * Ενεργοποιείται αυτόματα μόλις αναγνωριστεί ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ στον
   * κώδικα (μόνο ΓΛΩΣΣΑ) — αντί για το SVG διάγραμμα, δείχνει ΟΛΟΚΛΗΡΟ το
   * πρόγραμμα με κάθε υποπρόγραμμα μετατρεμμένο στο αντίθετο είδος του
   * (βλ. `convertAllSubprograms`).
   */
  function showSubprogramConversionMode(noteText: string | null): void {
    setFlowchartSelectOptionLabel('Μετατροπή υποπρογράμματος');
    // Σύντομη εκδοχή (όχι ολόκληρο "Μετατροπή υποπρογράμματος") — ίδιο μήκος
    // λέξης με τις άλλες ετικέτες του κάτω tab bar ("Κώδικας"/"Λέξεις"/
    // "Μεταβλητές"), δεν χωράει το πλήρες κείμενο στο στενό πλάτος του tab.
    const mobileFlowchartTabBtn = mobileTabBtnForSlot(findSlotFor('flowchart'));
    if (mobileFlowchartTabBtn !== null) mobileFlowchartTabBtn.textContent = 'Μετατροπή';
    flowchartZoomControls.hidden = true;
    flowchartContainer.hidden = true;
    subprogramConversionContainer.hidden = false;
    subprogramConversionNote.hidden = noteText === null;
    if (noteText !== null) subprogramConversionNote.textContent = noteText;
  }

  const SUBPROGRAM_SIGNAL_KEYWORDS = new Set(['ΚΑΛΕΣΕ', 'ΔΙΑΔΙΚΑΣΙΑ', 'ΣΥΝΑΡΤΗΣΗ']);

  /**
   * Ένα απλό ΛΕΞΙΚΟ (όχι συντακτικό) σάρωμα — δουλεύει ΚΑΙ πάνω σε ημιτελή/
   * λανθασμένο κώδικα, πριν καν προσπαθήσουμε να τον αναλύσουμε πλήρως. Πιάνει
   * τα σημάδια ότι ο μαθητής γράφει (ή μόλις ξεκίνησε να γράφει) υποπρόγραμμα:
   * ΚΑΛΕΣΕ/ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ, ή μια κλήση "Όνομα(...)" που ΔΕΝ είναι μία
   * από τις έτοιμες μαθηματικές συναρτήσεις (βλ. `BUILTIN_MATH_FUNCTION_NAMES`)
   * — άρα πρέπει να είναι κλήση ΣΥΝΑΡΤΗΣΗΣ του μαθητή. Έτσι το πλαίσιο δείχνει
   * «Μετατροπή υποπρογράμματος» ΑΜΕΣΩΣ, αντί να περιμένει πλήρη επιτυχή
   * ανάλυση (βλ. `updateFlowchart`).
   */
  function codeSuggestsSubprogram(tokens: readonly Token[]): boolean {
    for (let i = 0; i < tokens.length; i++) {
      const tok = tokens[i]!;
      if (tok.type === TokenType.KEYWORD && SUBPROGRAM_SIGNAL_KEYWORDS.has(String(tok.value))) return true;
      if (tok.type === TokenType.IDENTIFIER && tokens[i + 1]?.type === TokenType.LPAREN) {
        if (!BUILTIN_MATH_FUNCTION_NAMES.has(normalizeKeywordText(String(tok.value)))) return true;
      }
    }
    return false;
  }

  /** Εναλλακτικό του `clearFlowchart` για όταν ήδη ξέρουμε (βλ. `codeSuggestsSubprogram`) ότι πρόκειται για υποπρόγραμμα — δείχνει το placeholder ΜΕΣΑ στο πλαίσιο μετατροπής αντί για το SVG διάγραμμα. */
  function showSubprogramConversionPlaceholder(noteText: string): void {
    subprogramConversionEditor.setValue('');
    showSubprogramConversionMode(noteText);
  }

  function updateFlowchart(): void {
    updateFlowchartDiagram();
    flowchartDownloadBtn.disabled = flowchartContainer.querySelector('svg') === null;
  }

  function updateFlowchartDiagram(): void {
    const source = editor.getValue();
    if (source.trim().length === 0) {
      showFlowchartDiagramMode();
      clearFlowchart(flowchartContainer, 'Γράψε κώδικα για να εμφανιστεί το διάγραμμα ροής.');
      return;
    }

    const dialect = describeDialect();
    const { tokens, errors: lexErrors } = tokenize(source, dialect);
    autoDisableSegmentModeIfFullProgram(tokens);
    const suggestsSubprogram = dialect === Dialect.GLOSSA && codeSuggestsSubprogram(tokens);
    if (suggestsSubprogram) {
      showSubprogramConversionMode(null);
    } else {
      showFlowchartDiagramMode();
    }

    if (lexErrors.length > 0) {
      if (suggestsSubprogram) {
        showSubprogramConversionPlaceholder('Η μετατροπή θα εμφανιστεί μόλις διορθωθούν τα σφάλματα του κώδικα.');
      } else {
        clearFlowchart(flowchartContainer, 'Το διάγραμμα θα εμφανιστεί μόλις διορθωθούν τα σφάλματα του κώδικα.');
      }
      return;
    }

    try {
      if (segmentMode) {
        const { statements, subprograms, error: parseError } = parseFragment(tokens, dialect);
        if (parseError !== null || statements === null) {
          if (suggestsSubprogram) {
            showSubprogramConversionPlaceholder('Η μετατροπή θα εμφανιστεί μόλις διορθωθούν τα σφάλματα του κώδικα.');
          } else {
            clearFlowchart(flowchartContainer, 'Το διάγραμμα θα εμφανιστεί μόλις διορθωθούν τα σφάλματα του κώδικα.');
          }
          return;
        }
        if (subprograms.length === 0 && suggestsSubprogram) {
          showSubprogramConversionPlaceholder('Γράψε τη ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ που καλείς για να εμφανιστεί εδώ η μετατροπή της.');
          return;
        }
        if (subprograms.length > 0) {
          // Υπάρχει τουλάχιστον μία ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ στο τμήμα (με ή χωρίς
          // εντολές πριν από αυτήν) — τυλίγουμε σε ένα «αδειανό» πρόγραμμα μόνο
          // για να ξαναχρησιμοποιήσουμε το ίδιο `convertAllSubprograms` (που
          // ξαναγράφει ΚΑΙ τα σημεία κλήσης μέσα στις εντολές), και τυπώνουμε
          // τις (μετατρεμμένες) εντολές + τα (μετατρεμμένα) υποπρογράμματα,
          // ΧΩΡΙΣ το ΠΡΟΓΡΑΜΜΑ-περίβλημα που δεν έγραψε ο χρήστης.
          const dummyProgram: GlossaProgram = {
            kind: 'Program',
            dialect: Dialect.GLOSSA,
            name: 'Τμήμα',
            declarations: [],
            body: statements,
            subprograms,
            endLine: null,
            line: subprograms[0]!.line,
            column: subprograms[0]!.column,
          };
          const { program: converted, declined } = convertAllSubprograms(dummyProgram);
          const outputLines: string[] = [];
          if (converted.declarations.length > 0) {
            printDeclarationsSection(converted.declarations, Dialect.GLOSSA, outputLines);
          }
          if (converted.body.length > 0) {
            outputLines.push(printStatements(converted.body, Dialect.GLOSSA, 0));
          }
          for (const sub of converted.subprograms) {
            if (outputLines.length > 0) outputLines.push('');
            outputLines.push(printSubprogram(sub, Dialect.GLOSSA));
          }
          subprogramConversionEditor.setValue(outputLines.join('\n'));
          showSubprogramConversionMode(
            declined.length > 0 ? `Δεν ήταν δυνατή η αυτόματη μετατροπή: ${declined.join(', ')} (παρέμειναν όπως ήταν).` : null,
          );
          return;
        }
        renderFlowchartFragment(statements, dialect, flowchartContainer);
      } else {
        const { program, error: parseError } = parse(tokens, dialect);
        if (parseError !== null || program === null) {
          if (suggestsSubprogram) {
            showSubprogramConversionPlaceholder('Η μετατροπή θα εμφανιστεί μόλις διορθωθούν τα σφάλματα του κώδικα.');
          } else {
            clearFlowchart(flowchartContainer, 'Το διάγραμμα θα εμφανιστεί μόλις διορθωθούν τα σφάλματα του κώδικα.');
          }
          return;
        }
        if (program.dialect === Dialect.GLOSSA && program.subprograms.length === 0 && suggestsSubprogram) {
          showSubprogramConversionPlaceholder('Γράψε τη ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ που καλείς για να εμφανιστεί εδώ η μετατροπή της.');
          return;
        }
        if (program.dialect === Dialect.GLOSSA && program.subprograms.length > 0) {
          const { program: converted, declined } = convertAllSubprograms(program);
          subprogramConversionEditor.setValue(printProgram(converted));
          showSubprogramConversionMode(
            declined.length > 0 ? `Δεν ήταν δυνατή η αυτόματη μετατροπή: ${declined.join(', ')} (παρέμειναν όπως ήταν).` : null,
          );
          return;
        }
        renderFlowchart(program, flowchartContainer);
      }
      if (flowchartAutoFit) {
        applyFlowchartAutoFit();
      } else {
        setFlowchartZoom(flowchartContainer, flowchartZoom);
      }
    } catch {
      clearFlowchart(flowchartContainer, 'Δεν ήταν δυνατή η σχεδίαση του διαγράμματος ροής για αυτόν τον κώδικα.');
    }
  }

  function scheduleFlowchartUpdate(): void {
    if (flowchartDebounceTimer !== undefined) clearTimeout(flowchartDebounceTimer);
    flowchartDebounceTimer = setTimeout(updateFlowchart, FLOWCHART_DEBOUNCE_MS);
  }

  flowchartZoomInBtn.addEventListener('click', () => {
    flowchartAutoFit = false;
    flowchartZoom = Math.min(FLOWCHART_ZOOM_MAX, flowchartZoom + FLOWCHART_ZOOM_STEP);
    setFlowchartZoom(flowchartContainer, flowchartZoom);
    updateFlowchartZoomButtons();
  });

  flowchartZoomOutBtn.addEventListener('click', () => {
    flowchartAutoFit = false;
    flowchartZoom = Math.max(FLOWCHART_ZOOM_MIN, flowchartZoom - FLOWCHART_ZOOM_STEP);
    setFlowchartZoom(flowchartContainer, flowchartZoom);
    updateFlowchartZoomButtons();
  });

  flowchartDownloadBtn.addEventListener('click', () => {
    downloadFlowchartAsPng(flowchartContainer, 'Διάγραμμα_ροής.png').catch((err: unknown) => {
      console.error(err);
    });
  });

  updateFlowchartZoomButtons();

  // Ξαναπροσαρμόζει το διάγραμμα όταν αλλάζει το μέγεθος του πλαισίου του
  // (π.χ. σύρσιμο των λαβών αλλαγής μεγέθους, ή αλλαγή μεγέθους παραθύρου) —
  // μόνο όσο είμαστε ακόμα σε auto-fit λειτουργία.
  new ResizeObserver(() => applyFlowchartAutoFit()).observe(flowchartContainer);

  editor.onDidChangeModelContent(() => {
    autoDetectDialectFromMainEditorStart();
    scheduleFlowchartUpdate();
    // Αν τρέχει (ή είναι παγωμένη σε breakpoint) μια εκτέλεση ΚΑΙ αλλάξει ο
    // κώδικας — είτε χειροκίνητα (θεωρητικά δεν γίνεται όσο editor.readOnly,
    // βλ. `setRunning`) είτε ΠΡΟΓΡΑΜΜΑΤΙΣΤΙΚΑ (π.χ. `editor.setValue()` από τον
    // Μετατροπέα ή από τα κουμπιά "▶ Άνοιξε στον Διερμηνευτή" της Μεθοδολογίας)
    // — η τρέχουσα εκτέλεση διακόπτεται αυτόματα, ίδιο με το χειροκίνητο "■
    // Διακοπή" (βλ. `stopBtn` listener). Χωρίς αυτό, ο παλιός αλγόριθμος θα
    // συνέχιζε να "τρέχει" στο παρασκήνιο πάνω σε πλέον ξεπερασμένο κώδικα.
    currentController?.abort();
    scheduleCodeAutosave();
  });

  // --- Κονσόλα εξόδου -----------------------------------------------------------

  function clearOutput(): void {
    outputEl.innerHTML = '';
  }

  function appendOutputLine(text: string, kind: 'normal' | 'error' | 'info' = 'normal'): void {
    const line = document.createElement('div');
    line.className = kind === 'normal' ? 'output-line' : `output-line ${kind}`;
    line.textContent = text;
    outputEl.appendChild(line);
    outputEl.scrollTop = outputEl.scrollHeight;
  }

  /**
   * Πλαίσιο ρυθμίσεων γεμίσματος πίνακα (⚙️) — πεδία ανάλογα με τον
   * `suggestion.elementType`:
   * - ΑΚΕΡΑΙΕΣ/ΠΡΑΓΜΑΤΙΚΕΣ: "Ελάχιστη"/"Μέγιστη τιμή" (προσυμπληρωμένα από το
   *   αυτόματα ανιχνευμένο εύρος, αν υπάρχει) + "Δεκαδικά ψηφία" (μόνο για
   *   ΠΡΑΓΜΑΤΙΚΕΣ) — εκτελείται με δικό του κουμπί "Συμπλήρωσε".
   * - ΧΑΡΑΚΤΗΡΕΣ: ΜΟΝΟ 3 επιλογές (Ονόματα/Επίθετα/Αριθμούς — βλ. σημείο
   *   κλήσης, εκεί το "🎲" είναι κρυμμένο αρχικά ΑΚΡΙΒΩΣ γι' αυτόν τον τύπο,
   *   εμφανίζεται μέσα σε ΑΥΤΟ το πλαίσιο ως το κουμπί εκτέλεσης).
   * ΛΟΓΙΚΕΣ δεν έχει νόημα εδώ (μόνο ΑΛΗΘΗΣ/ΨΕΥΔΗΣ) — το κουμπί ⚙️ απλά δεν
   * εμφανίζεται γι' αυτόν τον τύπο (βλ. σημείο κλήσης).
   */
  function createArrayFillSettingsPanel(suggestion: ArrayFillSuggestion, onApply: (override: ArrayFillOverride | undefined) => void): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'array-fill-settings-panel';

    if (suggestion.elementType === 'ΑΚΕΡΑΙΕΣ' || suggestion.elementType === 'ΠΡΑΓΜΑΤΙΚΕΣ') {
      const isReal = suggestion.elementType === 'ΠΡΑΓΜΑΤΙΚΕΣ';
      const pattern = suggestion.pattern;
      const defaultMin = pattern.kind === 'range' && pattern.min !== null ? (pattern.minInclusive ? pattern.min : pattern.min + 1) : 0;
      const defaultMax = pattern.kind === 'range' && pattern.max !== null ? (pattern.maxInclusive ? pattern.max : pattern.max - 1) : 100;

      const minInput = document.createElement('input');
      minInput.type = 'number';
      minInput.value = String(defaultMin);
      const minLabel = document.createElement('label');
      minLabel.textContent = 'Ελάχιστη τιμή:';
      minLabel.appendChild(minInput);

      const maxInput = document.createElement('input');
      maxInput.type = 'number';
      maxInput.value = String(defaultMax);
      const maxLabel = document.createElement('label');
      maxLabel.textContent = 'Μέγιστη τιμή:';
      maxLabel.appendChild(maxInput);

      panel.appendChild(minLabel);
      panel.appendChild(maxLabel);

      let decimalsInput: HTMLInputElement | null = null;
      if (isReal) {
        decimalsInput = document.createElement('input');
        decimalsInput.type = 'number';
        decimalsInput.min = '0';
        decimalsInput.max = '6';
        decimalsInput.value = '2';
        const decimalsLabel = document.createElement('label');
        decimalsLabel.textContent = 'Δεκαδικά ψηφία:';
        decimalsLabel.appendChild(decimalsInput);
        panel.appendChild(decimalsLabel);
      }

      const applyBtn = document.createElement('button');
      applyBtn.type = 'button';
      applyBtn.className = 'array-fill-btn';
      applyBtn.textContent = 'Συμπλήρωσε';
      applyBtn.addEventListener('click', () => {
        const min = Number(minInput.value);
        const max = Number(maxInput.value);
        if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) return;
        const decimals = decimalsInput !== null ? Number(decimalsInput.value) : 0;
        onApply({ kind: 'range', min, max, decimals: Number.isFinite(decimals) ? decimals : 0 });
      });
      panel.appendChild(applyBtn);
    } else if (suggestion.elementType === 'ΧΑΡΑΚΤΗΡΕΣ') {
      const select = document.createElement('select');
      const options: ReadonlyArray<{ readonly value: CharacterAlphabet | 'custom'; readonly label: string }> = [
        { value: 'firstNames', label: 'Ονόματα' },
        { value: 'surnames', label: 'Επίθετα' },
        { value: 'numbers', label: 'Αριθμούς' },
        { value: 'custom', label: 'Δικά σου γράμματα/αριθμοί' },
      ];
      for (const opt of options) {
        const optionEl = document.createElement('option');
        optionEl.value = opt.value;
        optionEl.textContent = opt.label;
        select.appendChild(optionEl);
      }
      panel.appendChild(select);

      // Μόνο για "Δικά σου γράμματα/αριθμοί" — λίστα χωρισμένη με κόμμα (π.χ.
      // "Α,Β,Γ" ή "1,2,3"), επιλέγει ΤΥΧΑΙΑ ανάμεσά τους (ίδιος μηχανισμός με
      // ένα αναγνωρισμένο "σύνολο τιμών" — βλ. `ArrayFillOverride` 'set').
      const customInput = document.createElement('input');
      customInput.type = 'text';
      customInput.placeholder = 'π.χ. Α,Β,Γ ή 1,2,3';
      customInput.hidden = true;
      panel.appendChild(customInput);

      select.addEventListener('change', () => {
        customInput.hidden = select.value !== 'custom';
      });

      // Εδώ το κουμπί εκτέλεσης είναι το ίδιο το "🎲" (βλ. σχόλιο αρχείου) —
      // δεν υπάρχει ξεχωριστό "Συμπλήρωσε".
      const fillBtn = document.createElement('button');
      fillBtn.type = 'button';
      fillBtn.className = 'array-fill-btn';
      fillBtn.textContent = '🎲';
      fillBtn.title = suggestion.targetKind === 'array' ? 'Συμπλήρωσε τον πίνακα με την επιλεγμένη κατηγορία' : 'Δώσε τιμή στη μεταβλητή με την επιλεγμένη κατηγορία';
      fillBtn.addEventListener('click', () => {
        if (select.value === 'custom') {
          const values = customInput.value
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v.length > 0);
          if (values.length === 0) return;
          onApply({ kind: 'set', values });
          return;
        }
        onApply({ kind: 'alphabet', alphabet: select.value as CharacterAlphabet });
      });
      panel.appendChild(fillBtn);
    }

    return panel;
  }

  /**
   * Εμφανίζει ένα inline πεδίο εισόδου στην κονσόλα και περιμένει Enter.
   *
   * Δέχεται το AbortSignal του τρέχοντος run ώστε, αν ο χρήστης πατήσει
   * «Διακοπή» ενώ το πεδίο είναι ακόμα ανοιχτό, να το αφαιρεί αμέσως αντί να
   * μένει ορφανό στην οθόνη (ο interpreter σταματά να περιμένει αυτό το
   * Promise μέσω δικού του race στο `waitForInput` — εδώ φροντίζουμε μόνο
   * την οπτική καθαριότητα, το ίδιο το Promise σκόπιμα δεν το settle-άρουμε).
   *
   * Δύο επιπλέον περιπτώσεις γύρω από το αυτόματο γέμισμα πίνακα (βλ.
   * `arrayFillSuggestionsByLine`/`activeArrayFills`):
   * 1. Αν η τρέχουσα γραμμή αντιστοιχεί σε πρόταση που είναι ΗΔΗ ενεργή
   *    (`activeArrayFills`), απαντάει αμέσως — χωρίς να εμφανίσει καν πεδίο,
   *    χωρίς να περιμένει τον μαθητή. Πολλαπλά γεμίσματα μπορούν να είναι
   *    ενεργά ταυτόχρονα (βλ. σχόλιο στο `activeArrayFills`).
   * 2. Αλλιώς, αν η τρέχουσα γραμμή αντιστοιχεί σε ΝΕΑ αναγνωρισμένη πρόταση,
   *    δείχνει κουμπιά "🎲" (αυτόματο μοτίβο, πρώτο) και "⚙️" (ρυθμίσεις —
   *    βλ. `createArrayFillSettingsPanel`, όχι για ΛΟΓΙΚΕΣ) δίπλα στο κανονικό
   *    πεδίο — και τα δύο ενεργοποιούν τη λειτουργία αυτόματου γεμίσματος
   *    (βλ. 1) για όλα τα επόμενα αιτήματα εισόδου αυτού του πίνακα. Στους
   *    ΧΑΡΑΚΤΗΡΕΣ, το "⚙️" ανοίγει έναν δεύτερο, ΕΣΩΤΕΡΙΚΟ "🎲" μέσα στο ίδιο
   *    το πλαίσιο ρυθμίσεων, που εκτελεί με τη ρητά επιλεγμένη κατηγορία
   *    (Ονόματα/Επίθετα/Αριθμούς) αντί για το γενικό αυτόματο μοτίβο.
   *
   * Παράλληλος, ανεξάρτητος μηχανισμός για `ΔΙΑΒΑΣΕ α, β, γ` (πάνω από έναν
   * στόχο στην ίδια εντολή — ένας στόχος μπορεί να είναι ΚΑΙ στοιχείο πίνακα,
   * π.χ. "ΔΙΑΒΑΣΕ Α[i], Β[i]" μέσα σε ΓΙΑ i, όχι μόνο απλή μεταβλητή, βλ.
   * `MultiReadFillSuggestion`) — βλ. `multiReadSuggestionsByLine`/
   * `activeMultiReadFills`: ΕΝΑ κουμπί "🎲 Συμπλήρωσε όλα (Ν)" ενεργοποιεί το
   * γέμισμα και για τους Ν στόχους μαζί, με τον ΔΙΚΟ του δηλωμένο τύπο ο
   * καθένας· κάθε επόμενο αίτημα σε αυτή τη γραμμή απαντάει με τον ΕΠΟΜΕΝΟ
   * στόχο, κυκλικά — αν το ΔΙΑΒΑΣΕ ξαναρχίσει μέσα σε βρόχο (όπως εδώ, μία
   * φορά ανά επανάληψη του ΓΙΑ), ο κύκλος απλά συνεχίζει από την αρχή, χωρίς
   * νέο κλικ — δουλεύει ήδη σωστά και για επαναλαμβανόμενες επισκέψεις της
   * ίδιας γραμμής, χωρίς καμία επιπλέον αλλαγή εδώ.
   */
  /** `MultiReadFillSuggestion.targets[i]` σαν αυτόνομη `ArrayFillSuggestion` — ώστε να ξαναχρησιμοποιείται το `generateArrayFillInputText`. `targetKind`/`totalCount` έρχονται ΑΠΟ τον ίδιο τον στόχο (μπορεί να είναι είτε απλή μεταβλητή είτε στοιχείο πίνακα, π.χ. "ΔΙΑΒΑΣΕ Α[i], Β[i]" μέσα σε ΓΙΑ — βλ. `MultiReadFillSuggestion`). Το `pattern` του κάθε στόχου έχει ήδη υπολογιστεί (π.χ. μη-αρνητικό αν χρησιμοποιείται σε DIV/MOD — βλ. `applyDivModConstraint`). */
  function multiReadMemberAsSuggestion(multi: MultiReadFillSuggestion, index: number): ArrayFillSuggestion {
    const member = multi.targets[index]!;
    return { name: member.name, targetKind: member.targetKind, totalCount: member.totalCount, elementType: member.elementType, pattern: member.pattern, readLines: multi.readLines };
  }

  function promptForInput(signal: AbortSignal): Promise<string> {
    const currentLine = latestExecutingLine;

    // "ΔΙΑΒΑΣΕ α, β, γ" ήδη ενεργό (πατήθηκε το "🎲 Συμπλήρωσε όλα") — απαντάει
    // αμέσως με την ΕΠΟΜΕΝΗ τιμή της σειράς, κυκλικά (βλ. σχόλιο `activeMultiReadFills`).
    const multiSuggestion = currentLine !== null ? multiReadSuggestionsByLine.get(currentLine) : undefined;
    if (multiSuggestion !== undefined && activeMultiReadFills.has(multiSuggestion)) {
      const index = activeMultiReadFills.get(multiSuggestion)!;
      activeMultiReadFills.set(multiSuggestion, (index + 1) % multiSuggestion.targets.length);
      return Promise.resolve(generateArrayFillInputText(multiReadMemberAsSuggestion(multiSuggestion, index)));
    }

    const suggestion = currentLine !== null ? arrayFillSuggestionsByLine.get(currentLine) : undefined;

    if (suggestion !== undefined && activeArrayFills.has(suggestion)) {
      return Promise.resolve(generateArrayFillInputText(suggestion, activeArrayFills.get(suggestion)));
    }

    return new Promise((resolve) => {
      const row = document.createElement('div');
      row.className = 'output-input-row';

      const label = document.createElement('span');
      label.textContent = '➜';

      const input = document.createElement('input');
      input.type = 'text';
      input.autocomplete = 'off';
      input.spellcheck = false;

      row.appendChild(label);
      row.appendChild(input);

      const onAbort = (): void => {
        input.disabled = true;
        row.remove();
      };
      signal.addEventListener('abort', onAbort, { once: true });

      if (multiSuggestion !== undefined) {
        // "ΔΙΑΒΑΣΕ α, β, γ" — ΕΝΑ κουμπί γεμίζει ΟΛΟΥΣ τους στόχους, ο καθένας
        // με τον ΔΙΚΟ του δηλωμένο τύπο (βλ. `MultiReadFillSuggestion`) — ένας
        // στόχος μπορεί να είναι ΚΑΙ στοιχείο πίνακα (π.χ. "ΔΙΑΒΑΣΕ Α[i],
        // Β[i]" μέσα σε ΓΙΑ), όχι μόνο απλή μεταβλητή. Χωρίς "⚙️" εδώ — δεν
        // έχει νόημα ένα ενιαίο πλαίσιο ρυθμίσεων όταν οι στόχοι μπορεί να
        // έχουν διαφορετικό τύπο ο ένας από τον άλλον.
        const fillBtn = document.createElement('button');
        fillBtn.type = 'button';
        fillBtn.className = 'array-fill-btn';
        fillBtn.textContent = `🎲 Συμπλήρωσε όλα (${multiSuggestion.targets.length})`;
        fillBtn.title = `Δίνει αυτόματα τυχαίες τιμές στους στόχους: ${multiSuggestion.targets.map((t) => t.name).join(', ')}`;
        fillBtn.addEventListener('click', () => {
          activeMultiReadFills.set(multiSuggestion, 1); // ο στόχος 0 απαντιέται ΤΩΡΑ, το επόμενο αίτημα θα ζητήσει τον στόχο 1
          signal.removeEventListener('abort', onAbort);
          input.disabled = true;
          row.classList.remove('output-input-row');
          const echo = document.createElement('div');
          echo.className = 'output-line info';
          echo.textContent = `➜ Αυτόματο γέμισμα: ${multiSuggestion.targets.map((t) => t.name).join(', ')}…`;
          row.replaceWith(echo);
          resolve(generateArrayFillInputText(multiReadMemberAsSuggestion(multiSuggestion, 0)));
        });
        row.appendChild(fillBtn);
      } else if (suggestion !== undefined) {
        const isArray = suggestion.targetKind === 'array';
        const startAutoFill = (override: ArrayFillOverride | undefined): void => {
          activeArrayFills.set(suggestion, override);
          signal.removeEventListener('abort', onAbort);
          input.disabled = true;
          row.classList.remove('output-input-row');
          const echo = document.createElement('div');
          echo.className = 'output-line info';
          echo.textContent = isArray
            ? `➜ Αυτόματο γέμισμα πίνακα '${suggestion.name}' (${suggestion.totalCount} στοιχεία)…`
            : `➜ Αυτόματη τυχαία τιμή για τη μεταβλητή '${suggestion.name}'…`;
          row.replaceWith(echo);
          resolve(generateArrayFillInputText(suggestion, override));
        };

        // Μαζεμένα σε ΚΟΙΝΟ container (`.array-fill-btn-group`) αντί για
        // ξεχωριστά, άμεσα παιδιά του `row` — έτσι όταν δεν χωράνε δίπλα στο
        // πεδίο εισαγωγής (π.χ. πολύ στενό πλαίσιο, βλ. setSlotContent), το
        // `flex-wrap` του `.output-input-row` τα στέλνει ΜΑΖΙ σε νέα γραμμή
        // αντί να σπάει το "🎲"/"⚙️" σε δύο διαφορετικές γραμμές.
        const btnGroup = document.createElement('div');
        btnGroup.className = 'array-fill-btn-group';
        row.appendChild(btnGroup);

        // Το "🎲" (αυτόματο μοτίβο, χωρίς ρυθμίσεις) φαίνεται ΠΑΝΤΑ πρώτο,
        // πριν από το "⚙️" — ίδια συμπεριφορά για όλους τους τύπους,
        // συμπεριλαμβανομένων των ΧΑΡΑΚΤΗΡΕΣ (εκεί χρησιμοποιεί το
        // αναγνωρισμένο σύνολο τιμών αν υπάρχει, αλλιώς τυχαίο ελληνικό
        // γράμμα — βλ. `randomUnconstrained`). Ίδιο κουμπί και ίδιος μηχανισμός
        // για πίνακες ΚΑΙ απλές μεταβλητές (`suggestion.targetKind`) — αλλάζει
        // μόνο το κείμενο.
        const fillBtn = document.createElement('button');
        fillBtn.type = 'button';
        fillBtn.className = 'array-fill-btn';
        fillBtn.textContent = isArray ? '🎲 Συμπλήρωσε τον πίνακα' : '🎲 Τυχαία τιμή';
        fillBtn.title = isArray
          ? `Γεμίζει αυτόματα και τα ${suggestion.totalCount} στοιχεία του πίνακα '${suggestion.name}' με τυχαίες έγκυρες τιμές`
          : `Δίνει αυτόματα τυχαίες έγκυρες τιμές στη μεταβλητή '${suggestion.name}'`;
        fillBtn.addEventListener('click', () => startAutoFill(undefined));
        btnGroup.appendChild(fillBtn);

        // Η ΛΟΓΙΚΕΣ δεν έχει νόημα ρυθμίσεων — μόνο ΑΛΗΘΗΣ/ΨΕΥΔΗΣ.
        if (suggestion.elementType !== 'ΛΟΓΙΚΕΣ') {
          const settingsBtn = document.createElement('button');
          settingsBtn.type = 'button';
          settingsBtn.className = 'array-fill-btn';
          settingsBtn.textContent = '⚙️';
          settingsBtn.title = 'Ρυθμίσεις αυτόματου γεμίσματος';

          const settingsPanel = createArrayFillSettingsPanel(suggestion, (override) => {
            settingsPanel.hidden = true;
            startAutoFill(override);
          });
          settingsPanel.hidden = true;

          settingsBtn.addEventListener('click', () => {
            settingsPanel.hidden = !settingsPanel.hidden;
          });
          btnGroup.appendChild(settingsBtn);
          // ΕΚΤΟΣ του btnGroup (όχι μέσα) — απόλυτα τοποθετημένο (βλ.
          // .array-fill-settings-panel στο style.css) σχετικά με το ίδιο το
          // `row`, ώστε να ανοίγει πάντα ΚΑΤΩ από ΟΛΗ τη γραμμή, όχι μέσα σε
          // ένα flex item περιορισμένου πλάτους.
          row.appendChild(settingsPanel);
        }
      }

      outputEl.appendChild(row);
      outputEl.scrollTop = outputEl.scrollHeight;
      input.focus();

      input.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        signal.removeEventListener('abort', onAbort);
        const value = input.value;
        input.disabled = true;
        row.classList.remove('output-input-row');
        const echo = document.createElement('div');
        echo.className = 'output-line';
        echo.textContent = `➜ ${value}`;
        row.replaceWith(echo);
        resolve(value);
      });
    });
  }

  // --- Σφάλματα ↔ Monaco markers -------------------------------------------------

  let errorWordDecorations: monaco.editor.IEditorDecorationsCollection | null = null;

  /**
   * Σφάλμα σε (γραμμή, στήλη) → κόκκινο κυματιστό underline (Monaco marker, με
   * το μήνυμα στο hover) ΚΑΙ φόντο χρώματος πάνω σε ΟΛΗ τη λέξη (`.error-word-
   * highlight`, βλ. style.css) — όχι μόνο στον 1 χαρακτήρα της (γ, στ). Το
   * εύρος της λέξης βρίσκεται με `model.getWordAtPosition` (ίδιο word-pattern
   * που ήδη χρησιμοποιεί το Monaco tokenizer/double-click-select) — αν η θέση
   * δεν πέφτει πάνω σε λέξη (π.χ. δείχνει σε κενό ή σε τέλος αρχείου), γίνεται
   * fallback στον έναν χαρακτήρα.
   */
  function setErrorMarker(line: number, column: number, message: string): void {
    const model = editor.getModel();
    if (model === null) return;

    const word = model.getWordAtPosition({ lineNumber: line, column });
    const startColumn = word?.startColumn ?? column;
    const endColumn = word?.endColumn ?? column + 1;

    monaco.editor.setModelMarkers(model, GLOSSA_LANGUAGE_ID, [
      {
        severity: monaco.MarkerSeverity.Error,
        startLineNumber: line,
        startColumn,
        endLineNumber: line,
        endColumn,
        message,
      },
    ]);

    const decorations: monaco.editor.IModelDeltaDecoration[] = [
      { range: new monaco.Range(line, startColumn, line, endColumn), options: { className: 'error-word-highlight' } },
    ];
    if (errorWordDecorations === null) {
      errorWordDecorations = editor.createDecorationsCollection(decorations);
    } else {
      errorWordDecorations.set(decorations);
    }
  }

  function clearErrorMarkers(): void {
    const model = editor.getModel();
    if (model !== null) monaco.editor.setModelMarkers(model, GLOSSA_LANGUAGE_ID, []);
    errorWordDecorations?.clear();
  }

  // --- Υπογράμμιση τρέχουσας εντολής (όπως στο alkisg.mysch.gr) -----------------

  let currentLineDecorations: monaco.editor.IEditorDecorationsCollection | null = null;
  let naturalLineDecorations: monaco.editor.IEditorDecorationsCollection | null = null;

  /**
   * Συγχρονισμένη υπογράμμιση στο naturalEditor κατά την εκτέλεση — βλ.
   * `currentNaturalLineMap` (γραμμή κώδικα → πρώτο "Βήμα Ν", από την τελευταία
   * `refreshNaturalFromCode`). Ψάχνει τη ΓΡΑΜΜΗ ΚΕΙΜΕΝΟΥ που αρχίζει με
   * "Βήμα <N>:" αντί να υποθέσει θέση, ώστε να παραμένει σωστό ακόμα κι αν ο
   * χρήστης πρόσθεσε/αφαίρεσε κενές γραμμές χειροκίνητα μετά τη μετατροπή.
   */
  function findNaturalTextLineForStep(step: number): number | null {
    // Ασφαλές "!": καλείται μόνο μέσα από `highlightNaturalLine`, που φεύγει
    // νωρίς αν `naturalEditorBox.hidden` — δηλαδή το πλαίσιο (άρα και ο
    // editor) έχει ήδη ανοίξει.
    const lines = naturalEditor!.getValue().split('\n');
    const prefix = `Βήμα ${step}:`;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i]!.trim().startsWith(prefix)) return i + 1;
    }
    return null;
  }

  function highlightNaturalLine(sourceLine: number): void {
    if (naturalEditorBox.hidden || currentNaturalLineMap === null) return;
    const step = currentNaturalLineMap.get(sourceLine);
    if (step === undefined) return;
    const textLine = findNaturalTextLineForStep(step);
    if (textLine === null) return;
    const decorations: monaco.editor.IModelDeltaDecoration[] = [
      { range: new monaco.Range(textLine, 1, textLine, 1), options: { isWholeLine: true, className: 'current-execution-line' } },
    ];
    if (naturalLineDecorations === null) {
      naturalLineDecorations = naturalEditor!.createDecorationsCollection(decorations);
    } else {
      naturalLineDecorations.set(decorations);
    }
  }

  function clearNaturalLineHighlight(): void {
    naturalLineDecorations?.clear();
  }

  function highlightCurrentLine(line: number): void {
    const decorations: monaco.editor.IModelDeltaDecoration[] = [
      { range: new monaco.Range(line, 1, line, 1), options: { isWholeLine: true, className: 'current-execution-line' } },
    ];
    if (currentLineDecorations === null) {
      currentLineDecorations = editor.createDecorationsCollection(decorations);
    } else {
      currentLineDecorations.set(decorations);
    }
    // Αυτόματο scroll ΜΟΝΟ όταν η γραμμή είναι ήδη εκτός ορατής περιοχής (σε
    // μεγάλα προγράμματα το highlight "χανόταν" καθώς προχωρούσε η εκτέλεση
    // εκτός οθόνης) — το "IfOutsideViewport" αποφεύγει άσκοπο/ενοχλητικό
    // scroll σε κάθε βήμα όταν η γραμμή είναι ήδη ορατή.
    editor.revealLineInCenterIfOutsideViewport(line);
    setActiveFlowchartLine(flowchartContainer, line);
    highlightNaturalLine(line);
  }

  function clearCurrentLineHighlight(): void {
    currentLineDecorations?.clear();
    setActiveFlowchartLine(flowchartContainer, null);
    clearNaturalLineHighlight();
    // Ακυρώνει οποιαδήποτε ΕΚΚΡΕΜΗ ενημέρωση από το `scheduleLineHighlight`
    // (μέσω requestAnimationFrame) — χωρίς αυτό, μια εκκρεμής ενημέρωση για
    // την ΤΕΛΕΥΤΑΙΑ εντολή θα μπορούσε να «ζωγραφίσει» την υπογράμμιση ΞΑΝΑ
    // αμέσως μετά το καθάρισμα (η εκτέλεση τελείωσε πριν προλάβει να τρέξει
    // το επόμενο animation frame) — αφήνοντάς τη μόνιμα κολλημένη.
    latestExecutingLine = null;
  }

  /** Ίδιο throttling-σκεπτικό με το scheduleVariablesRender: πολλές ειδοποιήσεις ανά frame, μία ζωγραφιά. */
  let latestExecutingLine: number | null = null;
  let lineHighlightScheduled = false;

  /**
   * Γραμμή πηγαίου κώδικα → πρόταση αυτόματου γεμίσματος πίνακα (βλ.
   * `findArrayFillSuggestions`) — υπολογίζεται ΞΑΝΑ σε κάθε "Εκτέλεση" (βλ.
   * `runProgram`), πριν ξεκινήσει η interpretation. Μία πρόταση μπορεί να
   * αντιστοιχεί σε ΠΑΝΩ ΑΠΟ μία γραμμή (βλ. `ArrayFillSuggestion.readLines`
   * — π.χ. το σχήμα ΟΣΟ έχει 2 γραμμές ΔΙΑΒΑΣΕ).
   */
  let arrayFillSuggestionsByLine = new Map<number, ArrayFillSuggestion>();
  /**
   * Σύνολο ΕΝΕΡΓΩΝ γεμισμάτων πίνακα — κλειδί είναι το ΙΔΙΟ το αντικείμενο
   * `ArrayFillSuggestion` (αναγνώριση με reference, όχι τιμή — μία πρόταση
   * μπορεί να έχει ΠΑΝΩ ΑΠΟ μία γραμμή στο `readLines`, π.χ. το σχήμα ΟΣΟ, και
   * ΟΛΕΣ πρέπει να ταιριάζουν στο ΙΔΙΟ ενεργό γέμισμα). Κάθε επόμενο αίτημα
   * εισόδου (βλ. `promptForInput`) σε γραμμή που ανήκει σε ΚΑΠΟΙΑ ενεργή
   * πρόταση απαντιέται ΑΥΤΟΜΑΤΑ, χωρίς να περιμένει τον μαθητή — ενεργοποιείται
   * ανά πίνακα, όταν πατηθεί το κουμπί "🎲" (χωρίς `override`, αυτόματο μοτίβο)
   * ή "Συμπλήρωσε" στο πλαίσιο ρυθμίσεων ⚙️ (με ρητό `override`).
   *
   * ΕΠΙΤΗΔΕΣ Map (όχι μονό nullable slot): σε nested ΓΙΑ όπου διαβάζονται ΔΥΟ
   * πίνακες εναλλάξ (π.χ. "ΔΙΑΒΑΣΕ ΟΝ[i]" πριν από ΓΙΑ j "ΔΙΑΒΑΣΕ Π[i,j]"), οι
   * γραμμές τους ΕΝΑΛΛΑΣΣΟΝΤΑΙ στην εκτέλεση — ένα μονό "τρέχον ενεργό γέμισμα"
   * θα "έληγε" κάθε φορά που η εκτέλεση προσπερνούσε στην ΑΛΛΗ γραμμή,
   * αναγκάζοντας τον μαθητή να ξαναπατάει το κουμπί σε κάθε εξωτερική
   * επανάληψη. Με σύνολο, ΚΑΘΕ ενεργό γέμισμα καταναλώνεται ΜΟΝΟ στη δική του
   * γραμμή, ανεξάρτητα από το τι μεσολαβεί — δεν χρειάζεται καν ρητή λήξη: μια
   * ολοκληρωμένη πρόταση απλά δεν ξανα-ταιριάζει ποτέ (ο βρόχος της έχει ήδη
   * τελειώσει), οπότε μένει αβλαβώς στο σύνολο μέχρι το επόμενο "Εκτέλεση"
   * (βλ. τα δύο σημεία μηδενισμού `activeArrayFills = new Map()` στο `runProgram`).
   */
  let activeArrayFills = new Map<ArrayFillSuggestion, ArrayFillOverride | undefined>();

  /**
   * Γραμμή πηγαίου κώδικα → πρόταση γεμίσματος για `ΔΙΑΒΑΣΕ α, β, γ` (πάνω από
   * έναν στόχο στην ΙΔΙΑ εντολή — βλ. `findMultiReadFillSuggestions`). Ξεχωριστό
   * από το `arrayFillSuggestionsByLine` πιο πάνω γιατί ο interpreter ζητάει ΜΙΑ
   * τιμή ΑΝΑ στόχο σε ξεχωριστές κλήσεις `promptForInput`, όλες στην ΙΔΙΑ
   * γραμμή — χρειάζεται να θυμόμαστε ΠΟΙΟΣ στόχος είναι η επόμενη σειρά (βλ.
   * `activeMultiReadFills`), κάτι που δεν έχει νόημα για το απλό (μονο-στόχου)
   * μηχανισμό πιο πάνω.
   */
  let multiReadSuggestionsByLine = new Map<number, MultiReadFillSuggestion>();
  /**
   * Ενεργά γεμίσματα πολλαπλών στόχων — κλειδί το ίδιο το `MultiReadFillSuggestion`
   * (ίδιο σκεπτικό με `activeArrayFills`), τιμή ο δείκτης (0-based) του ΕΠΟΜΕΝΟΥ
   * στόχου που θα απαντηθεί. Κάθε φορά που καταναλώνεται ένας στόχος ο δείκτης
   * προχωράει κατά 1 modulo `targets.length` — έτσι, αν το ΔΙΑΒΑΣΕ ξαναρχίσει
   * (π.χ. μέσα σε ΓΙΑ), ο κύκλος συνεχίζει αυτόματα από την αρχή, χωρίς να
   * χρειαστεί να ξαναπατήσει ο μαθητής το κουμπί.
   */
  let activeMultiReadFills = new Map<MultiReadFillSuggestion, number>();

  function scheduleLineHighlight(line: number): void {
    latestExecutingLine = line;
    if (lineHighlightScheduled) return;
    lineHighlightScheduled = true;
    requestAnimationFrame(() => {
      lineHighlightScheduled = false;
      if (latestExecutingLine !== null) highlightCurrentLine(latestExecutingLine);
    });
  }

  /** Η αριστερή μεριά του πλαισίου Κώδικας δουλεύει πάντα σε πραγματική Dialect — βλ. `codeDialect` (το dropdown μπορεί να δείχνει "natural" χωρίς αυτό να αλλάζει τι αναγνωρίζει ο lexer/parser εκεί). */
  function describeDialect(): Dialect {
    return codeDialect;
  }

  // --- Panel μεταβλητών --------------------------------------------------------

  function scalarCellText(value: RuntimeValue | undefined): string {
    if (value === undefined) return '—';
    if (value instanceof RuntimeArray) return 'πίνακας';
    return formatRuntimeValue(value);
  }

  /**
   * Πίνακες (RuntimeArray) ζωγραφίζονται ΞΑΝΑ σε κάθε ζωγραφιά του πλαισίου
   * Μεταβλητές (μία φορά ανά animation frame όσο τρέχει το πρόγραμμα — βλ.
   * `scheduleVariablesRender`). Για μεγάλους πίνακες (π.χ. 100×200 = 20.000
   * κελιά, ειδικά δύο τέτοιοι μαζί) το να ΞΑΝΑΧΤΙΖΟΥΜΕ ολόκληρο το `<table>`
   * (νέο `<td>` για κάθε κελί) σε κάθε frame κολλάει πραγματικά τη σελίδα —
   * επιβεβαιωμένο με μέτρηση. Αυτή η cache κρατάει το ΗΔΗ χτισμένο `<table>`
   * ανά όνομα πίνακα (μαζί με άμεση πρόσβαση σε κάθε `<td>` από τους δείκτες
   * του) — όσο το "σχήμα" (διαστάσεις/όρια) παραμένει ίδιο, ξαναζωγραφίζουμε
   * μόνο ενημερώνοντας το `textContent` των ΗΔΗ υπαρχόντων κελιών αντί να τα
   * δημιουργούμε από την αρχή — πολύ φθηνότερο. Πλήρης ανακατασκευή γίνεται
   * ΜΟΝΟ όταν αλλάζουν πραγματικά τα όρια (σπάνιο — π.χ. δυναμικός πίνακας
   * Ψευδογλώσσας που μόλις άγγιξε νέο δείκτη).
   */
  interface ArrayTableCacheEntry {
    readonly wrapper: HTMLElement;
    readonly dimCount: number;
    readonly from: readonly number[];
    readonly to: readonly number[];
    readonly cellsByKey: ReadonlyMap<string, HTMLTableCellElement>;
  }

  const arrayTableCache = new Map<string, ArrayTableCacheEntry>();

  function setArrayCellContent(td: HTMLTableCellElement, value: RuntimeValue | undefined): void {
    td.textContent = scalarCellText(value);
    td.className = value === undefined ? 'array-empty-cell' : '';
  }

  /**
   * Πάνω από `MAX_VISIBLE_PER_AXIS` θέσεις σε έναν άξονα (γραμμές Ή στήλες,
   * ανεξάρτητα), δείχνουμε μόνο τις πρώτες/τελευταίες `VISIBLE_HEAD_TAIL` με
   * ένα "κενό" (⋯) ανάμεσα — αλλιώς ένας πίνακας 100×200 σημαίνει 20.000
   * πραγματικά κελιά στο DOM, αργό να φτιαχτεί ΚΑΙ αργό να ξαναζωγραφιστεί σε
   * κάθε ενημέρωση (επιβεβαιωμένο με μέτρηση). Τυπικοί πίνακες άσκησης
   * (μέχρι ~40 θέσεις ανά άξονα) δεν επηρεάζονται καθόλου.
   */
  const MAX_VISIBLE_PER_AXIS = 40;
  const VISIBLE_HEAD_TAIL = 20;

  /** `null` = θέση του "κενού" (⋯) ανάμεσα στις πρώτες/τελευταίες `VISIBLE_HEAD_TAIL` — βλ. `MAX_VISIBLE_PER_AXIS`. */
  function visibleAxisIndices(from: number, to: number): readonly (number | null)[] {
    const total = to - from + 1;
    if (total <= MAX_VISIBLE_PER_AXIS) {
      const all: number[] = [];
      for (let i = from; i <= to; i++) all.push(i);
      return all;
    }
    const head: number[] = [];
    for (let i = from; i < from + VISIBLE_HEAD_TAIL; i++) head.push(i);
    const tail: number[] = [];
    for (let i = to - VISIBLE_HEAD_TAIL + 1; i <= to; i++) tail.push(i);
    return [...head, null, ...tail];
  }

  /**
   * Δική μας οριζόντια κύλιση (κουμπιά ‹/› + μπάρα track/thumb) — ΟΧΙ η
   * native μπάρα του browser (το styling πάνω σε native scrollbars δεν
   * είναι αξιόπιστο cross-browser, βλ. `.variable-array-scroll` στο
   * style.css). Τα κουμπιά ‹/› είναι η αξιόπιστη εγγύηση, η μπάρα μένει
   * σαν επιπλέον οπτική ένδειξη.
   *
   * ΤΟ ΠΡΑΓΜΑΤΙΚΟ BUG που κυνηγούσαμε (bug report: "δεν φαίνεται καθόλου")
   * ΔΕΝ ήταν timing/reflow — ήταν ότι ελέγχαμε την υπερχείλιση ΜΙΑ ΚΑΙ
   * ΜΟΝΑΔΙΚΗ φορά, τη στιγμή της δημιουργίας. Αν το πλαίσιο δεν
   * υπερχείλιζε ΤΟΤΕ αλλά υπερχειλίζει ΑΡΓΟΤΕΡΑ (π.χ. ο χρήστης άλλαξε
   * πλάτος πλαισίου μέσω `setSlotContent`/λαβής αλλαγής μεγέθους ΑΦΟΥ ο
   * πίνακας είχε ήδη φτιαχτεί χωρίς κουμπιά), δεν ξαναγινόταν ΠΟΤΕ
   * επανέλεγχος — επιβεβαιώθηκε με ζωντανό διαγνωστικό: `scrollWidth`/
   * `clientWidth` άλλαξαν ΜΕΤΑ την αρχική δημιουργία, αλλά τα κουμπιά
   * παρέμειναν απόντα για πάντα. Ο ResizeObserver πιο κάτω λύνει ακριβώς
   * αυτό — ξαναελέγχει ΚΑΘΕ φορά που αλλάζει το πλάτος του
   * `scrollContainer`, όχι μόνο μία φορά στην αρχή.
   */
  function attachCustomHorizontalScrollbar(scrollContainer: HTMLElement): void {
    // Ιδεμπότητα στο ΠΑΡΑΤΗΡΗΤΗ (ResizeObserver), όχι στα ίδια τα
    // κουμπιά — καλείται ΚΑΘΕ φορά που ξαναζωγραφίζεται το πλαίσιο
    // Μεταβλητές (βλ. call sites), ΑΚΟΜΑ ΚΙ ΑΝ το `scrollContainer` είναι
    // το ΙΔΙΟ, ξαναχρησιμοποιημένο στοιχείο (βλ. `arrayTableCache`) — χωρίς
    // αυτόν τον φρουρό θα προσθέταμε ΞΑΝΑ έναν δεύτερο, τρίτο... παρατηρητή
    // στο ΙΔΙΟ στοιχείο κάθε φορά.
    if (scrollContainer.dataset.hscrollObserverAttached === 'true') {
      tryBuildHorizontalScrollControls(scrollContainer);
      return;
    }
    scrollContainer.dataset.hscrollObserverAttached = 'true';

    tryBuildHorizontalScrollControls(scrollContainer);

    // Ξαναελέγχει σε ΚΑΘΕ αλλαγή πλάτους του scrollContainer — π.χ. ο
    // χρήστης άλλαξε το ποιο περιεχόμενο δείχνει αυτή η θέση
    // (setSlotContent) ή έσυρε τη λαβή αλλαγής μεγέθους. Μένει ενεργός
    // ΓΙΑ ΠΑΝΤΑ πάνω σε αυτό το στοιχείο (καμία ανάγκη αποσύνδεσης — αν το
    // στοιχείο αφαιρεθεί ολόκληρο από το DOM, ο browser τον αγνοεί μόνος
    // του, δεν διαρρέει μνήμη).
    new ResizeObserver(() => tryBuildHorizontalScrollControls(scrollContainer)).observe(scrollContainer);
  }

  /** Ελέγχει ΤΩΡΑ αν υπερχειλίζει και φτιάχνει τα κουμπιά/μπάρα αν χρειάζεται — καλείται επανειλημμένα (αρχικά + σε κάθε αλλαγή πλάτους), γι' αυτό ξεχωριστός φρουρός (`hscrollControlsBuilt`) από αυτόν του παρατηρητή. */
  function tryBuildHorizontalScrollControls(scrollContainer: HTMLElement): void {
    if (scrollContainer.dataset.hscrollControlsBuilt === 'true') return;
    if (scrollContainer.scrollWidth <= scrollContainer.clientWidth) return;
    scrollContainer.dataset.hscrollControlsBuilt = 'true';
    buildHorizontalScrollControls(scrollContainer);
  }

  /** Το πραγματικό DOM-building της γραμμής ελέγχου — βλ. `attachCustomHorizontalScrollbar` για το γιατί τρέχει μέσα σε `requestAnimationFrame`. */
  function buildHorizontalScrollControls(scrollContainer: HTMLElement): void {
    const controls = document.createElement('div');
    controls.className = 'hscroll-controls';

    const leftBtn = document.createElement('button');
    leftBtn.type = 'button';
    leftBtn.className = 'hscroll-btn';
    leftBtn.textContent = '‹';
    leftBtn.setAttribute('aria-label', 'Κύλιση αριστερά');

    const track = document.createElement('div');
    track.className = 'custom-hscrollbar-track';
    const thumb = document.createElement('div');
    thumb.className = 'custom-hscrollbar-thumb';
    track.appendChild(thumb);

    const rightBtn = document.createElement('button');
    rightBtn.type = 'button';
    rightBtn.className = 'hscroll-btn';
    rightBtn.textContent = '›';
    rightBtn.setAttribute('aria-label', 'Κύλιση δεξιά');

    controls.appendChild(leftBtn);
    controls.appendChild(track);
    controls.appendChild(rightBtn);

    const updateThumb = (): void => {
      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      const ratio = scrollContainer.clientWidth / scrollContainer.scrollWidth;
      thumb.style.width = `${ratio * 100}%`;
      thumb.style.left = maxScroll > 0 ? `${(scrollContainer.scrollLeft / maxScroll) * (100 - ratio * 100)}%` : '0%';
      leftBtn.disabled = scrollContainer.scrollLeft <= 0;
      rightBtn.disabled = scrollContainer.scrollLeft >= maxScroll - 1;
    };
    updateThumb();
    scrollContainer.addEventListener('scroll', updateThumb);

    const scrollByStep = (direction: 1 | -1): void => {
      // ~2/3 του ορατού πλάτους ανά κλικ — αρκετό να νιώθεται "μετακίνηση",
      // όχι τόσο ώστε να χάνεται εντελώς η οπτική συνέχεια με το πριν.
      scrollContainer.scrollBy({ left: direction * scrollContainer.clientWidth * 0.66, behavior: 'smooth' });
    };
    leftBtn.addEventListener('click', () => scrollByStep(-1));
    rightBtn.addEventListener('click', () => scrollByStep(1));

    // Σύρσιμο του thumb με το ποντίκι/δάχτυλο — μετατρέπει τη θέση Χ σε
    // αντίστοιχο scrollLeft πάνω στο πραγματικό scrollContainer.
    let dragStartX = 0;
    let dragStartScrollLeft = 0;
    const onPointerMove = (e: PointerEvent): void => {
      const scrollableTrack = track.clientWidth - thumb.clientWidth;
      if (scrollableTrack <= 0) return;
      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      scrollContainer.scrollLeft = dragStartScrollLeft + ((e.clientX - dragStartX) / scrollableTrack) * maxScroll;
    };
    const onPointerUp = (): void => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
    };
    thumb.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      dragStartX = e.clientX;
      dragStartScrollLeft = scrollContainer.scrollLeft;
      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerUp);
    });

    scrollContainer.insertAdjacentElement('afterend', controls);
  }

  function createGapCell(tag: 'th' | 'td'): HTMLTableCellElement {
    const cell = document.createElement(tag);
    cell.textContent = '⋯';
    cell.className = 'array-gap-cell';
    cell.title = 'Δεν εμφανίζονται όλες οι ενδιάμεσες θέσεις — ο πίνακας είναι πολύ μεγάλος';
    return cell;
  }

  /** Πίνακας μίας διάστασης: μία γραμμή δεικτών, μία γραμμή τιμών. */
  function renderArrayTable1D(name: string, array: RuntimeArray, entries: ReadonlyMap<string, RuntimeValue>): HTMLElement {
    const indices = [...entries.keys()].map(Number);
    const declared = array.dimensions;
    const from = declared !== null ? 1 : Math.min(...indices);
    const to = declared !== null ? declared[0]! : Math.max(...indices);
    const visible = visibleAxisIndices(from, to);

    const cached = arrayTableCache.get(name);
    if (cached !== undefined && cached.dimCount === 1 && cached.from[0] === from && cached.to[0] === to) {
      for (const i of visible) {
        if (i === null) continue;
        setArrayCellContent(cached.cellsByKey.get(String(i))!, entries.get(String(i)));
      }
      return cached.wrapper;
    }

    const table = document.createElement('table');
    table.className = 'variable-array-table';
    const headerRow = document.createElement('tr');
    const valueRow = document.createElement('tr');
    const cellsByKey = new Map<string, HTMLTableCellElement>();
    for (const i of visible) {
      if (i === null) {
        headerRow.appendChild(createGapCell('th'));
        valueRow.appendChild(createGapCell('td'));
        continue;
      }
      const th = document.createElement('th');
      th.textContent = String(i);
      headerRow.appendChild(th);

      const td = document.createElement('td');
      setArrayCellContent(td, entries.get(String(i)));
      valueRow.appendChild(td);
      cellsByKey.set(String(i), td);
    }
    table.appendChild(headerRow);
    table.appendChild(valueRow);

    const wrapper = document.createElement('div');
    wrapper.className = 'variable-array-scroll';
    wrapper.appendChild(table);
    arrayTableCache.set(name, { wrapper, dimCount: 1, from: [from], to: [to], cellsByKey });
    return wrapper;
  }

  /** Πίνακας δύο διαστάσεων: γραμμή/στήλη δεικτών γύρω από ένα grid τιμών. */
  function renderArrayTable2D(name: string, array: RuntimeArray, entries: ReadonlyMap<string, RuntimeValue>): HTMLElement {
    const rows: number[] = [];
    const cols: number[] = [];
    for (const key of entries.keys()) {
      const [r, c] = key.split(',').map(Number);
      rows.push(r!);
      cols.push(c!);
    }
    const declared = array.dimensions;
    const rowFrom = declared !== null ? 1 : Math.min(...rows);
    const rowTo = declared !== null ? declared[0]! : Math.max(...rows);
    const colFrom = declared !== null ? 1 : Math.min(...cols);
    const colTo = declared !== null ? declared[1]! : Math.max(...cols);
    const visibleRows = visibleAxisIndices(rowFrom, rowTo);
    const visibleCols = visibleAxisIndices(colFrom, colTo);

    const cached = arrayTableCache.get(name);
    if (
      cached !== undefined &&
      cached.dimCount === 2 &&
      cached.from[0] === rowFrom &&
      cached.to[0] === rowTo &&
      cached.from[1] === colFrom &&
      cached.to[1] === colTo
    ) {
      for (const r of visibleRows) {
        if (r === null) continue;
        for (const c of visibleCols) {
          if (c === null) continue;
          const key = `${r},${c}`;
          setArrayCellContent(cached.cellsByKey.get(key)!, entries.get(key));
        }
      }
      return cached.wrapper;
    }

    const table = document.createElement('table');
    table.className = 'variable-array-table';

    const headerRow = document.createElement('tr');
    headerRow.appendChild(document.createElement('th'));
    for (const c of visibleCols) {
      if (c === null) {
        headerRow.appendChild(createGapCell('th'));
        continue;
      }
      const th = document.createElement('th');
      th.textContent = String(c);
      headerRow.appendChild(th);
    }
    table.appendChild(headerRow);

    const cellsByKey = new Map<string, HTMLTableCellElement>();
    for (const r of visibleRows) {
      const tr = document.createElement('tr');
      if (r === null) {
        tr.appendChild(createGapCell('th'));
        for (const c of visibleCols) tr.appendChild(createGapCell('td'));
        table.appendChild(tr);
        continue;
      }
      const rowHeader = document.createElement('th');
      rowHeader.textContent = String(r);
      tr.appendChild(rowHeader);
      for (const c of visibleCols) {
        if (c === null) {
          tr.appendChild(createGapCell('td'));
          continue;
        }
        const td = document.createElement('td');
        const key = `${r},${c}`;
        setArrayCellContent(td, entries.get(key));
        tr.appendChild(td);
        cellsByKey.set(key, td);
      }
      table.appendChild(tr);
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'variable-array-scroll';
    wrapper.appendChild(table);
    arrayTableCache.set(name, { wrapper, dimCount: 2, from: [rowFrom, colFrom], to: [rowTo, colTo], cellsByKey });
    return wrapper;
  }

  /** 3+ διαστάσεις (σπάνιο) — απλή λίστα "[δείκτες] = τιμή" αντί για grid. */
  function renderArrayFlatList(entries: ReadonlyMap<string, RuntimeValue>): HTMLElement {
    const list = document.createElement('div');
    for (const [key, value] of entries) {
      const row = document.createElement('div');
      row.textContent = `[${key}] = ${scalarCellText(value)}`;
      list.appendChild(row);
    }
    return list;
  }

  function renderArray(name: string, array: RuntimeArray): HTMLElement {
    const entries = array.entries();
    // Πίνακας ΔΗΛΩΜΕΝΟΥ μεγέθους (π.χ. "ΑΚΕΡΑΙΕΣ: Α[10]") — το μέγεθος είναι
    // ήδη γνωστό ΑΠΟ ΤΗ ΔΗΛΩΣΗ, πριν γραφτεί έστω ένα στοιχείο. Ο μαθητής
    // πρέπει να βλέπει ΟΛΟΚΛΗΡΟ τον πίνακα (όλα τα κελιά, άδεια) από την
    // ΑΡΧΗ, και μετά να τα βλέπει να γεμίζουν ένα-ένα — όχι να εμφανίζεται
    // "(κενός ακόμα)" μέχρι την πρώτη ανάθεση. Μόνο για ΠΡΑΓΜΑΤΙΚΑ άγνωστου
    // μεγέθους πίνακα (δυναμικός, Ψευδογλώσσα, χωρίς Δεδομένα-δήλωση) δεν
    // έχουμε τίποτα σίγουρο να δείξουμε πριν γραφτεί κάτι.
    if (entries.size === 0 && array.dimensions === null) {
      arrayTableCache.delete(name);
      const empty = document.createElement('p');
      empty.className = 'variables-empty';
      empty.textContent = '(κενός ακόμα)';
      return empty;
    }
    const dimCount = entries.size === 0 ? array.dimensions!.length : [...entries.keys()][0]!.split(',').length;
    if (dimCount === 1) return renderArrayTable1D(name, array, entries);
    if (dimCount === 2) return renderArrayTable2D(name, array, entries);
    return renderArrayFlatList(entries);
  }

  /**
   * "Πίνακας τιμών" στυλ σχολικού βιβλίου (§25/§26, υποπρογράμματα) — μόνιμη
   * μορφή του πλαισίου (όχι πια απλή επίπεδη λίστα): μία ΟΜΑΔΑ στηλών ανά
   * scope (Κύριο Πρόγραμμα + μία ομάδα ανά υποπρόγραμμα που κλήθηκε ποτέ,
   * με σειρά πρώτης εμφάνισης) και μία γραμμή ανά ειδοποίηση του interpreter
   * (κάθε εντολή, βλ. `VariableScope`). Σε κάθε γραμμή γεμίζουν ΜΟΝΟ οι
   * στήλες του scope που ήταν ενεργό εκείνη τη στιγμή — οι υπόλοιπες μένουν
   * κενές, ακριβώς όπως ο πίνακας ιχνηλάτησης του βιβλίου. Η τελευταία στήλη
   * "Έξοδος" δείχνει ό,τι τυπώθηκε (ΓΡΑΨΕ/ΕΚΤΥΠΩΣΕ/ΕΜΦΑΝΙΣΕ) ΑΚΡΙΒΩΣ πριν από
   * αυτή τη γραμμή (βλ. `pendingOutputText`). Οι πίνακες (RuntimeArray)
   * παραμένουν έξω από αυτόν τον πίνακα — εμφανίζονται χωριστά, από κάτω, με
   * την τρέχουσα (όχι ιστορική) κατάστασή τους.
   */
  interface ScopeColumnGroup {
    readonly key: string;
    readonly label: string;
    readonly varOrder: string[];
  }

  interface TraceRow {
    readonly scopeKey: string;
    readonly values: ReadonlyMap<string, RuntimeValue>;
    readonly outputText: string | null;
    /** Αποτέλεσμα (+ κείμενο, π.χ. "ι <= 3") ελέγχου συνθήκης βρόχου (ΓΙΑ/ΟΣΟ/ΜΕΧΡΙΣ_ΟΤΟΥ) που οδήγησε σε αυτή τη γραμμή — `null` αν η γραμμή δεν σχετίζεται με έλεγχο συνθήκης (βλ. `VariablesListener` στο interpreter.ts). */
    readonly condition: { readonly result: boolean; readonly text: string } | null;
    /** Ονόματα πινάκων (π.χ. ["Α"], ή ["Α","Β"] αν "ΔΙΑΒΑΣΕ Α[i], Β[i]" έγραψε ΚΑΙ τους δύο στην ΙΔΙΑ εντολή) αν αυτή η γραμμή είναι εγγραφή σε μεμονωμένο(α) στοιχείο(α) πίνακα — `null` αλλιώς (βλ. `VariablesListener` στο interpreter.ts). Μη-`null` ⇒ η γραμμή ζωγραφίζεται σαν ΕΝΑ κελί πλάτους ολόκληρου του πίνακα ("Εισαγωγή δεδομένου στον πίνακα Α"), όχι σαν κανονική γραμμή στηλών — καμία ΟΡΑΤΗ (βαθμωτή) μεταβλητή δεν αλλάζει σε τέτοια γραμμή, θα έμοιαζε με άσκοπο διπλότυπο της προηγούμενης. */
    readonly arrayWriteNames: readonly string[] | null;
  }

  let traceRows: TraceRow[] = [];
  let scopeColumns: ScopeColumnGroup[] = [];
  /**
   * ΞΕΧΩΡΙΣΤΗ στήλη ανά ΔΙΑΚΡΙΤΟ κείμενο συνθήκης (π.χ. "ι <= 3" ΚΑΙ "ξ <= 2"
   * ταυτόχρονα σε εμφωλευμένα ΓΙΑ) — ίδια σύμβαση με τον "πίνακα τιμών" του
   * βιβλίου ΑΕΠΠ, όπου κάθε βρόχος έχει τη ΔΙΚΗ του στήλη με επικεφαλίδα την
   * ίδια τη συνθήκη, και οι τιμές Αληθής/Ψευδής κατεβαίνουν από κάτω (βλ.
   * `renderVariablesPanel`). Σειρά εμφάνισης = σειρά πρώτης συνάντησης
   * (ίδιο σκεπτικό με `ScopeColumnGroup.varOrder`).
   */
  let conditionColumns: string[] = [];
  /** Κείμενο από την ΤΕΛΕΥΤΑΙΑ κλήση `io.output` που δεν έχει ακόμα «κολλήσει» σε γραμμή του πίνακα — καταναλώνεται από την επόμενη ειδοποίηση μεταβλητών (βλ. σχόλιο στο σημείο κλήσης του `interpret`). */
  let pendingOutputText: string | null = null;

  function scopeKeyOf(scope: VariableScope): string {
    return scope.kind === 'main' ? 'main' : scope.name;
  }

  function scopeLabelOf(scope: VariableScope): string {
    if (scope.kind === 'main') return 'Κύριο Πρόγραμμα';
    return `${scope.kind === 'procedure' ? 'Διαδικασία' : 'Συνάρτηση'} ${scope.name}`;
  }

  function renderVariablesPanel(): void {
    variablesPanelBody.innerHTML = '';

    if (traceRows.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'variables-empty';
      empty.textContent = 'Καμία μεταβλητή ακόμα.';
      variablesPanelBody.appendChild(empty);
      return;
    }

    // Οι πίνακες (τρέχουσα κατάσταση) ΠΡΩΤΑ, ο πίνακας τιμών (ιστορικό) ΜΕΤΑ
    // — ρητό αίτημα: όποτε υπάρχει πίνακας στο πρόγραμμα, ο μαθητής θέλει να
    // τον βλέπει πρώτα (πιο σημαντικός/μεγάλος), όχι να τον ψάχνει κάτω από
    // ένα μακρύ ιστορικό.
    const latest = traceRows[traceRows.length - 1]!.values;
    const arrayEntries = [...latest].filter((entry): entry is [string, RuntimeArray] => entry[1] instanceof RuntimeArray);
    if (arrayEntries.length > 0) {
      const heading = document.createElement('h3');
      heading.className = 'variables-arrays-heading';
      heading.textContent = 'Πίνακες';
      variablesPanelBody.appendChild(heading);

      for (const [name, array] of arrayEntries) {
        const entry = document.createElement('div');
        entry.className = 'variable-entry';
        const nameEl = document.createElement('span');
        nameEl.className = 'variable-name';
        nameEl.textContent = name;
        entry.appendChild(nameEl);
        entry.appendChild(renderArray(name, array));
        variablesPanelBody.appendChild(entry);
        // Το renderArray μπορεί να επιστρέψει `.variable-array-scroll`
        // (πίνακες 1Δ/2Δ) — μηδενική επίδραση αν όχι (π.χ. flat list).
        const scrollWrapper = entry.querySelector<HTMLElement>('.variable-array-scroll');
        if (scrollWrapper !== null) attachCustomHorizontalScrollbar(scrollWrapper);
      }
    }

    const visibleGroups = scopeColumns.filter((g) => g.varOrder.length > 0);
    if (visibleGroups.length > 0) {
      const traceWrapper = document.createElement('div');
      traceWrapper.className = 'variable-array-scroll';
      const table = document.createElement('table');
      table.className = 'variables-trace-table';

      const groupHeaderRow = document.createElement('tr');
      const nameHeaderRow = document.createElement('tr');
      for (const group of visibleGroups) {
        const groupTh = document.createElement('th');
        groupTh.textContent = group.label;
        groupTh.colSpan = group.varOrder.length;
        groupTh.className = 'variables-scope-header';
        groupHeaderRow.appendChild(groupTh);
        for (const name of group.varOrder) {
          const nameTh = document.createElement('th');
          nameTh.textContent = name;
          nameHeaderRow.appendChild(nameTh);
        }
      }
      // ΞΕΧΩΡΙΣΤΗ στήλη ανά ΔΙΑΚΡΙΤΟ κείμενο συνθήκης (βλ. `conditionColumns`)
      // — η ίδια η συνθήκη (π.χ. "ι <= 3") είναι η ΕΠΙΚΕΦΑΛΙΔΑ της στήλης, και
      // το Αληθής/Ψευδής κατεβαίνει από κάτω σε κάθε γραμμή όπου ελέγχθηκε —
      // ίδια σύμβαση με τον "πίνακα τιμών" του βιβλίου ΑΕΠΠ. Σε εμφωλευμένα
      // ΓΙΑ ι/ΓΙΑ ξ, το "ι <= 3" και το "ξ <= 2" εμφανίζονται σε ΔΥΟ χωριστές
      // στήλες, ώστε να ξεχωρίζουν οπτικά ΠΟΙΟΥ βρόχου είναι ο κάθε έλεγχος.
      if (conditionColumns.length > 0) {
        const conditionGroupTh = document.createElement('th');
        conditionGroupTh.textContent = 'Συνθήκη';
        conditionGroupTh.colSpan = conditionColumns.length;
        conditionGroupTh.className = 'variables-scope-header';
        groupHeaderRow.appendChild(conditionGroupTh);
        for (const conditionText of conditionColumns) {
          const conditionNameTh = document.createElement('th');
          conditionNameTh.textContent = conditionText;
          nameHeaderRow.appendChild(conditionNameTh);
        }
      }

      const outputGroupTh = document.createElement('th');
      groupHeaderRow.appendChild(outputGroupTh);
      const outputNameTh = document.createElement('th');
      outputNameTh.textContent = 'Έξοδος';
      nameHeaderRow.appendChild(outputNameTh);
      table.appendChild(groupHeaderRow);
      table.appendChild(nameHeaderRow);

      /** Σύνολο στηλών του πίνακα — χρειάζεται για το `colSpan` της ειδικής γραμμής "Εισαγωγή δεδομένου στον πίνακα" παρακάτω (βλ. `arrayWriteNames`). */
      const totalColumnCount = visibleGroups.reduce((sum, g) => sum + g.varOrder.length, 0) + conditionColumns.length + 1;

      // Τελευταία γνωστή τιμή ανά (scope, μεταβλητή) — ΚΑΘΩΣ προχωράμε γραμμή
      // προς γραμμή (ΟΧΙ έτοιμο instantaneous state, βλ. `values` του
      // `TraceRow` — αυτό εδώ είναι ΔΙΚΟ ΜΑΣ, τοπικό στο render pass), ώστε να
      // ξεχωρίσουμε ΠΟΙΟ κελί άλλαξε σε ΚΑΘΕ γραμμή — ζητήθηκε ρητά: ένα
      // highlight ώστε ο μαθητής να βλέπει αμέσως τι άλλαξε σε κάθε γραμμή,
      // αντί να συγκρίνει με το μάτι ολόκληρη τη γραμμή από πάνω.
      const lastSeenPerScope = new Map<string, Map<string, RuntimeValue | undefined>>();

      for (const row of traceRows) {
        // Εγγραφή σε μεμονωμένο στοιχείο πίνακα — καμία ΟΡΑΤΗ (βαθμωτή)
        // μεταβλητή δεν αλλάζει σε τέτοια γραμμή, θα έμοιαζε με άσκοπο
        // διπλότυπο της προηγούμενης (bug report). ΜΙΑ ξεχωριστή γραμμή ΑΝΑ
        // πίνακα (όχι μία γραμμή με όλα τα ονόματα μαζί) — π.χ. "ΔΙΑΒΑΣΕ
        // Α[i], Β[i]" δίνει ΔΥΟ γραμμές, μία "...στον πίνακα Α" και μία
        // "...στον πίνακα Β". Κάθε γραμμή είναι ΕΝΑ κελί, πλάτους ολόκληρου
        // του πίνακα — ίδιο μήνυμα είτε προέρχεται από ΔΙΑΒΑΣΕ είτε από
        // ανάθεση/ΑΝΤΑΛΛΑΓΗ.
        if (row.arrayWriteNames !== null) {
          for (const arrayName of row.arrayWriteNames) {
            const messageRow = document.createElement('tr');
            const messageTd = document.createElement('td');
            messageTd.colSpan = totalColumnCount;
            messageTd.className = 'variables-array-write-row';
            messageTd.textContent = `Εισαγωγή δεδομένου στον πίνακα ${arrayName}`;
            messageRow.appendChild(messageTd);
            table.appendChild(messageRow);
          }
          continue;
        }
        const tr = document.createElement('tr');
        const lastSeen = lastSeenPerScope.get(row.scopeKey) ?? new Map<string, RuntimeValue | undefined>();
        lastSeenPerScope.set(row.scopeKey, lastSeen);
        for (const group of visibleGroups) {
          for (const name of group.varOrder) {
            const td = document.createElement('td');
            if (group.key === row.scopeKey) {
              const value = row.values.get(name);
              td.textContent = scalarCellText(value);
              if (value === undefined) td.className = 'array-empty-cell';
              else if (lastSeen.get(name) !== value) td.classList.add('variables-cell-changed');
              lastSeen.set(name, value);
            }
            tr.appendChild(td);
          }
        }
        // Μία στήλη ανά διακριτό κείμενο συνθήκης (βλ. `conditionColumns`) —
        // γεμίζει ΜΟΝΟ η στήλη που ταιριάζει με ΑΥΤΗ τη γραμμή, οι υπόλοιπες
        // μένουν κενές (ίδια σύμβαση με τον "πίνακα τιμών" του βιβλίου ΑΕΠΠ).
        // Κάθε γεμάτο κελί συνθήκης αντιστοιχεί ΠΑΝΤΑ σε έναν έλεγχο που μόλις
        // έγινε (βλ. `condition`, "μιας χρήσης") — άρα highlight πάντα.
        for (const conditionText of conditionColumns) {
          const conditionTd = document.createElement('td');
          conditionTd.className = 'variables-condition-cell';
          if (row.condition !== null && row.condition.text === conditionText) {
            conditionTd.textContent = row.condition.result ? 'Αληθής' : 'Ψευδής';
            conditionTd.classList.add(row.condition.result ? 'variables-condition-true' : 'variables-condition-false');
            conditionTd.classList.add('variables-cell-changed');
          }
          tr.appendChild(conditionTd);
        }
        const outputTd = document.createElement('td');
        outputTd.className = 'variables-output-cell';
        outputTd.textContent = row.outputText ?? '';
        if (row.outputText !== null) outputTd.classList.add('variables-cell-changed');
        tr.appendChild(outputTd);
        table.appendChild(tr);
      }
      traceWrapper.appendChild(table);
      variablesPanelBody.appendChild(traceWrapper);
      // ΜΕΤΑ το appendChild — το scrollWidth/clientWidth δεν έχουν νόημα
      // πριν το στοιχείο μπει πραγματικά στο DOM (βλ. σχόλιο στη συνάρτηση).
      attachCustomHorizontalScrollbar(traceWrapper);
    }
  }

  function clearVariablesPanel(): void {
    traceRows = [];
    scopeColumns = [];
    conditionColumns = [];
    pendingOutputText = null;
    arrayTableCache.clear();
    variablesPanelBody.innerHTML = '';
    const empty = document.createElement('p');
    empty.className = 'variables-empty';
    empty.textContent = 'Καμία μεταβλητή ακόμα.';
    variablesPanelBody.appendChild(empty);
  }

  /**
   * Το λογότυπο είναι διακοσμητικό, μόνο για την "άδεια" κατάσταση πριν την
   * ΠΡΩΤΗ Εκτέλεση — μόλις ξεκινήσει έστω και ένα τρέξιμο, εξαφανίζεται
   * μόνιμα (χωρίς να χρειάζεται να ξαναεμφανιστεί ποτέ σε επόμενα τρεξίματα).
   * ΣΚΟΠΙΜΑ ξεχωριστή συνάρτηση, ΟΧΙ μέσα στο `clearVariablesPanel()` — το
   * λογότυπο ανήκει στο πλαίσιο του ΔΙΑΓΡΑΜΜΑΤΟΣ ΡΟΗΣ (`content-flowchart`,
   * βλ. index.html), όχι στις Μεταβλητές· απλά τα δύο event ταυτίζονταν
   * χρονικά μέχρι τώρα. Τώρα που κάθε περιεχόμενο μπορεί να καταλήξει σε
   * ΟΠΟΙΑΔΗΠΟΤΕ θέση ανεξάρτητα (βλ. setSlotContent), το ένα δεν πρέπει να
   * αγγίζει σιωπηλά DOM στοιχεία του άλλου.
   */
  function hideVariablesPanelLogo(): void {
    variablesPanelLogo.hidden = true;
  }

  /**
   * Ο interpreter ειδοποιεί μετά από κάθε εντολή — πολύ πιο συχνά από όσο
   * χρειάζεται να ζωγραφίζουμε (ένας βρόχος 450 στοιχείων θα σήμαινε 450+
   * πλήρεις ανακατασκευές του DOM). Μαζεύουμε πάντα κάθε στιγμιότυπο στο
   * ιστορικό, αλλά ζωγραφίζουμε το πολύ μία φορά ανά frame.
   */
  let variablesRenderScheduled = false;

  function scheduleVariablesRender(
    vars: ReadonlyMap<string, RuntimeValue>,
    scope: VariableScope,
    condition: { readonly result: boolean; readonly text: string } | null,
    arrayWrite: { readonly arrayNames: readonly string[] } | null,
  ): void {
    const key = scopeKeyOf(scope);
    let group = scopeColumns.find((g) => g.key === key);
    if (group === undefined) {
      group = { key, label: scopeLabelOf(scope), varOrder: [] };
      scopeColumns.push(group);
    }
    for (const [name, value] of vars) {
      if (!(value instanceof RuntimeArray) && !group.varOrder.includes(name)) {
        group.varOrder.push(name);
      }
    }
    if (condition !== null && !conditionColumns.includes(condition.text)) {
      conditionColumns.push(condition.text);
    }
    // ΣΗΜΑΝΤΙΚΟ: το `vars` είναι ο ζωντανός (mutable) Map του Environment, όχι
    // ένα πάγιο στιγμιότυπο — αν το αποθηκεύαμε ως έχει, ΟΛΕΣ οι γραμμές του
    // ιστορικού θα έδειχναν τελικά την ίδια (τελευταία) κατάσταση, αφού θα
    // ήταν όλες αναφορές στο ίδιο αντικείμενο. Ρηχό αντίγραφο «παγώνει» τις
    // βαθμωτές τιμές (immutable primitives) τη στιγμή αυτή· οι τιμές πινάκων
    // παραμένουν ζωντανές αναφορές, κάτι επιθυμητό αφού δείχνουμε πάντα την
    // τρέχουσα (όχι ιστορική) κατάσταση για πίνακες.
    traceRows.push({ scopeKey: key, values: new Map(vars), outputText: pendingOutputText, condition, arrayWriteNames: arrayWrite?.arrayNames ?? null });
    pendingOutputText = null;
    if (variablesRenderScheduled) return;
    variablesRenderScheduled = true;
    requestAnimationFrame(() => {
      variablesRenderScheduled = false;
      renderVariablesPanel();
    });
  }

  // --- Εκτέλεση ------------------------------------------------------------------

  let currentController: AbortController | null = null;

  /**
   * Όσο δεν είναι `null`, ο αλγόριθμος είναι ΠΑΓΩΜΕΝΟΣ σε breakpoint — καλώντας
   * αυτή τη συνάρτηση (μέσω του κουμπιού "Εκτέλεση", που μετατρέπεται σε
   * "Συνέχεια", βλ. `runBtn` listener παρακάτω) ξεμπλοκάρει το αντίστοιχο
   * `await` μέσα στον interpreter (βλ. `onBreakpointHit`/`BreakpointHook`).
   */
  let resumeBreakpoint: (() => void) | null = null;

  function setRunButtonLabel(text: string): void {
    const label = runBtn.querySelector<HTMLElement>('.toolbar-btn-label');
    if (label !== null) label.textContent = ` ${text}`;
    runBtn.title = text;
  }

  /**
   * Το ίδιο κουμπί αλλάζει ρόλο ανάλογα με το αν τρέχει κάτι: "■ Διακοπή" όσο
   * εκτελείται (σταματάει την εκτέλεση, βλ. `stopBtn` listener), αλλιώς
   * "🧹 Καθαρισμός" (καθαρίζει έξοδο/μεταβλητές/highlight — ίδιος καθαρισμός
   * με αυτόν που ήδη κάνει η `runProgram` στην αρχή κάθε τρεξίματος). Ποτέ
   * ανενεργό — πάντα κάνει κάτι χρήσιμο.
   */
  function setStopButtonState(running: boolean): void {
    const icon = stopBtn.firstChild;
    const label = stopBtn.querySelector<HTMLElement>('.toolbar-btn-label');
    if (icon !== null) icon.textContent = running ? '■' : '🧹';
    if (label !== null) label.textContent = running ? ' Διακοπή' : ' Καθαρισμός';
    stopBtn.title = running ? 'Διακοπή' : 'Καθαρισμός';
  }

  function setRunning(running: boolean): void {
    runBtn.disabled = running;
    setStopButtonState(running);
    openCodeBtn.disabled = running;
    editor.updateOptions({ readOnly: running });
    // Δίχτυ ασφαλείας: αν η εκτέλεση σταμάτησε (π.χ. "Διακοπή") ΕΝΩ ήταν
    // παγωμένη σε breakpoint, το κουμπί πρέπει να ξαναγίνει "Εκτέλεση" — το
    // `resumeBreakpoint` της προηγούμενης εκτέλεσης δεν έχει πια νόημα.
    if (!running) {
      resumeBreakpoint = null;
      setRunButtonLabel('Εκτέλεση');
    }
  }

  /**
   * Βλ. `BreakpointHook` στο interpreter.ts — καλείται ΠΡΙΝ από ΚΑΘΕ εντολή.
   * Αν η γραμμή δεν έχει breakpoint, επιστρέφει αμέσως (`undefined`) και ο
   * αλγόριθμος συνεχίζει κανονικά. Αλλιώς, "παγώνει" — επιστρέφει ένα Promise
   * που λύνεται μόνο όταν ο μαθητής πατήσει "Συνέχεια" (βλ. `resumeBreakpoint`).
   */
  function onBreakpointHit(line: number): Promise<void> | void {
    if (!currentBreakpointLines().has(line)) return undefined;
    return new Promise<void>((resolve) => {
      resumeBreakpoint = resolve;
      runBtn.disabled = false;
      setRunButtonLabel('Συνέχεια');
      statusText.textContent = `Παγωμένο στη γραμμή ${line}`;
    });
  }

  async function runProgram(): Promise<void> {
    clearOutput();
    clearErrorMarkers();
    clearVariablesPanel();
    hideVariablesPanelLogo();
    clearCurrentLineHighlight();

    // Το split "Κώδικας"/"Φυσική γλώσσα" τρέχει σαν ένα: αν ο χρήστης έγραψε
    // (ή πρόσφατα άλλαξε) μόνο τη φυσική γλώσσα, συγχρόνισε πρώτα τον κώδικα
    // από εκεί, ώστε η Εκτέλεση να μη σκάει επειδή το πλαίσιο κώδικα ήταν
    // άδειο/ξεπερασμένο — και το highlight της τρέχουσας εντολής να φαίνεται
    // ΚΑΙ στις δύο πλευρές (βλ. `scheduleLineHighlight`/`highlightNaturalLine`).
    if (!naturalEditorBox.hidden && naturalEditor!.getValue().trim().length > 0) {
      if (!convertNaturalToCode()) {
        statusText.textContent = 'Σφάλμα μετατροπής';
        return;
      }
    }

    // Ξαναδιορθώνει τις εσοχές ΚΑΙ τα κενά γύρω από τελεστές/κόμμα/παρενθέσεις
    // σε ΟΛΟ το κείμενο πριν τρέξει — καλύπτει κώδικα που επικολλήθηκε από
    // αλλού (χωρίς εσοχές/κενά, ή με λάθος βάθος), που το per-keystroke
    // `registerAutoIndent` δεν προλαβαίνει να διορθώσει αφού δεν περνάει από
    // Enter γραμμή-γραμμή. Αν δεν άλλαξε τίποτα, το `executeEdits` παρακάτω
    // απλά δεν κάνει διαφορά.
    const reindented = reindentGlossaCode(normalizeOperatorSpacing(editor.getValue()));
    if (reindented !== editor.getValue()) {
      const model = editor.getModel();
      if (model !== null) {
        const breakpointLinesBeforeReindent = [...currentBreakpointLines()];
        editor.executeEdits('reindent-on-run', [{ range: model.getFullModelRange(), text: reindented }]);
        restoreBreakpointsAtLines(breakpointLinesBeforeReindent);
      }
    }

    setRunning(true);
    statusText.textContent = 'Εκτελείται…';

    const source = editor.getValue();
    const dialect = describeDialect();

    const { tokens, errors: lexErrors } = tokenize(source, dialect);
    if (lexErrors.length > 0) {
      reportLexErrors(lexErrors);
      finishRun('Λεξικό σφάλμα');
      return;
    }
    autoDisableSegmentModeIfFullProgram(tokens);

    const controller = new AbortController();
    const io = {
      input: () => promptForInput(controller.signal),
      // Κρατάει το κείμενο για τη στήλη "Έξοδος" του πίνακα τιμών — «κολλάει»
      // στην ΕΠΟΜΕΝΗ γραμμή του πίνακα (βλ. `scheduleVariablesRender`), που σε
      // όλες τις περιπτώσεις μας ενδιαφέρουν (ΓΡΑΨΕ/ΕΚΤΥΠΩΣΕ/ΕΜΦΑΝΙΣΕ) ταιριάζει
      // ΑΚΡΙΒΩΣ με την αμέσως επόμενη ειδοποίηση μεταβλητών του interpreter.
      output: (text: string) => {
        appendOutputLine(text);
        pendingOutputText = pendingOutputText === null ? text : `${pendingOutputText}\n${text}`;
      },
    };
    const delayMs = slowExecutionEnabled ? SLOW_EXECUTION_DELAY_MS : undefined;

    let runtimeError: RuntimeError | null;
    let cancelled: boolean;

    if (segmentMode) {
      const { statements, subprograms, error: parseError } = parseFragment(tokens, dialect);
      if (parseError !== null || statements === null) {
        reportParseError(parseError);
        finishRun('Συντακτικό σφάλμα');
        return;
      }
      // Ίδιος κανόνας με πλήρες πρόγραμμα (βλ. παρακάτω) — ισχύει ΚΑΙ σε
      // "τμήμα αλγορίθμου", ανεξαρτήτως διαλέκτου.
      const reusedCounter = findFirstReusedLoopCounter([statements, ...subprograms.map((s) => s.body)]);
      if (reusedCounter !== null) {
        reportReusedLoopCounterError(reusedCounter);
        finishRun('Σφάλμα βρόχων');
        return;
      }
      arrayFillSuggestionsByLine = new Map(findArrayFillSuggestionsInStatements(statements).flatMap((s) => s.readLines.map((line) => [line, s] as const)));
      activeArrayFills = new Map();
      multiReadSuggestionsByLine = new Map(findMultiReadFillSuggestionsInStatements(statements).flatMap((s) => s.readLines.map((line) => [line, s] as const)));
      activeMultiReadFills = new Map();
      currentController = controller;
      ({ error: runtimeError, cancelled } = await interpretStatements(
        statements,
        subprograms,
        io,
        controller.signal,
        scheduleVariablesRender,
        scheduleLineHighlight,
        delayMs,
        undefined,
        onBreakpointHit,
      ));
    } else {
      const { program, error: parseError } = parse(tokens, dialect);
      if (parseError !== null || program === null) {
        reportParseError(parseError);
        finishRun('Συντακτικό σφάλμα');
        return;
      }
      // Μια έτοιμη μαθηματική συνάρτηση (ΗΜ/ΣΥΝ/ΕΦ/ΛΟΓ/Ε/Α_Μ/Α_Τ/Τ_Ρ) είναι
      // δεσμευμένη λέξη — δεν επιτρέπεται ως όνομα μεταβλητής/πίνακα/
      // σταθεράς/παραμέτρου, σε καμία από τις δύο διαλέκτους (βλ.
      // `findReservedFunctionNameAsIdentifier`).
      const reservedName = findReservedFunctionNameAsIdentifier(program);
      if (reservedName !== null) {
        reportReservedFunctionNameError(reservedName);
        finishRun('Σφάλμα ονόματος');
        return;
      }
      // Μόνο ΓΛΩΣΣΑ: ΚΑΘΕ μεταβλητή/πίνακας (ΚΑΙ ο μετρητής ενός ΓΙΑ) πρέπει
      // να έχει δηλωθεί στο ΜΕΤΑΒΛΗΤΕΣ. Ελέγχεται ΠΡΙΝ την εκτέλεση (σαν
      // συντακτικό σφάλμα) ώστε να πιάνει αδήλωτες μεταβλητές ΑΚΟΜΑ ΚΙ αν
      // βρίσκονται σε κλάδο που δεν θα εκτελούνταν σε αυτό το συγκεκριμένο
      // τρέξιμο (βλ. `findUndeclaredGlossaReference`).
      if (program.dialect === Dialect.GLOSSA) {
        // Το ίδιο όνομα δεν επιτρέπεται να δηλωθεί δύο φορές (βλ.
        // `findDuplicateDeclaration`) — ελέγχεται ΠΡΩΤΑ, πριν από ό,τι
        // υποθέτει ότι οι δηλώσεις είναι καλά σχηματισμένες.
        const duplicateDeclaration = findDuplicateDeclaration(program);
        if (duplicateDeclaration !== null) {
          reportDuplicateDeclarationError(duplicateDeclaration);
          finishRun('Σφάλμα δηλώσεων');
          return;
        }
        // Το ίδιο όνομα παραμέτρου δεν επιτρέπεται δύο φορές στο ΙΔΙΟ
        // υποπρόγραμμα (βλ. `findDuplicateParameterName`).
        const duplicateParameter = findDuplicateParameterName(program);
        if (duplicateParameter !== null) {
          reportDuplicateParameterNameError(duplicateParameter);
          finishRun('Σφάλμα παραμέτρου');
          return;
        }
        // Ένα υποπρόγραμμα δεν επιτρέπεται να έχει το ίδιο όνομα με έτοιμη
        // μαθηματική συνάρτηση (βλ. `findBuiltinFunctionNameCollision`).
        const builtinCollision = findBuiltinFunctionNameCollision(program);
        if (builtinCollision !== null) {
          reportBuiltinFunctionNameCollisionError(builtinCollision);
          finishRun('Σφάλμα ονόματος');
          return;
        }
        const undeclared = findUndeclaredGlossaReference(program);
        if (undeclared !== null) {
          reportDeclarationError(undeclared);
          finishRun('Σφάλμα δηλώσεων');
          return;
        }
        // Μια ΣΤΑΘΕΡΑ δεν αλλάζει τιμή μετά τη δήλωσή της (βλ. `findConstantReassignment`).
        const reassignedConstant = findConstantReassignment(program);
        if (reassignedConstant !== null) {
          reportConstantReassignmentError(reassignedConstant);
          finishRun('Σφάλμα σταθεράς');
          return;
        }
        // Μια πραγματική παράμετρος πρέπει να είναι πίνακας ΑΚΡΙΒΩΣ όταν η
        // τυπική είναι δηλωμένη πίνακας (βλ. `findParameterKindMismatch`).
        const kindMismatch = findParameterKindMismatch(program);
        if (kindMismatch !== null) {
          reportParameterKindMismatchError(kindMismatch);
          finishRun('Σφάλμα παραμέτρου');
          return;
        }
        // Ένας πίνακας-παράμετρος πρέπει να έχει ΑΚΡΙΒΩΣ το δηλωμένο μέγεθος
        // της τυπικής (βλ. `findArrayParameterSizeMismatch`).
        const arraySizeMismatch = findArrayParameterSizeMismatch(program);
        if (arraySizeMismatch !== null) {
          reportArrayParameterSizeMismatchError(arraySizeMismatch);
          finishRun('Σφάλμα παραμέτρου');
          return;
        }
        // Το πλήθος δεικτών σε ΚΑΘΕ χρήση πίνακα (π.χ. `β[i]` ή `β[i, j]`)
        // πρέπει να ταιριάζει με το δηλωμένο πλήθος διαστάσεων (βλ.
        // `findArrayDimensionMismatch`) — αλλιώς μπερδεύει με ένα εντελώς
        // άσχετο "εκτός ορίων" σφάλμα ΜΟΝΟ στο runtime (bug report).
        const dimensionMismatch = findArrayDimensionMismatch(program);
        if (dimensionMismatch !== null) {
          reportArrayDimensionMismatchError(dimensionMismatch);
          finishRun('Σφάλμα πίνακα');
          return;
        }
        // Δεν επιτρέπεται ανάθεση σε ολόκληρο πίνακα, μόνο στα στοιχεία του
        // (βλ. `findWholeArrayAssignment`).
        const wholeArrayAssignment = findWholeArrayAssignment(program);
        if (wholeArrayAssignment !== null) {
          reportWholeArrayAssignmentError(wholeArrayAssignment);
          finishRun('Σφάλμα πίνακα');
          return;
        }
        // Δεν μπορεί να χρησιμοποιηθεί ολόκληρος πίνακας ως έκφραση στο
        // ΕΠΙΛΕΞΕ (βλ. `findSwitchArrayDiscriminant`).
        const switchArrayDiscriminant = findSwitchArrayDiscriminant(program);
        if (switchArrayDiscriminant !== null) {
          reportSwitchArrayDiscriminantError(switchArrayDiscriminant);
          finishRun('Σφάλμα πίνακα');
          return;
        }
        // Η ΔΙΑΒΑΣΕ δεν δέχεται μεταβλητές τύπου ΛΟΓΙΚΕΣ (βλ. `findReadBooleanTarget`).
        const readBooleanTarget = findReadBooleanTarget(program);
        if (readBooleanTarget !== null) {
          reportReadBooleanTargetError(readBooleanTarget);
          finishRun('Σφάλμα εισόδου');
          return;
        }
      }
      // Δεν επιτρέπεται η ίδια μεταβλητή-μετρητής σε εμφωλευμένους βρόχους —
      // ισχύει ΚΑΙ στις δύο διαλέκτους (βλ. `findReusedLoopCounter`).
      const reusedCounter = findFirstReusedLoopCounter([
        program.body,
        ...(program.dialect === Dialect.GLOSSA ? program.subprograms.map((s) => s.body) : []),
      ]);
      if (reusedCounter !== null) {
        reportReusedLoopCounterError(reusedCounter);
        finishRun('Σφάλμα βρόχων');
        return;
      }
      // Ο μετρητής ενός ΓΙΑ δεν αλλάζει χειροκίνητα μέσα στο σώμα του — ισχύει
      // ΚΑΙ στις δύο διαλέκτους (βλ. `findLockedForCounterReassignment`).
      const lockedCounterReassignment = findFirstLockedForCounterReassignment([
        program.body,
        ...(program.dialect === Dialect.GLOSSA ? program.subprograms.map((s) => s.body) : []),
      ]);
      if (lockedCounterReassignment !== null) {
        reportLockedCounterReassignmentError(lockedCounterReassignment);
        finishRun('Σφάλμα βρόχων');
        return;
      }
      arrayFillSuggestionsByLine = new Map(findArrayFillSuggestions(program).flatMap((s) => s.readLines.map((line) => [line, s] as const)));
      activeArrayFills = new Map();
      multiReadSuggestionsByLine = new Map(findMultiReadFillSuggestions(program).flatMap((s) => s.readLines.map((line) => [line, s] as const)));
      activeMultiReadFills = new Map();
      currentController = controller;
      ({ error: runtimeError, cancelled } = await interpret(
        program,
        io,
        controller.signal,
        scheduleVariablesRender,
        scheduleLineHighlight,
        delayMs,
        undefined,
        onBreakpointHit,
      ));
    }
    currentController = null;
    clearCurrentLineHighlight();

    // Εγγύηση ότι το panel δείχνει την πραγματικά τελευταία κατάσταση, χωρίς
    // να περιμένει το επόμενο animation frame (βλ. σχόλιο στο scheduleVariablesRender).
    if (traceRows.length > 0) renderVariablesPanel();

    if (cancelled) {
      appendOutputLine('Η εκτέλεση διακόπηκε.', 'info');
      finishRun('Διακόπηκε');
      return;
    }
    if (runtimeError !== null) {
      reportRuntimeError(runtimeError);
      finishRun('Σφάλμα εκτέλεσης');
      return;
    }

    appendOutputLine('Η εκτέλεση ολοκληρώθηκε.', 'info');
    finishRun('Ολοκληρώθηκε');
  }

  function reportLexErrors(errors: readonly LexError[]): void {
    for (const err of errors) {
      appendOutputLine(`Λεξικό σφάλμα (γραμμή ${err.line}, στήλη ${err.column}): ${err.message}`, 'error');
    }
    const first = errors[0];
    if (first !== undefined) setErrorMarker(first.line, first.column, first.message);
  }

  function reportParseError(error: ParseError | null): void {
    if (error === null) {
      appendOutputLine('Άγνωστο σφάλμα ανάλυσης.', 'error');
      return;
    }
    appendOutputLine(`Συντακτικό σφάλμα (γραμμή ${error.line}, στήλη ${error.column}): ${error.message}`, 'error');
    setErrorMarker(error.line, error.column, error.message);
  }

  function reportRuntimeError(error: RuntimeError): void {
    appendOutputLine(`Σφάλμα εκτέλεσης (γραμμή ${error.line}, στήλη ${error.column}): ${error.message}`, 'error');
    setErrorMarker(error.line, error.column, error.message);
  }

  function reportDeclarationError(ref: UndeclaredReference): void {
    const message = `Η μεταβλητή/πίνακας '${ref.name}' δεν έχει δηλωθεί στο ΜΕΤΑΒΛΗΤΕΣ`;
    appendOutputLine(`Σφάλμα δηλώσεων (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  /** Ελέγχει πολλαπλά "σώματα" (κύριο + κάθε υποπρόγραμμα) με το `findReusedLoopCounter` και επιστρέφει το ΠΡΩΤΟ που βρέθηκε. */
  function findFirstReusedLoopCounter(bodies: readonly (readonly Statement[])[]): ReusedLoopCounter | null {
    for (const body of bodies) {
      const result = findReusedLoopCounter(body);
      if (result !== null) return result;
    }
    return null;
  }

  function reportReusedLoopCounterError(ref: ReusedLoopCounter): void {
    const message = `Ο μετρητής '${ref.name}' χρησιμοποιείται ήδη από εξωτερικό βρόχο — δεν επιτρέπεται η ίδια μεταβλητή-μετρητής σε εμφωλευμένους βρόχους`;
    appendOutputLine(`Σφάλμα βρόχων (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  function findFirstLockedForCounterReassignment(bodies: readonly (readonly Statement[])[]): LockedCounterReassignment | null {
    for (const body of bodies) {
      const result = findLockedForCounterReassignment(body);
      if (result !== null) return result;
    }
    return null;
  }

  function reportLockedCounterReassignmentError(ref: LockedCounterReassignment): void {
    const message = `Ο μετρητής '${ref.name}' του ΓΙΑ δεν επιτρέπεται να αλλάξει τιμή μέσα στο σώμα του βρόχου`;
    appendOutputLine(`Σφάλμα βρόχων (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  function reportConstantReassignmentError(ref: ConstantReassignment): void {
    const message = `Η σταθερά '${ref.name}' δεν μπορεί να αλλάξει τιμή`;
    appendOutputLine(`Σφάλμα σταθεράς (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  function reportParameterKindMismatchError(ref: ParameterKindMismatch): void {
    const message = `Η παράμετρος '${ref.parameterName}' περιμένει ${ref.expectedKind} — το όρισμα δεν ταιριάζει`;
    appendOutputLine(`Σφάλμα παραμέτρου (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  function reportArrayParameterSizeMismatchError(ref: ArrayParameterSizeMismatch): void {
    const message = `Η παράμετρος '${ref.parameterName}' είναι δηλωμένη πίνακας [${ref.expectedDimensions.join(', ')}] — δεν ταιριάζει με το μέγεθος του πίνακα που δόθηκε [${ref.actualDimensions.join(', ')}]`;
    appendOutputLine(`Σφάλμα παραμέτρου (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  function reportArrayDimensionMismatchError(ref: ArrayDimensionMismatch): void {
    const message = `Ο πίνακας '${ref.arrayName}' έχει δηλωθεί με ${dimensionWord(ref.declaredDimensions)} αλλά χρησιμοποιείται εδώ με ${indexWord(ref.usedIndices)}`;
    appendOutputLine(`Σφάλμα πίνακα (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  function reportWholeArrayAssignmentError(ref: WholeArrayAssignment): void {
    const message = `Δεν επιτρέπεται ανάθεση σε ολόκληρο πίνακα ('${ref.name}') — μόνο στα μεμονωμένα στοιχεία του`;
    appendOutputLine(`Σφάλμα πίνακα (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  function reportDuplicateDeclarationError(ref: DuplicateDeclaration): void {
    const message = `Το όνομα '${ref.name}' έχει ήδη δηλωθεί`;
    appendOutputLine(`Σφάλμα δηλώσεων (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  function reportDuplicateParameterNameError(ref: DuplicateParameterName): void {
    const message = `Η παράμετρος '${ref.name}' εμφανίζεται δύο φορές στο '${ref.subprogramName}'`;
    appendOutputLine(`Σφάλμα παραμέτρου (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  function reportBuiltinFunctionNameCollisionError(ref: BuiltinFunctionNameCollision): void {
    const message = `Υπάρχει ήδη έτοιμη μαθηματική συνάρτηση με όνομα '${ref.name}'`;
    appendOutputLine(`Σφάλμα ονόματος (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  function reportReservedFunctionNameError(ref: ReservedFunctionNameUse): void {
    const message = `Η λέξη '${ref.name}' είναι δεσμευμένη (έτοιμη μαθηματική συνάρτηση) και δεν μπορεί να χρησιμοποιηθεί ως όνομα ${ref.role}`;
    appendOutputLine(`Σφάλμα ονόματος (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  function reportSwitchArrayDiscriminantError(ref: SwitchArrayDiscriminant): void {
    const message = `Δεν μπορεί να χρησιμοποιηθεί ολόκληρος πίνακας ('${ref.name}') ως έκφραση στο ΕΠΙΛΕΞΕ — χρειάζεται δείκτης (π.χ. ${ref.name}[i])`;
    appendOutputLine(`Σφάλμα πίνακα (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  function reportReadBooleanTargetError(ref: ReadBooleanTarget): void {
    const message = `Η ΔΙΑΒΑΣΕ δεν δέχεται μεταβλητές τύπου ΛΟΓΙΚΕΣ ('${ref.name}')`;
    appendOutputLine(`Σφάλμα εισόδου (γραμμή ${ref.line}, στήλη ${ref.column}): ${message}`, 'error');
    setErrorMarker(ref.line, ref.column, message);
  }

  function finishRun(status: string): void {
    statusText.textContent = status;
    setRunning(false);
  }

  runBtn.addEventListener('click', () => {
    if (resumeBreakpoint !== null) {
      const resolve = resumeBreakpoint;
      resumeBreakpoint = null;
      runBtn.disabled = true;
      setRunButtonLabel('Εκτέλεση');
      statusText.textContent = 'Εκτελείται…';
      resolve();
      return;
    }
    runProgram().catch((err: unknown) => {
      console.error(err);
      appendOutputLine(`Απρόσμενο εσωτερικό σφάλμα: ${String(err)}`, 'error');
      finishRun('Εσωτερικό σφάλμα');
    });
  });

  stopBtn.addEventListener('click', () => {
    if (runBtn.disabled) {
      // Τρέχει εκτέλεση αυτή τη στιγμή (βλ. `setRunning`) — "■ Διακοπή".
      currentController?.abort();
    } else {
      // Αδρανές — "🧹 Καθαρισμός": ίδιος καθαρισμός με αυτόν που ήδη κάνει η
      // `runProgram` στην αρχή κάθε τρεξίματος, εδώ σαν ρητή ενέργεια.
      clearOutput();
      clearErrorMarkers();
      clearVariablesPanel();
      hideVariablesPanelLogo();
      clearCurrentLineHighlight();
    }
  });

  /**
   * "🆕 Νέος Αλγόριθμος" — αδειάζει τον editor για να ξεκινήσει ο μαθητής από
   * την αρχή. Ρωτάει πρώτα ΜΟΝΟ αν υπάρχει ήδη γραμμένος κώδικας (κενός editor
   * ⇒ τίποτα να χάσει, καθαρίζει κατευθείαν χωρίς ερώτηση). Αν τρέχει αυτή τη
   * στιγμή εκτέλεση, τη σταματάει πρώτα (ίδιο σκεπτικό με το ανενεργό "🧹
   * Καθαρισμός" παραπάνω) — δεν έχει νόημα να σβήνεται ο κώδικας κάτω από
   * έναν αλγόριθμο που ακόμα τρέχει.
   */
  newDocumentBtn.addEventListener('click', async () => {
    const hasCode = editor.getValue().trim().length > 0;
    if (hasCode) {
      const confirmed = await showConfirmToast(
        'Νέος Αλγόριθμος',
        'Θέλεις να ξεκινήσεις νέο αλγόριθμο; Ο τρέχων κώδικας θα διαγραφεί.',
      );
      if (!confirmed) return;
    }
    if (runBtn.disabled) {
      currentController?.abort();
    }
    editor.setValue('');
    saveCodeAutosave(); // άμεσα, όχι debounced — αλλιώς ένα refresh μέσα στο παράθυρο του debounce θα έφερνε πίσω τον διαγραμμένο κώδικα
    clearOutput();
    clearErrorMarkers();
    clearVariablesPanel();
    hideVariablesPanelLogo();
    clearCurrentLineHighlight();
    editor.focus();
  });

  // --- Μετατροπές δομών επιλογής ------------------------------------------------

  /**
   * Πλήρους-οθόνης λειτουργία (αντικαθιστά το .app-main, όχι δίπλα του) —
   * ανεξάρτητο, αφιερωμένο πλαίσιο εισόδου/εξόδου που ΔΕΝ διαβάζει τον κύριο
   * κώδικα του editor. Ο χρήστης γράφει/επικολλά εδώ ΜΟΝΟ το κομμάτι
   * αλγορίθμου που θέλει να μετατρέψει (π.χ. ένα ΑΝ/ΑΛΛΙΩΣ_ΑΝ), όχι όλο το
   * πρόγραμμα — η ανίχνευση δομών γίνεται live, καθώς πληκτρολογεί.
   */

  const SELECTION_SHAPE_LABELS: Record<SelectionShape, string> = {
    simple: 'Απλή',
    composite: 'Σύνθετη',
    multiple: 'Πολλαπλή',
    'nested-else': 'Εμφωλευμένη (σε Αλλιώς)',
    'nested-then': 'Εμφωλευμένη (σε Τότε, με Και)',
    switch: 'ΕΠΙΛΕΞΕ',
  };

  const REPETITION_SHAPE_LABELS: Record<RepetitionShape, string> = {
    for: 'ΓΙΑ',
    while: 'ΟΣΟ',
    'repeat-until': 'ΜΕΧΡΙΣ_ΟΤΟΥ',
  };

  function setConversionOutputCode(code: string): void {
    conversionOutputEditor!.setValue(code);
    conversionOutputPlaceholder.classList.toggle('is-hidden', code.length > 0);
  }

  interface ActiveConversionSelection {
    readonly button: HTMLButtonElement;
    readonly anchor: Statement;
    readonly apply: (root: readonly Statement[]) => readonly Statement[];
  }

  /**
   * Οι τρέχουσες ενεργές επιλογές μετατροπής — ΜΙΑ ανά δομή (detection, ως
   * κλειδί αναφοράς αντικειμένου): επιλέγοντας άλλη επιλογή ΤΗΣ ΙΔΙΑΣ δομής
   * αντικαθιστά την προηγούμενη (δεν έχει νόημα να μετατραπεί η ίδια δομή με
   * δύο τρόπους ταυτόχρονα), αλλά ΔΙΑΦΟΡΕΤΙΚΕΣ δομές μπορούν να είναι ενεργές
   * ΤΑΥΤΟΧΡΟΝΑ, ώστε το αποτέλεσμα να τις συνδυάζει όλες μαζί.
   */
  const activeSelections = new Map<object, ActiveConversionSelection>();
  let currentConversionStatements: readonly Statement[] = [];

  /**
   * Μη-`null` ΜΟΝΟ όταν το κείμενο του `conversionInputEditor` αναγνωρίστηκε ως
   * ΠΛΗΡΕΣ πρόγραμμα (ΠΡΟΓΡΑΜΜΑ/ΑΛΓΟΡΙΘΜΟΣ...ΤΕΛΟΣ — βλ. `updateConversionOptions`),
   * όχι γυμνό τμήμα εντολών· κρατάει το ΥΠΟΛΟΙΠΟ πρόγραμμα (όνομα, δηλώσεις,
   * υποπρογράμματα, Δεδομένα/Αποτελέσματα) ώστε το `recomputeConversionOutput`
   * να ξαναχτίσει ΟΛΟΚΛΗΡΟ το πρόγραμμα γύρω από το μετατρεπόμενο σώμα, αντί να
   * επιστρέψει μόνο τις γυμνές εντολές (που θα έχανε την κεφαλίδα/δηλώσεις).
   */
  let currentConversionProgram: Program | null = null;

  /** Εφαρμόζει ΟΛΕΣ τις τρέχουσες ενεργές επιλογές μαζί, με ασφαλή σειρά (πρόγονος πριν τον απόγονο — βλ. `computeNodeDepths`), και ενημερώνει το αποτέλεσμα. */
  function recomputeConversionOutput(): void {
    if (activeSelections.size === 0) {
      setConversionOutputCode('');
      return;
    }
    const depths = computeNodeDepths(currentConversionStatements);
    const sorted = [...activeSelections.values()].sort((a, b) => (depths.get(a.anchor) ?? 0) - (depths.get(b.anchor) ?? 0));
    let root: readonly Statement[] = currentConversionStatements;
    for (const selection of sorted) root = selection.apply(root);
    const code =
      currentConversionProgram !== null ? printProgram({ ...currentConversionProgram, body: root } as Program) : printStatements(root, describeDialect());
    setConversionOutputCode(code);
  }

  function addOptionButton(
    container: HTMLElement,
    label: string,
    detectionKey: object,
    anchor: Statement,
    apply: (root: readonly Statement[]) => readonly Statement[],
  ): void {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    btn.addEventListener('click', () => {
      const existing = activeSelections.get(detectionKey);
      if (existing?.button === btn) {
        // Ξαναπατήθηκε το ήδη ενεργό κουμπί -> αποεπιλογή.
        btn.classList.remove('is-active');
        activeSelections.delete(detectionKey);
      } else {
        // Ίδια δομή, άλλη επιλογή -> αντικατάσταση (μία επιλογή ανά δομή).
        existing?.button.classList.remove('is-active');
        btn.classList.add('is-active');
        activeSelections.set(detectionKey, { button: btn, anchor, apply });
      }
      recomputeConversionOutput();
    });
    container.appendChild(btn);
  }

  /** Δημιουργεί μια ενότητα κουμπιών με τίτλο (χρησιμοποιείται μόνο όταν συνυπάρχουν δομές επιλογής ΚΑΙ επανάληψης). */
  function createOptionsGroup(title: string): HTMLElement {
    const group = document.createElement('div');
    group.className = 'conversion-options-group';
    const heading = document.createElement('div');
    heading.className = 'conversion-options-group-title';
    heading.textContent = title;
    group.appendChild(heading);
    conversionOptions.appendChild(group);
    return group;
  }

  function showConversionOptionsMessage(message: string): void {
    const p = document.createElement('div');
    p.className = 'conversion-options-empty';
    p.textContent = message;
    conversionOptions.appendChild(p);
  }

  /**
   * Ξαναϋπολογίζει τις δυνατές μετατροπές του περιεχομένου του conversionInputEditor
   * — καλείται σε κάθε πληκτρολόγηση (με debounce).
   *
   * Δέχεται είτε γυμνό "τμήμα αλγορίθμου" (`parseFragment`) είτε ΟΛΟΚΛΗΡΟ
   * πρόγραμμα (ΠΡΟΓΡΑΜΜΑ/ΑΛΓΟΡΙΘΜΟΣ...ΤΕΛΟΣ, οποιαδήποτε διάλεκτος) — δοκιμάζει
   * πρώτα το τμήμα (πιο συχνή περίπτωση) και, μόνο αν αποτύχει, δοκιμάζει
   * πλήρες πρόγραμμα· έτσι ένα πρόγραμμα με μόνο ΜΙΑ εντολή (π.χ. ένα γυμνό
   * `ΑΝ`) δεν παρεξηγείται ποτέ ως αποτυχημένη ανάλυση προγράμματος. Αν
   * αναγνωριστεί πλήρες πρόγραμμα, οι δομές ανιχνεύονται μέσα στο `program.body`
   * και το `currentConversionProgram` κρατάει τα υπόλοιπα (όνομα/δηλώσεις/
   * υποπρογράμματα) ώστε το αποτέλεσμα να βγαίνει ΚΙ αυτό πλήρες πρόγραμμα
   * (βλ. `recomputeConversionOutput`), όχι γυμνές εντολές.
   */
  function updateConversionOptions(): void {
    conversionOptions.innerHTML = '';
    activeSelections.clear();
    currentConversionStatements = [];
    currentConversionProgram = null;
    setConversionOutputCode('');

    const source = conversionInputEditor!.getValue();
    if (source.trim().length === 0) return;

    const dialect = describeDialect();
    const { tokens, errors: lexErrors } = tokenize(source, dialect);
    if (lexErrors.length > 0) {
      showConversionOptionsMessage('Υπάρχει σφάλμα στον κώδικα.');
      return;
    }

    const fragmentResult = parseFragment(tokens, dialect);
    if (fragmentResult.error === null && fragmentResult.statements !== null) {
      currentConversionStatements = fragmentResult.statements;
    } else {
      const { program, error: fullError } = parse(tokens, dialect);
      if (fullError !== null || program === null) {
        showConversionOptionsMessage('Υπάρχει σφάλμα στον κώδικα.');
        return;
      }
      currentConversionStatements = program.body;
      currentConversionProgram = program;
    }

    const detections = detectSelectionStructures(currentConversionStatements);
    const repetitionDetections = detectRepetitionStructures(currentConversionStatements);
    if (detections.length === 0 && repetitionDetections.length === 0) {
      showConversionOptionsMessage('Δεν βρέθηκε δομή επιλογής ή επανάληψης με δυνατή μετατροπή.');
      return;
    }

    // Όταν συνυπάρχουν και τα δύο είδη δομών, χωρίζουμε τα κουμπιά σε δύο
    // ξεχωριστές, επώνυμες ενότητες· διαφορετικά τα κουμπιά πάνε απευθείας
    // στο κοινό container (χωρίς περιττό τίτλο για μία μόνο κατηγορία).
    const showGroups = detections.length > 0 && repetitionDetections.length > 0;
    const selectionContainer = showGroups ? createOptionsGroup('Δομές επιλογής') : conversionOptions;
    const repetitionContainer = showGroups ? createOptionsGroup('Δομές επανάληψης') : conversionOptions;

    for (const detection of detections) {
      if (detection.kind === 'chain') {
        // `detection.options` έχει ΠΑΝΤΑ τουλάχιστον 1 στοιχείο (η ίδια η ένωση
        // σε Σύνθετη/Πολλαπλή — το πραγματικό σχήμα υπολογίζεται ήδη μέσα στο
        // `detectSelectionStructures`, δεν μαντεύουμε εδώ) ΚΑΙ, όποτε δέχεται
        // ΚΑΙ ΕΠΙΛΕΞΕ, μια 2η — έτσι ο χρήστης βλέπει και τα δύο βήματα μαζί,
        // χωρίς να χρειάζεται να ξαναδώσει το αποτέλεσμα σαν νέα είσοδο.
        for (const option of detection.options) {
          addOptionButton(selectionContainer, `→ ${SELECTION_SHAPE_LABELS[option.targetShape]}`, detection, detection.statements[0]!, (root) =>
            replaceChain(root, detection.statements, option.apply()),
          );
        }
      } else {
        for (const option of detection.options) {
          addOptionButton(selectionContainer, `→ ${SELECTION_SHAPE_LABELS[option.targetShape]}`, detection, detection.node, option.apply);
        }
      }
    }

    // Όταν υπάρχει πραγματική εμφώλευση βρόχων (level > 1 κάπου), κάθε κουμπί
    // δηλώνει ρητά ποιον βρόχο αφορά ("1η εξωτερική επανάληψη", κ.ο.κ.) — τα
    // κουμπιά παραμένουν ανεξάρτητα μεταξύ τους, απλώς γίνονται σαφή.
    const maxLevel = repetitionDetections.reduce((max, d) => Math.max(max, d.level), 1);
    for (const detection of repetitionDetections) {
      const prefix = maxLevel > 1 ? `${detection.level}η εξωτερική επανάληψη ` : '';
      for (const option of detection.options) {
        addOptionButton(
          repetitionContainer,
          `${prefix}→ ${REPETITION_SHAPE_LABELS[option.targetShape]}`,
          detection,
          detection.anchor,
          option.apply,
        );
      }
    }
  }

  const CONVERSION_INPUT_DEBOUNCE_MS = 400;
  let conversionInputDebounceTimer: ReturnType<typeof setTimeout> | undefined;

  /** Βλ. `autoDetectDialectFromMainEditorStart` — ίδια ιδέα, πάνω στο πλαίσιο εισαγωγής του Μετατροπέα. Δεν χρειάζεται φρουρό για το split φυσικής γλώσσας — τα δύο πλαίσια δεν είναι ποτέ ανοιχτά ταυτόχρονα (το άνοιγμα του Μετατροπέα κρύβει το `centerColumn`, που περιέχει το split). */
  function autoDetectDialectFromConversionEditorStart(): void {
    autoDetectDialectFromText(conversionInputEditor!.getValue());
  }

  function openConversionPanel(): void {
    ensureConversionEditors();
    centerColumn.hidden = true;
    variablesResizeHandle.hidden = true;
    variablesPanel.hidden = true;
    conversionPanel.hidden = false;
    transformBtn.setAttribute('aria-pressed', 'true');
    // Μεταφέρει αυτόματα ό,τι έχει ήδη γράψει ο μαθητής στον κύριο editor μέσα
    // στο πλαίσιο εισαγωγής του Μετατροπέα — έτσι δεν χρειάζεται να το
    // ξαναγράψει/επικολλήσει με το χέρι· απλώς γράφει κώδικα και πατάει
    // "⇄ Μετατροπέας". Αντικαθιστά ό,τι είχε τυχόν μείνει από προηγούμενο
    // άνοιγμα του πλαισίου (πάντα ο κύριος editor είναι η πηγή αλήθειας εδώ).
    // Δέχεται και "τμήμα αλγορίθμου" ΚΑΙ πλήρες πρόγραμμα (βλ. `updateConversionOptions`).
    const mainCode = editor.getValue();
    if (mainCode.trim().length > 0) {
      conversionInputEditor!.setValue(mainCode);
      // Το setValue μόλις πυροδότησε το onDidChangeModelContent παρακάτω, που
      // προγραμμάτισε ΚΑΙ ένα καθυστερημένο (debounced) updateConversionOptions
      // — το ακυρώνουμε και το τρέχουμε ΑΜΕΣΩΣ εδώ, ώστε οι επιλογές να
      // εμφανιστούν με το που ανοίγει το πλαίσιο, όχι μετά από καθυστέρηση.
      if (conversionInputDebounceTimer !== undefined) clearTimeout(conversionInputDebounceTimer);
      updateConversionOptions();
    }
    // Σε στενή οθόνη, οι καρτέλες κάτω στοχεύουν πλαίσια που μόλις κρύφτηκαν
    // (κώδικας/έξοδος/διάγραμμα/μεταβλητές) — δεν έχουν νόημα όσο είναι
    // ανοιχτή η Μετατροπή, οπότε κρύβονται κι αυτές (η δική της λωρίδα
    // συμβόλων καλύπτει την εισαγωγή συμβόλων εκεί μέσα).
    mobileTabs.hidden = true;
    // Δεν έχουν νόημα όσο είναι ανοιχτή η Μετατροπή (δεν υπάρχει κώδικας
    // προγράμματος να εκτελεστεί/μετατραπεί σε τμήμα εκεί) — οι Λέξεις-κλειδιά
    // ΜΕΝΟΥΝ ενεργές, ο χρήστης τις χρειάζεται για να γράψει στο πλαίσιο εισόδου.
    segmentToggleBtn.disabled = true;
    slowExecutionToggleBtn.disabled = true;
    runBtn.disabled = true;
    stopBtn.disabled = true;
    // Τα δύο editors δημιουργήθηκαν ενώ ήταν κρυφό το πλαίσιο (0x0 container) —
    // χρειάζονται ρητό layout() τώρα που παίρνουν πραγματικό χώρο, αλλιώς
    // μένουν κενά/λάθος μεγέθους μέχρι το επόμενο resize του παραθύρου.
    conversionInputEditor!.layout();
    conversionOutputEditor!.layout();
    conversionInputEditor!.focus();
  }

  function closeConversionPanel(): void {
    conversionPanel.hidden = true;
    centerColumn.hidden = false;
    variablesResizeHandle.hidden = false;
    // Επαναφέρει το πλαίσιο Διάγραμμα ροής στην κατάσταση που το είχε αφήσει
    // ο ΙΔΙΟΣ ο χρήστης (βλ. `flowchartPanelUserHidden`/`setFlowchartPanelVisible`)
    // — ΟΧΙ πάντα ορατό, αλλιώς θα ξανάνοιγε σιωπηλά ένα πλαίσιο που είχε
    // κλείσει ο χρήστης πριν ανοίξει τη Μετατροπή.
    variablesPanel.hidden = flowchartPanelUserHidden;
    flowchartPanelToggleBtn.setAttribute('aria-pressed', String(!flowchartPanelUserHidden));
    flowchartPanelToggleBtn.textContent = flowchartPanelUserHidden ? '‹' : '›';
    transformBtn.setAttribute('aria-pressed', 'false');
    mobileTabs.hidden = false;
    segmentToggleBtn.disabled = false;
    slowExecutionToggleBtn.disabled = false;
    runBtn.disabled = false;
    // Το "■/🧹" ξαναγίνεται ενεργό — δεν μπορεί να υπάρχει εκτέλεση σε εξέλιξη
    // αφού το Εκτέλεση ήταν απενεργοποιημένο όσο ήταν ανοιχτή η Μετατροπή, άρα
    // σίγουρα βρίσκεται στην κατάσταση "🧹 Καθαρισμός" (βλ. `setStopButtonState`).
    stopBtn.disabled = false;
    setStopButtonState(false);
  }

  transformBtn.addEventListener('click', () => {
    if (conversionPanel.hidden) {
      openConversionPanel();
    } else {
      closeConversionPanel();
    }
  });

  conversionPanelCloseBtn.addEventListener('click', closeConversionPanel);

  // --- Μετατροπή Κώδικας ↔ Φυσική γλώσσα κατά βήματα (βλ. #editor-split) -------

  /**
   * Γραμμή πηγαίου κώδικα → πρώτο "Βήμα Ν" αντίστοιχο — από την ΤΕΛΕΥΤΑΙΑ
   * επιτυχημένη μετατροπή κώδικα→φυσικής (βλ. `refreshNaturalFromCode`).
   * Χρησιμοποιείται για να συγχρονίζει το highlight της τρέχουσας εντολής και
   * στις δύο πλευρές κατά την εκτέλεση (βλ. `highlightNaturalLine`).
   */
  let currentNaturalLineMap: ReadonlyMap<number, number> | null = null;

  /**
   * Κώδικας → φυσική γλώσσα. Σε κανονική λειτουργία χρειάζεται ΠΛΗΡΕΣ, έγκυρο
   * πρόγραμμα σε Ψευδογλώσσα (με Αλγόριθμος...Τέλος) — το κεφάλαιο δίνει σαφή
   * κανόνα Είσοδος/Έξοδος ΜΟΝΟ από τα Δεδομένα/Αποτελέσματα της Ψευδογλώσσας,
   * όχι για ΓΛΩΣΣΑ. Σε "Τμήμα αλγορίθμου" (βλ. `segmentMode`) δεν υπάρχει καν
   * κεφαλίδα — ξεκινάει κατευθείαν με "Βήμα 1", όπως ακριβώς η Εκτέλεση/το
   * Διάγραμμα ροής δουλεύουν ήδη πάνω στο τμήμα (βλ. `updateFlowchart`/`runProgram`).
   * Επιστρέφει true αν πέτυχε (και ενημερώνει το `currentNaturalLineMap`).
   */
  /**
   * @param preferInlineIf Βλ. `printProgramAsNaturalLanguageSteps` — false
   *   (προεπιλογή) για το ρητό κουμπί "Μετάτρεψε →" (πλήρης διδακτική GOTO
   *   μορφή), true όταν καλείται ΕΣΩΤΕΡΙΚΑ ως re-sync αμέσως μετά από
   *   επιτυχημένη φυσική→κώδικας (βλ. `convertNaturalToCode`) — ώστε μια ήδη
   *   έγκυρη σύντομη ΑΝ που έγραψε ο μαθητής να ΜΗΝ επεκτείνεται άσκοπα σε
   *   πλήρες GOTO μόλις πατήσει Εκτέλεση/Μετάτρεψε.
   * @param updateEditorValue false ΜΟΝΟ στο εσωτερικό re-sync (βλ. πάνω): θέλουμε
   *   ΜΟΝΟ φρέσκο `currentNaturalLineMap` για το highlight, ΟΧΙ να ξαναγράψουμε
   *   το ήδη-σωστό κείμενο που έγραψε ο μαθητής (π.χ. "Εμφάνισε"→"Γράψε",
   *   "και"→", " κ.λπ. — μικρές μεν, αλλά ανεπιθύμητες αλλαγές λέξεων που ο
   *   μαθητής ΔΕΝ ζήτησε). Το ρητό κουμπί "Μετάτρεψε →" πάντα το αφήνει true.
   */
  function refreshNaturalFromCode(preferInlineIf = false, updateEditorValue = true): { readonly ok: true } | { readonly ok: false; readonly reason: string } {
    const source = editor.getValue();
    const { tokens, errors: lexErrors } = tokenize(source, Dialect.PSEUDOGLOSSA);
    if (lexErrors.length > 0) {
      return { ok: false, reason: 'υπάρχει λεξικό σφάλμα στον κώδικα.' };
    }
    autoDisableSegmentModeIfFullProgram(tokens);

    if (segmentMode) {
      const { statements, error: parseError } = parseFragment(tokens, Dialect.PSEUDOGLOSSA);
      if (parseError !== null || statements === null) {
        return { ok: false, reason: 'υπάρχει συντακτικό σφάλμα στον κώδικα.' };
      }
      const result = printStatementsAsNaturalLanguageSteps(statements, preferInlineIf);
      if (!result.ok) return { ok: false, reason: result.reason };
      if (updateEditorValue) naturalEditor!.setValue(result.text);
      currentNaturalLineMap = result.lineMap;
      return { ok: true };
    }

    const { program, error: parseError } = parse(tokens, Dialect.PSEUDOGLOSSA);
    if (parseError !== null || program === null || program.dialect !== Dialect.PSEUDOGLOSSA) {
      return {
        ok: false,
        reason: 'χρειάζεται πλήρες, έγκυρο πρόγραμμα σε Ψευδογλώσσα (με Αλγόριθμος...Τέλος) — ή ενεργοποίησε το "Τμήμα αλγορίθμου" — όχι πρόγραμμα σε ΓΛΩΣΣΑ.',
      };
    }
    const result = printProgramAsNaturalLanguageSteps(program, preferInlineIf);
    if (!result.ok) return { ok: false, reason: result.reason };
    if (updateEditorValue) naturalEditor!.setValue(result.text);
    currentNaturalLineMap = result.lineMap;
    return { ok: true };
  }

  toNaturalBtn.addEventListener('click', () => {
    clearOutput();
    const result = refreshNaturalFromCode();
    appendOutputLine(result.ok ? 'Η μετατροπή σε φυσική γλώσσα ολοκληρώθηκε.' : `Δεν είναι δυνατή η μετατροπή σε φυσική γλώσσα: ${result.reason}`, result.ok ? 'info' : 'error');
  });

  /**
   * Φυσική γλώσσα → κώδικας. Αναγνωρίζει ΜΟΝΟ τα γνωστά σχήματα GOTO του
   * κεφαλαίου (τα ίδια που παράγει η αντίστροφη κατεύθυνση) — αρνείται ρητά,
   * χωρίς να μαντέψει, οτιδήποτε άλλο. Σε "Τμήμα αλγορίθμου" δεν αναμένεται
   * καν κεφαλίδα ("Αλγόριθμος"/"Είσοδος:"/"Έξοδος:") — μόνο "Βήμα Ν: ...".
   *
   * Αμέσως μετά ξανατρέχει το `refreshNaturalFromCode` (κώδικας→φυσική) πάνω
   * στον ΦΡΕΣΚΟ κώδικα, αλλά ΜΟΝΟ για να πάρουμε φρέσκο lineMap για
   * συγχρονισμένο highlight κατά την εκτέλεση — χωρίς αυτό, ένα lineMap
   * χτισμένο πάνω σε ΠΑΛΙΟ κώδικα δεν θα ταίριαζε πια στις γραμμές. ΔΕΝ
   * ξαναγράφει το naturalEditor (updateEditorValue: false) — το κείμενο που
   * έγραψε ο μαθητής παραμένει ΑΚΡΙΒΩΣ όπως το έγραψε (π.χ. "Εμφάνισε" δεν
   * γίνεται "Γράψε", "και" δεν γίνεται ", ", ένα inline "Αν...τότε" δεν
   * επεκτείνεται σε πλήρες GOTO) — μόνο ο ρητός κόμβος "Μετάτρεψε →" παράγει
   * και ΔΕΙΧΝΕΙ την κανονική/διδακτική μορφή.
   */
  function convertNaturalToCode(): boolean {
    const text = naturalEditor!.getValue();
    if (segmentMode) {
      const result = parseNaturalLanguageStepsToStatements(text);
      if (!result.ok) {
        appendOutputLine(`Δεν είναι δυνατή η μετατροπή σε κώδικα: ${result.reason}`, 'error');
        return false;
      }
      editor.setValue(printStatements(result.statements, Dialect.PSEUDOGLOSSA));
    } else {
      const result = parseNaturalLanguageStepsToProgram(text);
      if (!result.ok) {
        appendOutputLine(`Δεν είναι δυνατή η μετατροπή σε κώδικα: ${result.reason}`, 'error');
        return false;
      }
      editor.setValue(printProgram(result.program));
    }

    // codeDialect είναι πάντα Ψευδογλώσσα όσο το split είναι ανοιχτό (βλ. enterNaturalSplit) — δεν χρειάζεται συγχρονισμός εδώ.
    scheduleFlowchartUpdate();
    const refreshResult = refreshNaturalFromCode(true, false);
    if (!refreshResult.ok) {
      // Θεωρητικά δεν πρέπει να συμβεί ποτέ (μόλις παράχθηκε από τη forward μετατροπή) — αν συμβεί, δεν έχει νόημα συγχρονισμένο highlight, αλλά ο κώδικας παραμένει σωστός.
      currentNaturalLineMap = null;
    }
    return true;
  }

  toCodeBtn.addEventListener('click', () => {
    clearOutput();
    if (convertNaturalToCode()) {
      appendOutputLine('Η μετατροπή σε κώδικα ολοκληρώθηκε.', 'info');
    }
  });

  // --- Καρτέλες κάτω μέρους οθόνης (μόνο σε στενές οθόνες, βλ. media query στο style.css) --

  /**
   * Σε ευρεία οθόνη όλα τα πλαίσια (κώδικας/έξοδος/διάγραμμα/λέξεις/μεταβλητές)
   * φαίνονται ταυτόχρονα — αυτή η λογική δεν κάνει τίποτα ορατό εκεί, το CSS
   * αγνοεί το `data-active-mobile-tab` πάνω από ένα πλάτος. Σε στενή οθόνη
   * όμως, μόνο ΕΝΑ πλαίσιο τη φορά χωράει άνετα, οπότε το CSS δείχνει μόνο
   * αυτό που ταιριάζει με το ενεργό tab.
   */
  const mobileTabButtons = document.querySelectorAll<HTMLButtonElement>('.mobile-tab-btn');

  function setActiveMobileTab(tab: string): void {
    appMain.dataset.activeMobileTab = tab;
    for (const btn of mobileTabButtons) {
      btn.classList.toggle('is-active', btn.dataset.tab === tab);
    }
    // Ο κύριος editor δημιουργήθηκε ενώ ήταν ήδη ορατός (αρχική καρτέλα =
    // "Κώδικας"), αλλά αν ο χρήστης έφυγε σε άλλη καρτέλα (container 0x0) και
    // ξαναγύρισε, χρειάζεται ρητό layout() — ίδιος λόγος με το conversion panel.
    if (tab === 'code') {
      editor.layout();
      if (!naturalEditorBox.hidden) naturalEditor!.layout();
    }
  }

  for (const btn of mobileTabButtons) {
    btn.addEventListener('click', () => setActiveMobileTab(btn.dataset.tab ?? 'code'));
  }

  setActiveMobileTab('code');

  // Ο Monaco + όλη η εφαρμογή είναι πλέον έτοιμα — ξανα-ενεργοποιούμε το
  // toolbar/mobile-tabs (βλ. index.html/style.css `.app-booting`, μπήκε
  // πριν κληθεί το `boot()`).
  document.documentElement.classList.remove('app-booting');

  /**
   * --- Λήψη κώδικα παραδείγματος από το methodologia.html ------------------
   * Κάποια παραδείγματα εκεί έχουν κουμπί "αντιγραφή στον διερμηνευτή" — το
   * ίδιο μοτίβο navigation με το "← Πίσω στον Διερμηνευτή" (window.close() αν
   * υπάρχει opener, αλλιώς κανονική πλοήγηση), απλά ΕΠΙΠΛΕΟΝ περνάει και τον
   * κώδικα μέσω localStorage (μοναδικός αξιόπιστος τρόπος επικοινωνίας μεταξύ
   * δύο tabs). ΔΥΟ σενάρια χρειάζονται κάλυψη:
   *  - Υπήρχε ήδη ανοιχτό tab εργαλείου (opener) → η μεθοδολογία απλά το
   *    φέρνει μπροστά (δεν κάνει reload), άρα πιάνεται μόνο με το listener στο
   *    eventual 'storage' event.
   *  - Άνοιξε ΝΕΟ tab εργαλείου (δεν υπήρχε opener) → ΓΙΝΕΤΑΙ πλήρες load, άρα
   *    πιάνεται με τον έλεγχο localStorage κατά την αρχικοποίηση.
   */
  /**
   * Επαναφορά αυτόματα αποθηκευμένου κώδικα (βλ. `saveCodeAutosave`) — ΠΡΙΝ
   * τον έλεγχο μεταφοράς παραδείγματος από τη Μεθοδολογία παρακάτω, ώστε ένα
   * ρητό "▶ Άνοιξε στον Διερμηνευτή" να υπερισχύει πάντα του απλού autosave
   * (ρητή ενέργεια του μαθητή > σιωπηλή επαναφορά προηγούμενης συνεδρίας).
   * Καλεί ΚΑΙ το `dismissEditorIntroLine()` — αν επανέρχεται ήδη γραμμένος
   * κώδικας, ο μαθητής έχει προφανώς ξαναχρησιμοποιήσει το εργαλείο, οπότε η
   * εισαγωγική γραμμή/τίτλος δεν έχουν νόημα να ξαναφανούν σε κάθε refresh
   * (ρητό αίτημα: "όταν κάνω refresh δεν θέλω να εμφανίζονται αυτά").
   */
  const savedCode = loadCodeAutosave();
  if (savedCode !== null) {
    applyCodeDialect(savedCode.dialect);
    if (savedCode.segment && !segmentMode) {
      segmentMode = true;
      segmentToggleBtn.setAttribute('aria-pressed', 'true');
    }
    editor.setValue(savedCode.code);
    updateFlowchart();
    dismissEditorIntroLine();
  }

  const EXAMPLE_TRANSFER_KEY = 'glossa-example-code-transfer';

  interface TransferredExamplePayload {
    code: string;
    /** Μερικά παραδείγματα (π.χ. 4.5, 5.1, 5.8) είναι γυμνά τμήματα εντολών,
     *  ΧΩΡΙΣ ΑΛΓΟΡΙΘΜΟΣ/ΠΡΟΓΡΑΜΜΑ...ΤΕΛΟΣ — χρειάζονται το "Τμήμα αλγορίθμου"
     *  ενεργό, αλλιώς θα έδιναν συντακτικό σφάλμα κατά την Εκτέλεση. */
    segment?: boolean;
    /** Αν 'natural', ο κώδικας είναι σε Φυσική Γλώσσα Κατά Βήματα ("Βήμα 1:
     *  ...") — πάει στο ΑΡΙΣΤΕΡΟ πλαίσιο του split-view Μετατροπέα
     *  (`naturalEditor`), ΟΧΙ στον κύριο editor (βλ. `enterNaturalSplit` — η
     *  φυσική γλώσσα δεν είναι ξεχωριστή "διάλεκτος εκτέλεσης", είναι
     *  μετατροπέας ΠΡΟΣ Ψευδογλώσσα). */
    dialect?: 'natural';
  }

  function applyTransferredExampleCode(raw: string): void {
    let payload: TransferredExamplePayload;
    try {
      payload = JSON.parse(raw) as TransferredExamplePayload;
    } catch {
      payload = { code: raw }; // παλιά μορφή (απλό string) — fallback, μη σπάσει
    }
    if (typeof payload.code !== 'string') return;

    // Ίδιος καθαρισμός με το ανενεργό "🧹 Καθαρισμός" (βλ. `stopBtn` listener)
    // — αλλιώς θα έμενε στην οθόνη έξοδος/highlight από τυχόν προηγούμενο
    // τρέξιμο, μπερδεύοντας το τι ανήκει στο νέο παράδειγμα.
    clearOutput();
    clearErrorMarkers();
    clearVariablesPanel();
    hideVariablesPanelLogo();
    clearCurrentLineHighlight();

    if (payload.dialect === 'natural') {
      // Ίδια ενέργεια με το άλλαγμα του dialect-select σε "natural" (βλ.
      // `dialectSelect` change listener) — ανοίγει το split-view, αλλά ΔΕΝ
      // αλλάζει το dropdown's value αυτόματα, το κάνουμε ρητά εδώ.
      dialectSelect.value = 'natural';
      renderKeywordPalette();
      renderMobileQuickStrips();
      enterNaturalSplit();
      naturalEditor!.setValue(payload.code);
    } else {
      if (!naturalEditorBox.hidden) exitNaturalSplit();
      if (payload.segment === true && !segmentMode) {
        // Ενεργοποίηση "Τμήμα αλγορίθμου" ΠΡΙΝ μπει ο κώδικας (όχι μέσω του
        // click-handler του segmentToggleBtn — αυτός έχει async λογική
        // "μετατροπή υπάρχοντος πλήρους προγράμματος", άσχετη εδώ: φορτώνουμε
        // ΚΑΤΕΥΘΕΙΑΝ ένα ήδη-γυμνό τμήμα, όχι μετατρέπουμε το τρέχον περιεχόμενο).
        segmentMode = true;
        segmentToggleBtn.setAttribute('aria-pressed', 'true');
      }
      editor.setValue(payload.code);
      updateFlowchart();
    }

    showToast(
      'Ο κώδικας αντιγράφηκε',
      payload.dialect === 'natural'
        ? 'Το παράδειγμα από τη Μεθοδολογία φορτώθηκε στη Φυσική Γλώσσα Κατά Βήματα.'
        : payload.segment === true
          ? 'Το τμήμα αλγορίθμου από τη Μεθοδολογία φορτώθηκε στον editor (ενεργό το "Τμήμα αλγορίθμου").'
          : 'Το παράδειγμα από τη Μεθοδολογία φορτώθηκε στον editor.',
    );
  }

  const pendingExampleCode = localStorage.getItem(EXAMPLE_TRANSFER_KEY);
  if (pendingExampleCode !== null) {
    localStorage.removeItem(EXAMPLE_TRANSFER_KEY);
    applyTransferredExampleCode(pendingExampleCode);
  }

  window.addEventListener('storage', (e) => {
    if (e.key === EXAMPLE_TRANSFER_KEY && e.newValue !== null) {
      localStorage.removeItem(EXAMPLE_TRANSFER_KEY);
      applyTransferredExampleCode(e.newValue);
    }
  });
}

/**
 * ΔΕΝ καλούμε το `boot()` αμέσως — σε αργή σύνδεση, το δυναμικό `import()`
 * (~2.8MB Monaco) θα ανταγωνιζόταν το ήδη περιορισμένο εύρος ζώνης με τα
 * ΚΡΙΣΙΜΑ αρχεία της αρχικής σελίδας (HTML/CSS/το μικρό αρχικό JS) αν
 * ξεκινούσε ταυτόχρονα μαζί τους — μετρήσιμο πρόβλημα, όχι υποθετικό (βλ.
 * plan transient-zooming-sky: FCP/LCP ΔΕΝ βελτιώθηκαν σχεδόν καθόλου με
 * άμεσο `boot()`, παρόλο που το JS bundle μειώθηκε 3MB→211KB). Περιμένουμε
 * το `load` event (η αρχική σελίδα έχει ήδη πλήρως αποδοθεί) ΚΑΙ έναν κύκλο
 * αδράνειας του browser (`requestIdleCallback`, με timeout ασφαλείας — δεν
 * το υποστηρίζει το Safari, οπότε fallback σε `setTimeout`) πριν ξεκινήσει
 * η φόρτωση του βαριού κομματιού.
 */
function startBoot(): void {
  document.documentElement.classList.add('app-booting');
  boot().catch((err) => {
    console.error('Αποτυχία φόρτωσης του επεξεργαστή κώδικα:', err);
    showBootErrorState(err);
  });
}

function scheduleBoot(): void {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(startBoot, { timeout: 2000 });
  } else {
    setTimeout(startBoot, 0);
  }
}

if (document.readyState === 'complete') {
  scheduleBoot();
} else {
  window.addEventListener('load', scheduleBoot);
}
